import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ApplicationRef,
  EnvironmentInjector,
  Injector,
  inject,
  createComponent,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  HostBinding,
} from '@angular/core';

import {
  Model,
  SurveyModel,
  surveyLocalization,
  Question as SurveyQuestion,
  QuestionDropdownModel,
  PageModel,
  PanelModel,
} from 'survey-core';
import { SurveyModule } from 'survey-angular-ui';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ComponentService } from '../../services/component/component.service';
import { ComponentInputBase } from '../../models/ComponentInputBase.model';
import { DefaultLightPanelless } from 'survey-core/themes';
import { ComponentDataDestService } from '../../services/component-data-dest/component-data-dest.service';
import { dualPanelSelector } from '../../surveyJS-custom-questions/dual-panel-selector';
import '../../surveyJS-custom-questions/survey-ngform-bridge';
import { registerRngSurveyVisibilityDataSourceProperties } from '../../surveyJS-custom-questions/rng-survey-serializer';
import { dynamicTableQuestion } from '../../surveyJS-custom-questions/dynamic-table-question';
import '../../surveyJS-custom-questions/info-modal-question';
import {
  attachInfomodalBadgeToPartner,
  attachInfomodalFromPartnerQuestion,
  reattachPageInfoModals,
  tuckRequiredStarIntoTitle,
} from '../../surveyJS-custom-questions/info-modal-inline.util';
import '../../surveyJS-custom-questions/event-series-planner';
import {
  teardownEventMaterialPlanner,
} from '../../surveyJS-custom-questions/event-material-planner';
import '../../surveyJS-custom-questions/event-material-planner';
import {
  isPlannerSyncingToSurvey,
  teardownEventSeriesPlanner,
} from '../../surveyJS-custom-questions/event-series-planner';
import {
  hasSeriesPlannerDates,
  inferGuidedScheduleInferredMode,
  inferScheduleCrossesDays,
  syncEndDateFromStart,
} from '../../services/component-data-source/data/event/event-schedule-form.util';
import {
  syncPeriodScheduleToSeriesFields,
  syncSeriesFieldsToPeriodForm,
} from '../../services/component-data-source/data/event/event-schedule-view-sync.util';
import { resolveEventFormOrganizationId } from '../../services/component-data-source/data/event/event-form-org.util';
import { previousCateringBeforeAdditionalIndex } from '../../services/component-data-source/data/event/event-catering-field.util';
import {
  EVENT_CATEGORY_NONE_FORM_VALUE,
  eventCategoryFromFormValue,
} from '../../services/component-data-source/data/event/event-category-form.util';
import { unlinkEventFromGroup } from '../../services/component-data-dest/data/unlink-event-from-group';
import { deleteEvent } from '../../services/component-data-dest/data/delete-event';
import { ComponentDataSourceService } from '../../services/component-data-source/component-data-source.service';
import { LoadDataParams } from '../../interfaces/DataSource/DataSourceParams.interface';
import {
  firstValueFrom,
  Subject,
  takeUntil,
  catchError,
  of,
  timeout,
  take,
  skip,
  debounceTime,
  Observable,
} from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../ngrx/template/template.reducers';
import * as EventsActions from '../../ngrx/events/events.actions';
import {
  ParticipatePathModuleFutureDialogComponent,
  ParticipatePathModuleFutureDialogData,
} from '../duna/duna-user-participate/participate-path-module-future-dialog.component';
import { PathModuleFutureRegistration } from '../../services/component-data-source/data/event/event-participate-path-module-hints.util';
import {
  EventDeleteConfirmDialogComponent,
  EventDeleteConfirmDialogData,
} from './event-delete-confirm-dialog.component';
import {
  DunaCustomersCreateDialogComponent,
  DunaCustomersCreateDialogData,
} from '../duna/duna-customers/duna-customers-create-dialog.component';
import {
  DunaCateringPreferencesDialogComponent,
  DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
} from '../duna/duna-catering/duna-catering-preferences-dialog.component';
import type { CateringWishesDialogRow } from '../duna/duna-catering/duna-catering-preferences-dialog.component';
import { OrganizationCustomerDTO } from '../../dto/organization/organization-customer.dto';
import 'survey-core/i18n/danish';
import { Serializer } from 'survey-core';
import { environment } from '../../environment/environment';
import { resolveBundledSurveyForm } from '../../forms/bundled-survey-forms';

// Add this at the top of the file, before the component class
declare global {
  interface Window {
    Survey: any;
  }
}

surveyLocalization.defaultLocale = 'da';

/** Register RNG custom question properties before any SurveyModel is created. */
function registerDynamicFormSerializerProperties(): void {
  if (!Serializer.findProperty('question', 'defaultDateTime')) {
    Serializer.addProperty('question', {
      name: 'defaultDateTime',
      displayName: 'Standard dato/tid',
      type: 'string',
      category: 'general',
      visibleIf: (obj: { getType?: () => string; inputType?: string }) =>
        obj.getType?.() === 'text' && obj.inputType === 'datetime-local',
    });
  }
  if (!Serializer.findProperty('question', 'allowCreate')) {
    Serializer.addProperty('question', {
      name: 'allowCreate',
      type: 'boolean',
      category: 'data',
      default: false,
      visibleIf: (obj: { getType?: () => string; dataSource?: string }) =>
        obj.getType?.() === 'dropdown' && !!obj.dataSource,
    });
  }
  // SurveyJS 3 has no native `cssClass` property. The event add/edit forms use it to
  // attach layout classes (cards, segmented toggles ...) to questions and panels.
  if (!Serializer.findProperty('question', 'cssClass')) {
    Serializer.addProperty('question', {
      name: 'cssClass',
      displayName: 'Custom CSS class',
      type: 'string',
      category: 'layout',
      default: '',
    });
  }
  if (!Serializer.findProperty('panelbase', 'cssClass')) {
    Serializer.addProperty('panelbase', {
      name: 'cssClass',
      displayName: 'Custom CSS class',
      type: 'string',
      category: 'layout',
      default: '',
    });
  }
}
registerDynamicFormSerializerProperties();
registerRngSurveyVisibilityDataSourceProperties();

// Override the exact Danish localization key we found
surveyLocalization.locales['da'] = surveyLocalization.locales['da'] || {};
surveyLocalization.locales['da']['noEntriesText'] = '';
surveyLocalization.locales['da']['emptySurvey'] = ''; // This is the exact key!
surveyLocalization.locales['da']['panelDynamicProgressText'] =
  'Deltager {0}/{1}';

// Try to find any other keys after SurveyJS loads its translations
setTimeout(() => {
  Object.keys(surveyLocalization.locales['da'] || {}).forEach((key) => {
    const value = surveyLocalization.locales['da'][key];
    if (typeof value === 'string' && value.includes('synlige spørgsmål')) {
      surveyLocalization.locales['da'][key] = '';
    }
  });
}, 100);

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [SurveyModule, MatProgressBarModule],
  templateUrl: './dynamic-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: [
    './dynamic-form.component.scss',
    '../../surveyJS-custom-questions/dual-panel-selector.scss',
    '../../surveyJS-custom-questions/event-series-planner.scss',
  ],
})
export class DynamicFormComponent
  extends ComponentInputBase
  implements OnInit, AfterViewInit, OnDestroy
{
  @HostBinding('class.duna-event-form-survey')
  get isEventAddEditBundledForm(): boolean {
    const key = this.getInput('bundledSurveyForm');
    return key === 'add-event' || key === 'edit-event';
  }

  surveyModel!: SurveyModel;
  /** Stays false until data is loaded and the correct first page is determined.
   *  The template uses this to keep the survey invisible (opacity:0) during init,
   *  preventing any flash of the wrong page or submit button. */
  surveyReady = false;
  /** True when no page is visible (e.g. all hidden by visibilityDataSource/authVisibility). Show "no options" message instead of survey. */
  noVisiblePages = false;
  /** When no pages are visible, optional explanation from a visibilityDataSource (e.g. registration deadline). */
  noVisiblePagesHint: string | null = null;
  isAuthenticated = false;
  private destroy$ = new Subject<void>();
  private searchTextMap: Map<string, string> = new Map();
  /** Full room lists per extra-room dropdown instance (keyed by SurveyJS question id). */
  private extraRoomBaseChoices = new Map<
    string,
    Array<{ value: unknown; text: string }>
  >();
  private searchTimeouts: Map<string, any> = new Map();
  private debounceTime = 500; // 500ms debounce time
  private navigationButtonsLocationFromJSON: string | undefined; // Store JSON value to preserve it
  private navigationButtonsObserver: MutationObserver | null = null; // Observer for navigation buttons
  private resizeObserver: ResizeObserver | null = null;
  private resizeSubject = new Subject<ResizeObserverEntry | undefined>();
  /** True while we're handling a resize (onResizeForLayout). Used to avoid the nav MutationObserver re-hiding buttons. */
  private resizeLayoutInProgress = false;
  /** Last observed host size; used to run resize-only logic only when size actually changed (not on initial layout). */
  private lastResizeWidth = 0;
  private lastResizeHeight = 0;
  private resizeRestoreIntervalId: ReturnType<typeof setInterval> | null = null;
  /** Periodic restore so the nav stays visible after resize or on small viewport. */
  private keepNavVisibleIntervalId: ReturnType<typeof setInterval> | null =
    null;
  private paneldynamicObserver: MutationObserver | null = null;
  private participateUsersDotsObserver: MutationObserver | null = null;
  private participateUsersDotsObserverRoot: HTMLElement | null = null;
  private participatePathModuleFutureModalOpened = false;
  private datetimeObserver: MutationObserver | null = null;
  // Add a tracker for adjusted datetime values
  private adjustedDatetimes: Set<string> = new Set<string>();
  /** Resolved dataSource payloads keyed by source + org + location + search. */
  private dataSourceResultCache = new Map<string, unknown>();
  /** In-flight dataSource requests (dedupe concurrent identical loads). */
  private dataSourceInFlight = new Map<string, Promise<unknown>>();

  /**
   * Resolve a datetime-local default from survey JSON.
   * - "12:00" → today's date at that time
   * - "2025-06-01T14:30" → fixed date and time
   */
  private resolveDateTimeLocalDefault(raw: string): string {
    const trimmed = raw.trim();
    if (/^\d{1,2}:\d{2}(?::\d{2})?$/.test(trimmed)) {
      const [hoursPart, minutesPart] = trimmed.split(':');
      const hours = hoursPart.padStart(2, '0');
      const minutes = minutesPart.padStart(2, '0');
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    if (trimmed.includes('T')) {
      const withoutSeconds = trimmed.split('.')[0];
      return withoutSeconds.length > 16
        ? withoutSeconds.slice(0, 16)
        : withoutSeconds;
    }
    return trimmed;
  }

  /** Read defaultDateTime / defaultValue for datetime-local from question or original JSON. */
  private getDateTimeLocalDefaultRaw(question: any): string | undefined {
    const fromQuestion =
      question?.['defaultDateTime'] ??
      question?.getPropertyValue?.('defaultDateTime') ??
      question?.jsonObj?.defaultDateTime;
    if (fromQuestion != null && fromQuestion !== '') {
      return String(fromQuestion);
    }
    const name = question?.name;
    if (name && this.originalSurveyJSON) {
      const def = this.findElementDefinitionInOriginalJson(name);
      const fromJson = def?.['defaultDateTime'];
      if (fromJson != null && fromJson !== '') {
        return String(fromJson);
      }
    }
    if (question?.inputType === 'datetime-local') {
      const defaultValue =
        question?.['defaultValue'] ??
        question?.getPropertyValue?.('defaultValue') ??
        question?.jsonObj?.defaultValue;
      if (defaultValue != null && defaultValue !== '') {
        return String(defaultValue);
      }
      if (name && this.originalSurveyJSON) {
        const def = this.findElementDefinitionInOriginalJson(name);
        const fromJson = def?.['defaultValue'];
        if (fromJson != null && fromJson !== '') {
          return String(fromJson);
        }
      }
    }
    return undefined;
  }

  /** Apply defaultDateTime (or defaultValue) when a datetime-local field is still empty. */
  private applyDateTimeLocalDefault(question: any): boolean {
    if (
      question?.value ||
      question?.getType?.() !== 'text' ||
      question?.inputType !== 'datetime-local'
    ) {
      return false;
    }
    const raw = this.getDateTimeLocalDefaultRaw(question);
    if (!raw) {
      return false;
    }
    question.value = this.resolveDateTimeLocalDefault(raw);
    return true;
  }

  private applyDateTimeLocalDefaultsToAllQuestions(): void {
    if (!this.surveyModel) {
      return;
    }
    for (const question of this.surveyModel.getAllQuestions()) {
      this.applyDateTimeLocalDefault(question);
    }
  }
  // Track if data is being loaded from API - added with explicit type
  private isFromApiLoad: boolean = false;
  // Track if form submission is in progress to prevent double submission
  private isFormSubmitting: boolean = false;
  /** After add-event save, open catering dialog when user requested it before the event existed. */
  private openCateringAfterSave = false;
  /** Skip SurveyJS navigateToUrl once after add-event + forplejning flow. */
  private suppressSurveyNavigateToUrlOnce = false;
  // Store original JSON object to access custom properties like showEmptyFormSubmit
  private originalSurveyJSON: any = null;
  /** Restored when leaving serieplanlægger (`schedule_mode` ≠ multi_dates). */
  private surveyShowPageTitlesDefault = true;

  /** Visibility results from visibilityDataSource (key: "page:name" or "question:name") so updatePageVisibility can re-apply. */
  private visibilityFromDataSource = new Map<string, boolean>();
  /** Cached course price templates keyed by id (string) for add/edit-event Pris panel. */
  private coursePriceTemplateById = new Map<
    string,
    {
      pricesByGroupId: Map<
        number,
        {
          fixed_course_amount: number | null;
          per_participant_amount: number | null;
        }
      >;
      extraOptionsByGroupId: Map<number, number[]>;
    }
  >();
  private customerPriceGroupByGuid = new Map<string, number>();
  private customerDetailsByGuid = new Map<string, OrganizationCustomerDTO>();
  private priceGroupLabelById = new Map<number, string>();
  private standardPriceGroupIdCache: number | null = null;
  /** visibleIf expression that always evaluates to false; prevents SurveyJS from re-showing when visibleIf is re-evaluated. */
  private static readonly VISIBLEIF_ALWAYS_FALSE = '1=0';

  // Inject Angular services needed for dynamic component creation
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private environmentInjector = inject(EnvironmentInjector);
  private cdr = inject(ChangeDetectorRef);

  constructor(
    componentService: ComponentService,
    private componentDataDestService: ComponentDataDestService,
    private componentDataSourceService: ComponentDataSourceService,
    private authService: AuthService,
    private el: ElementRef
  ) {
    super(componentService);
    this.dynamicInput = {
      formReceiveFunction: { type: 'text', value: '' },
      surveyJSON: { type: 'surveyJSON', value: '' },
      bundledSurveyForm: { type: 'text', value: '' },
    };
  }

  /** When set (e.g. add-event / edit-event), survey JSON is loaded from source code, not CMS surveyJSON. */
  protected resolveSurveyJsonForInit(): Record<string, unknown> {
    const bundledKey = this.getInput('bundledSurveyForm');
    if (typeof bundledKey === 'string' && bundledKey.trim()) {
      const bundled = resolveBundledSurveyForm(bundledKey.trim());
      if (bundled) {
        return bundled;
      }
    }
    const surveyJSON = this.getInput('surveyJSON');
    if (!surveyJSON) {
      return {};
    }
    if (typeof surveyJSON === 'string') {
      const trimmed = surveyJSON.trim();
      if (!trimmed || trimmed === '{}') {
        return {};
      }
      return JSON.parse(trimmed) as Record<string, unknown>;
    }
    return surveyJSON as Record<string, unknown>;
  }

  private readSurveyJsonString(
    jsonObj: Record<string, unknown>,
    key: string
  ): string | undefined {
    const value = jsonObj[key];
    return typeof value === 'string' ? value : undefined;
  }

  // Helper method to convert UTC ISO string to local datetime format
  private convertUTCToLocalDateTime(utcDateString: string): string {
    try {
      // Do very detailed logging to diagnose exactly what's happening

      // Handle database format: YYYY-MM-DD HH:MM:SS.mmm
      if (
        /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}(\.\d{3})?$/.test(utcDateString)
      ) {

        // Convert space to T and remove milliseconds for datetime-local format
        const convertedValue = utcDateString.replace(' ', 'T').split('.')[0];
        return convertedValue;
      }

      // Handle UTC ISO format: YYYY-MM-DDTHH:MM:SS.SSSZ
      // Extract time directly from ISO string - example: "2023-06-15T19:41:00.000Z"
      const parts = utcDateString.split('T');
      if (parts.length !== 2) return utcDateString;

      const datePart = parts[0]; // "2023-06-15"
      const timeWithZ = parts[1]; // "19:41:00.000Z"

      // Extract hours and minutes
      const timeComponents = timeWithZ.split(':');
      let hours = parseInt(timeComponents[0], 10); // 19
      const minutes = timeComponents[1]; // "41"

      // Only add the timezone offset for API data (values ending with Z)
      // and not for user selections which the browser has already adjusted
      if (this.isFromApiLoad || utcDateString.endsWith('Z')) {
        // For UTC ISO strings, always add exactly 2 hours for Danish timezone (GMT+2)
        hours = (hours + 2) % 24;
      }

      // Format with padded zeros
      const formattedHours = String(hours).padStart(2, '0');

      // Create the final result
      const result = `${datePart}T${formattedHours}:${minutes.substring(0, 2)}`;

      return result;
    } catch (error) {
      console.error('[DateTime Debug] Error converting:', error);
      return utcDateString;
    }
  }

  // Method to directly adjust hours in a datetime-local format string
  private adjustDatetimeHours(datetimeString: string): string {
    try {
      // If this datetime has already been adjusted, don't adjust it again
      const uniqueKey = `adjust:${datetimeString}`;
      if (this.adjustedDatetimes.has(uniqueKey)) {
        return datetimeString;
      }

      // Split into date and time parts
      const parts = datetimeString.split('T');
      if (parts.length !== 2) return datetimeString;

      const datePart = parts[0];
      const timePart = parts[1].split(':');
      if (timePart.length < 2) return datetimeString;

      // Extract hours and minutes
      let hours = parseInt(timePart[0], 10);

      // IMPORTANT: Check if this is a user input from datepicker or a value from API
      // For user inputs from datepicker (which don't end with Z), don't adjust the time
      // as the browser has already handled the timezone
      const isUserInput = !datetimeString.endsWith('Z') && !this.isFromApiLoad;

      if (!isUserInput) {
        // Only add hours for API data, not for user-selected values
        hours = (hours + 2) % 24;
      }

      // Format and return
      const newTimePart = `${String(hours).padStart(2, '0')}:${timePart[1]}`;
      const result = `${datePart}T${newTimePart}`;

      // Mark this datetime as already adjusted
      this.adjustedDatetimes.add(uniqueKey);

      return result;
    } catch (error) {
      console.error('[DateTime Debug] Error in adjustDatetimeHours:', error);
      return datetimeString;
    }
  }

  // New method to check if a datetime looks like it's already adjusted
  private isDatetimeAlreadyAdjusted(datetimeString: string): boolean {
    // This is assuming 21:41 is what we want to see in the end
    // If we see either 21:XX or 23:XX, it's likely already been adjusted
    if (!datetimeString || typeof datetimeString !== 'string') return false;

    try {
      const parts = datetimeString.split('T');
      if (parts.length !== 2) return false;

      const timePart = parts[1].split(':');
      if (timePart.length < 2) return false;

      const hours = parseInt(timePart[0], 10);

      // If hours is already 21 or 23, it's likely already adjusted
      return hours === 21 || hours === 23;
    } catch (error) {
      return false;
    }
  }

  // Fix datetime values in form data
  private fixDateTimeValues(data: any): any {
    if (!data || typeof data !== 'object') return data;

    // DISABLED: Just return the original data without any datetime processing
    // The datetime conversion is happening elsewhere and causing issues
    return data;

    // OLD CODE COMMENTED OUT:
    /*
    // Set the flag to indicate we're processing API data
    this.isFromApiLoad = true;
    
    // Make a copy to avoid mutating the original
    const result = { ...data };
    
    for (const key in result) {
      const value = result[key];
      
      // Skip if value is already in correct datetime-local format
      if (typeof value === 'string' && 
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) {
        continue;
      }
      
      // Check if value is a string ending with Z (UTC ISO format)
      if (typeof value === 'string' && value.endsWith('Z')) {
        result[key] = this.convertUTCToLocalDateTime(value);
        
        // Mark this as already adjusted
        const uniqueKey = `adjust:${result[key]}`;
        this.adjustedDatetimes.add(uniqueKey);
      }
      
      // Also check for database format timestamps: YYYY-MM-DD HH:MM:SS.mmm
      else if (typeof value === 'string' && 
          /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}(\.\d{3})?$/.test(value)) {
          
        
        // Convert space to T for datetime-local format and remove milliseconds
        const convertedValue = value.replace(' ', 'T').split('.')[0];
        
        result[key] = convertedValue;
        
        // Mark this as already adjusted
        const uniqueKey = `adjust:${convertedValue}`;
        this.adjustedDatetimes.add(uniqueKey);
      }
    }
    
    // Reset the flag after processing
    this.isFromApiLoad = false;
    
    return result;
    */
  }

  // New method to fix datetime input element without adjusting hours
  private fixDatetimeInputInDOM(questionId: string, value: string): void {
    try {
      // Find the input element
      const inputElement = document.querySelector(
        `#${questionId} input[type="datetime-local"]`
      ) as HTMLInputElement;
      if (!inputElement) {
        return;
      }

      // Set the raw value to ensure it bypasses any SurveyJS transformations
      inputElement.value = value;

      // Add an event listener to prevent SurveyJS from changing it back
      inputElement.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
      });
    } catch (error) {
      console.error('[DateTime DOM] Error fixing input:', error);
    }
  }

  /** Panel model that contains a question inside paneldynamic (panel → questions). */
  private getPanelContainingQuestion(
    question: SurveyQuestion
  ): PanelModel | null {
    const candidates: unknown[] = [
      (question as any).parentQuestion,
      question.parent,
    ];
    for (const candidate of candidates) {
      const panel = candidate as PanelModel | null | undefined;
      if (!panel) continue;
      if (panel.getType?.() === 'panel' || Array.isArray(panel.questions)) {
        return panel;
      }
    }
    return null;
  }

  /** Location GUID for a room dropdown (sibling in panel, or root form via onChangeUpdateQuestion). */
  private getLocationValueForRoomQuestion(question: SurveyQuestion): unknown {
    const panel = this.getPanelContainingQuestion(question);
    if (panel?.questions) {
      const locationQuestion = panel.questions.find((q: SurveyQuestion) => {
        if (
          q.name === 'sub_event_location' ||
          q.name === 'location' ||
          q.name === 'copy_location' ||
          q.name === 'series_default_location' ||
          q.name === 'exception_location'
        ) {
          return true;
        }
        const updateTarget =
          (q as any).onChangeUpdateQuestion ||
          q.getPropertyValue?.('onChangeUpdateQuestion') ||
          (q as any).jsonObj?.onChangeUpdateQuestion;
        return updateTarget === question.name;
      });
      if (
        locationQuestion?.value !== undefined &&
        locationQuestion?.value !== null &&
        locationQuestion?.value !== ''
      ) {
        return locationQuestion.value;
      }
    }

    const updateTargetName = this.getOnChangeUpdateTargetName(question);
    if (updateTargetName && this.surveyModel) {
      const targetQuestion =
        this.surveyModel.getQuestionByName(updateTargetName);
      if (
        targetQuestion?.value !== undefined &&
        targetQuestion?.value !== null &&
        targetQuestion?.value !== ''
      ) {
        return targetQuestion.value;
      }
    }

    return undefined;
  }

  private getOnChangeUpdateTargetName(question: SurveyQuestion): string | null {
    const fromModel =
      question['onChangeUpdateQuestion'] ||
      question.getPropertyValue?.('onChangeUpdateQuestion') ||
      (question as any).jsonObj?.onChangeUpdateQuestion;
    if (typeof fromModel === 'string' && fromModel.trim()) {
      return fromModel.trim();
    }
    const fromJson = this.findElementDefinitionInOriginalJson(question.name)?.[
      'onChangeUpdateQuestion'
    ];
    return typeof fromJson === 'string' && fromJson.trim()
      ? fromJson.trim()
      : null;
  }

  /** Keep «Ingen kategori» first and map legacy empty values to a stable SurveyJS choice value. */
  private ensureEventCategoryQuestionState(question: SurveyQuestion): void {
    const noneChoice = {
      value: EVENT_CATEGORY_NONE_FORM_VALUE,
      text: 'Ingen kategori',
    };
    const existingChoices = question['choices'];
    const raw = Array.isArray(existingChoices) ? existingChoices : [];
    const rest = raw.filter(
      (c: { value?: unknown; text?: string }) =>
        c?.value !== EVENT_CATEGORY_NONE_FORM_VALUE &&
        c?.value !== '' &&
        c?.text !== 'Ingen kategori'
    );
    question['choices'] = [noneChoice, ...rest];

    const stored = eventCategoryFromFormValue(question.value);
    const nextValue =
      stored === null ? EVENT_CATEGORY_NONE_FORM_VALUE : stored;
    if (question.value !== nextValue) {
      question.value = nextValue;
    }
    this.surveyModel?.setValue('event_category', nextValue);
  }

  private isEventExtraRoomQuestion(question: SurveyQuestion): boolean {
    return (
      question.name === 'primary_extra_room' ||
      question.name === 'sub_extra_room' ||
      question.name === 'exception_extra_room'
    );
  }

  private async loadEventExtraRoomQuestion(
    question: SurveyQuestion
  ): Promise<void> {
    const locationValue = this.getLocationValueForRoomQuestion(question);
    if (
      locationValue === undefined ||
      locationValue === null ||
      locationValue === '' ||
      locationValue === 'manual'
    ) {
      return;
    }
    await this.loadQuestionData(question, locationValue);
  }

  private async loadAllEventExtraRoomQuestions(): Promise<void> {
    if (!this.surveyModel) {
      return;
    }
    const extraRoomQuestions = this.surveyModel
      .getAllQuestions()
      .filter((q) => this.isEventExtraRoomQuestion(q));
    await Promise.all(
      extraRoomQuestions.map((q) =>
        this.loadEventExtraRoomQuestion(q as SurveyQuestion)
      )
    );
    this.syncExtraRoomDropdownChoices();
  }

  private buildDataSourceCacheKey(
    dataSourceName: string,
    params: LoadDataParams
  ): string {
    const qp = params.queryParams ?? {};
    return [
      dataSourceName,
      String(
        params.organizationId ??
          qp['organizationId'] ??
          qp['organization_id'] ??
          qp['organizations_id'] ??
          ''
      ),
      String(qp['location'] ?? ''),
      String(params.searchQuery ?? qp['searchQuery'] ?? ''),
    ].join('|');
  }

  private async executeDataSourceWithCache(
    dataSourceName: string,
    params: LoadDataParams,
    execute: () => Observable<unknown>
  ): Promise<unknown> {
    const isSearch = !!(
      params.searchQuery ||
      (params.queryParams && params.queryParams['searchQuery'])
    );
    if (isSearch) {
      return firstValueFrom(execute());
    }

    const key = this.buildDataSourceCacheKey(dataSourceName, params);
    const cached = this.dataSourceResultCache.get(key);
    if (cached !== undefined) {
      return cached;
    }

    const inFlight = this.dataSourceInFlight.get(key);
    if (inFlight) {
      return inFlight;
    }

    const promise = firstValueFrom(execute())
      .then((data) => {
        this.dataSourceResultCache.set(key, data);
        this.dataSourceInFlight.delete(key);
        return data;
      })
      .catch((err) => {
        this.dataSourceInFlight.delete(key);
        throw err;
      });
    this.dataSourceInFlight.set(key, promise);
    return promise;
  }

  private clearDataSourceCaches(): void {
    this.dataSourceResultCache.clear();
    this.dataSourceInFlight.clear();
  }

  private invalidateDataSourceCache(dataSourceName: string): void {
    const prefix = `${dataSourceName}|`;
    for (const key of [...this.dataSourceResultCache.keys()]) {
      if (key.startsWith(prefix)) {
        this.dataSourceResultCache.delete(key);
      }
    }
    for (const key of [...this.dataSourceInFlight.keys()]) {
      if (key.startsWith(prefix)) {
        this.dataSourceInFlight.delete(key);
      }
    }
  }

  private ensureCustomerDropdownChoice(
    question: SurveyQuestion,
    customer: OrganizationCustomerDTO
  ): void {
    const existingChoices = Array.isArray(question['choices'])
      ? [...question['choices']]
      : [];
    const hasChoice = existingChoices.some(
      (choice: { value?: unknown }) => choice.value == customer.guid
    );
    if (!hasChoice) {
      question['choices'] = [
        { value: customer.guid, text: customer.name },
        ...existingChoices,
      ];
    }
  }

  private getExtraRoomPaneldynamicContext(question: SurveyQuestion): {
    paneldynamic: SurveyQuestion;
    fieldName: string;
    paneldynamicName: string;
  } | null {
    const paneldynamicByField: Record<string, string> = {
      primary_extra_room: 'additional_rooms',
      sub_extra_room: 'sub_event_additional_rooms',
      exception_extra_room: 'exception_additional_rooms',
    };
    const paneldynamicName = paneldynamicByField[question.name];
    if (!paneldynamicName || !this.surveyModel) {
      return null;
    }

    let panel = this.getPanelContainingQuestion(question);
    while (panel) {
      const parent = ((panel as any).parentQuestion ?? panel.parent) as
        | SurveyQuestion
        | undefined;
      if (
        parent?.getType?.() === 'paneldynamic' &&
        parent.name === paneldynamicName
      ) {
        return {
          paneldynamic: parent,
          fieldName: question.name,
          paneldynamicName,
        };
      }
      panel =
        parent?.getType?.() === 'panel'
          ? (parent as unknown as PanelModel)
          : null;
    }

    const paneldynamic = this.surveyModel.getQuestionByName(paneldynamicName);
    if (!paneldynamic) {
      return null;
    }
    return {
      paneldynamic: paneldynamic as SurveyQuestion,
      fieldName: question.name,
      paneldynamicName,
    };
  }

  private getPrimaryRoomGuidForExtraRoomPaneldynamic(
    paneldynamicName: string,
    paneldynamic: SurveyQuestion
  ): string | null {
    if (!this.surveyModel) {
      return null;
    }
    if (paneldynamicName === 'additional_rooms') {
      const value = this.surveyModel.getQuestionByName('room')?.value;
      return value != null && value !== '' ? String(value) : null;
    }

    const parent = ((paneldynamic as any).parentQuestion ??
      paneldynamic.parent) as PanelModel | undefined;
    if (!parent?.questions) {
      return null;
    }
    if (paneldynamicName === 'sub_event_additional_rooms') {
      const subRoom = parent.questions.find((q) => q.name === 'sub_event_room');
      if (subRoom?.value != null && subRoom.value !== '') {
        return String(subRoom.value);
      }
      const rootRoom = this.surveyModel.getQuestionByName('room')?.value;
      return rootRoom != null && rootRoom !== '' ? String(rootRoom) : null;
    }
    if (paneldynamicName === 'exception_additional_rooms') {
      const exceptionRoom = parent.questions.find(
        (q) => q.name === 'exception_room'
      );
      if (exceptionRoom?.value != null && exceptionRoom.value !== '') {
        return String(exceptionRoom.value);
      }
    }
    return null;
  }

  private cacheExtraRoomBaseChoices(question: SurveyQuestion): void {
    const choices = question['choices'];
    if (!Array.isArray(choices) || choices.length === 0) {
      return;
    }
    this.extraRoomBaseChoices.set(
      question.id,
      choices.map((choice: any) => ({
        value: choice.value,
        text: choice.text ?? choice.title ?? String(choice.value ?? ''),
      }))
    );
  }

  /** Hide already-picked rooms (incl. primary) from sibling extra-room dropdowns. */
  private syncExtraRoomDropdownChoices(changedQuestion?: SurveyQuestion): void {
    if (!this.surveyModel) {
      return;
    }

    const contexts = new Map<
      string,
      {
        paneldynamic: SurveyQuestion;
        fieldName: string;
        paneldynamicName: string;
      }
    >();

    if (changedQuestion && this.isEventExtraRoomQuestion(changedQuestion)) {
      const ctx = this.getExtraRoomPaneldynamicContext(changedQuestion);
      if (ctx) {
        contexts.set(ctx.paneldynamicName, ctx);
      }
    } else {
      for (const question of this.surveyModel.getAllQuestions()) {
        if (!this.isEventExtraRoomQuestion(question)) {
          continue;
        }
        const ctx = this.getExtraRoomPaneldynamicContext(question);
        if (ctx) {
          contexts.set(ctx.paneldynamicName, ctx);
        }
      }
    }

    for (const {
      paneldynamic,
      fieldName,
      paneldynamicName,
    } of contexts.values()) {
      const primaryRoom = this.getPrimaryRoomGuidForExtraRoomPaneldynamic(
        paneldynamicName,
        paneldynamic
      );
      const panels =
        (paneldynamic as any).panels ||
        (paneldynamic as any).visiblePanels ||
        [];
      const roomQuestions: SurveyQuestion[] = [];

      for (const panel of panels) {
        for (const question of (panel.questions || []) as SurveyQuestion[]) {
          if (question.name === fieldName) {
            roomQuestions.push(question);
          }
        }
      }

      for (const question of roomQuestions) {
        const questionChoices = question['choices'];
        const baseChoices =
          this.extraRoomBaseChoices.get(question.id) ||
          (Array.isArray(questionChoices)
            ? questionChoices.map((choice: any) => ({
                value: choice.value,
                text: choice.text ?? choice.title ?? String(choice.value ?? ''),
              }))
            : []);
        if (
          baseChoices.length > 0 &&
          !this.extraRoomBaseChoices.has(question.id)
        ) {
          this.extraRoomBaseChoices.set(question.id, [...baseChoices]);
        }

        const ownValue =
          question.value != null && question.value !== ''
            ? String(question.value)
            : null;

        if (ownValue && changedQuestion === question) {
          const isPrimaryRoom = !!(primaryRoom && ownValue === primaryRoom);
          const duplicateSibling = roomQuestions.some(
            (other) => other !== question && String(other.value) === ownValue
          );
          if (isPrimaryRoom || duplicateSibling) {
            question.clearValue();
          }
        }

        const effectiveValue =
          question.value != null && question.value !== ''
            ? String(question.value)
            : null;

        const exclude = new Set<string>();
        if (primaryRoom) {
          exclude.add(primaryRoom);
        }
        for (const other of roomQuestions) {
          if (other === question) {
            continue;
          }
          if (other.value != null && other.value !== '') {
            exclude.add(String(other.value));
          }
        }

        question['choices'] = baseChoices.filter((choice) => {
          const value = String(choice.value);
          if (effectiveValue && value === effectiveValue) {
            return true;
          }
          return !exclude.has(value);
        });
      }
    }
  }

  private applyExtraRoomPanelLayoutFix(rootElement?: HTMLElement | null): void {
    const roots: HTMLElement[] = rootElement
      ? [rootElement]
      : (Array.from(
          document.querySelectorAll(
            '[data-name="additional_rooms"], [data-name="sub_event_additional_rooms"], [data-name="exception_additional_rooms"]'
          )
        ) as HTMLElement[]);

    for (const root of roots) {
      root.style.width = '100%';
      root.style.maxWidth = '100%';
      root.style.boxSizing = 'border-box';

      const selectors = [
        '.sd-question__content',
        '.sv-question__content',
        '.sd-paneldynamic',
        '.sv-paneldynamic',
        '.sd-paneldynamic__panels-container',
        '.sd-paneldynamic__panel-wrapper',
        '.sd-paneldynamic__panel',
        '.sd-panel',
        '.sd-element',
        '.sd-element__content',
        '.sd-question--dropdown',
        '.sd-dropdown',
        '.sd-dropdown__value',
        '[data-name="primary_extra_room"]',
        '[data-name="sub_extra_room"]',
        '[data-name="exception_extra_room"]',
      ].join(',');

      root.querySelectorAll(selectors).forEach((node) => {
        const el = node as HTMLElement;
        el.style.width = '100%';
        el.style.maxWidth = '100%';
        el.style.boxSizing = 'border-box';
        if (
          el.classList.contains('sd-element__content') ||
          el.classList.contains('sv-element__content')
        ) {
          el.style.display = 'block';
        }
        if (
          el.classList.contains('sd-dropdown') ||
          el.classList.contains('sv-dropdown')
        ) {
          el.style.minWidth = '280px';
        }
      });
    }
  }

  /** Guest participate: SurveyJS adaptive action bars show a useless "Mere" overflow control. */
  private disableParticipateUsersActionBarOverflow(
    usersQuestion?: SurveyQuestion | null,
    rootElement?: HTMLElement | null
  ): void {
    if (this.originalSurveyJSON?.dataSource !== 'event_participate_form_data') {
      return;
    }
    const users =
      usersQuestion ??
      (this.surveyModel?.getQuestionByName('users') as SurveyQuestion | null);
    if (!users || users.getType() !== 'paneldynamic') {
      return;
    }

    type ActionContainerModel = {
      isResponsivenessDisabled?: boolean;
      dotsItem?: { visible?: boolean };
      flushUpdates?: () => void;
    };

    const disableContainer = (container?: ActionContainerModel | null) => {
      if (!container) {
        return;
      }
      container.isResponsivenessDisabled = true;
      if (container.dotsItem) {
        container.dotsItem.visible = false;
      }
      container.flushUpdates?.();
    };

    const usersPanelDynamic = users as SurveyQuestion & {
      tabbedMenu?: ActionContainerModel | null;
      footerToolbar?: ActionContainerModel | null;
      panels?: Array<{ getFooterToolbar?: () => ActionContainerModel | null }>;
    };

    disableContainer(usersPanelDynamic.tabbedMenu);
    disableContainer(usersPanelDynamic.footerToolbar);
    for (const panel of usersPanelDynamic.panels ?? []) {
      disableContainer(panel.getFooterToolbar?.() ?? null);
    }

    const roots: HTMLElement[] = rootElement
      ? [rootElement]
      : (Array.from(
          document.querySelectorAll('[data-name="users"]')
        ) as HTMLElement[]);
    for (const root of roots) {
      root.querySelectorAll('.sv-dots').forEach((node) => {
        const el = node as HTMLElement;
        el.style.display = 'none';
        el.style.visibility = 'hidden';
        el.setAttribute('aria-hidden', 'true');
      });
      root.querySelectorAll('.sd-action, .sv-action').forEach((node) => {
        const el = node as HTMLElement;
        const label = (
          el.getAttribute('title') ||
          el.getAttribute('aria-label') ||
          el.textContent ||
          ''
        )
          .trim()
          .toLowerCase();
        if (label === 'mere') {
          el.style.display = 'none';
          el.style.visibility = 'hidden';
          el.setAttribute('aria-hidden', 'true');
        }
      });
      this.attachParticipateUsersDotsObserver(users, root);
    }
  }

  private attachParticipateUsersDotsObserver(
    usersQuestion: SurveyQuestion,
    root: HTMLElement
  ): void {
    if (this.participateUsersDotsObserverRoot === root) {
      return;
    }
    this.participateUsersDotsObserver?.disconnect();
    this.participateUsersDotsObserverRoot = root;
    this.participateUsersDotsObserver = new MutationObserver(() => {
      this.disableParticipateUsersActionBarOverflow(usersQuestion, root);
    });
    this.participateUsersDotsObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  private scheduleParticipateUsersActionBarOverflowFix(
    usersQuestion: SurveyQuestion,
    rootElement?: HTMLElement | null
  ): void {
    const run = () =>
      this.disableParticipateUsersActionBarOverflow(usersQuestion, rootElement);
    run();
    requestAnimationFrame(run);
    setTimeout(run, 0);
    setTimeout(run, 100);
    setTimeout(run, 300);
  }

  private applyDropdownWidthToElement(
    element: HTMLElement | null | undefined
  ): void {
    if (!element) {
      return;
    }
    const dataName = element.getAttribute('data-name') || '';
    const isExtraRoom =
      dataName === 'primary_extra_room' ||
      dataName === 'sub_extra_room' ||
      dataName === 'exception_extra_room' ||
      !!element.closest(
        '[data-name="additional_rooms"], [data-name="sub_event_additional_rooms"], [data-name="exception_additional_rooms"]'
      );
    const isLocationDropdown =
      dataName === 'sub_event_location' ||
      dataName === 'sub_event_room' ||
      dataName === 'exception_location' ||
      dataName === 'exception_room' ||
      !!element.closest(
        '[data-name="sub_event_location"], [data-name="sub_event_room"], [data-name="exception_location"], [data-name="exception_room"]'
      );
    const needsFullWidth = isExtraRoom || isLocationDropdown;

    element.style.width = '100%';
    element.style.maxWidth = '100%';
    element.style.boxSizing = 'border-box';
    element.style.flex = needsFullWidth ? '1 1 100%' : '1 1 auto';
    element.style.minWidth = isExtraRoom
      ? '280px'
      : isLocationDropdown
      ? '100%'
      : '';

    const innerDropdown = element.querySelector(
      '.sv-dropdown, .sd-dropdown, .sd-input.sd-dropdown'
    ) as HTMLElement | null;
    if (innerDropdown) {
      innerDropdown.style.width = '100%';
      innerDropdown.style.maxWidth = '100%';
      innerDropdown.style.minWidth = isExtraRoom
        ? '280px'
        : isLocationDropdown
        ? '100%'
        : '';
    }

    const valueEl = element.querySelector(
      '.sv-dropdown__value, .sd-dropdown__value'
    ) as HTMLElement | null;
    if (valueEl) {
      valueEl.style.width = '100%';
      valueEl.style.maxWidth = '100%';
      if (!needsFullWidth) {
        valueEl.style.overflow = 'hidden';
        valueEl.style.textOverflow = 'ellipsis';
      } else {
        valueEl.style.overflow = 'visible';
        valueEl.style.textOverflow = 'clip';
        valueEl.style.whiteSpace = 'normal';
      }
    }

    const filterInput = element.querySelector(
      '.sv-dropdown__filter-string-input, .sd-dropdown__filter-string-input'
    ) as HTMLElement | null;
    if (filterInput && needsFullWidth) {
      filterInput.style.width = '100%';
      filterInput.style.minWidth = '100%';
      filterInput.style.maxWidth = '100%';
      filterInput.style.boxSizing = 'border-box';
    }

    const row = element.closest('.sv-row, .sd-row') as HTMLElement | null;
    if (row) {
      row.style.width = '100%';
      row.style.flexWrap = 'wrap';
    }

    const panelContent = element.closest(
      '.sd-panel__content, .sv-panel__content, .sd-element__content, .sd-paneldynamic__panel-wrapper'
    ) as HTMLElement | null;
    if (panelContent && needsFullWidth) {
      panelContent.style.width = '100%';
      panelContent.style.display = 'block';
    }

    const panel = element.closest(
      '.sv-paneldynamic__panel, .sd-paneldynamic__panel, .sd-panel, .sv-panel'
    ) as HTMLElement | null;
    if (panel) {
      panel.style.width = '100%';
      panel.style.maxWidth = '100%';
    }

    if (isExtraRoom) {
      this.applyExtraRoomPanelLayoutFix(
        element.closest(
          '[data-name="additional_rooms"], [data-name="sub_event_additional_rooms"], [data-name="exception_additional_rooms"]'
        ) as HTMLElement
      );
    }
  }

  /** Whether a panel location is selected (not empty). */
  private hasSelectedPanelLocation(question: SurveyQuestion): boolean {
    const locationValue = this.getLocationValueForRoomQuestion(question);
    return (
      locationValue !== undefined &&
      locationValue !== null &&
      locationValue !== ''
    );
  }

  /** Room/manual follow-ups should only show after a real location choice in the same panel. */
  private shouldRevealLocationDependentQuestion(
    question: SurveyQuestion
  ): boolean {
    if (!question.isVisible) {
      return false;
    }
    const locationValue = this.getLocationValueForRoomQuestion(question);
    if (!locationValue) {
      return false;
    }
    const name = question.name;
    if (name.includes('manual_location') && !name.includes('capacity')) {
      return locationValue === 'manual';
    }
    if (name.includes('room') && !name.includes('capacity')) {
      return locationValue !== 'manual';
    }
    return true;
  }

  private setQuestionElementDisplay(
    question: SurveyQuestion,
    display: 'none' | ''
  ): void {
    setTimeout(() => {
      let targetElement: HTMLElement | null = null;

      if (question.id) {
        targetElement = document.getElementById(question.id) as HTMLElement;
      }

      if (!targetElement && question.parent) {
        const parentPanel = question.parent as { id?: string };
        if (parentPanel?.id) {
          const panelElement = document.getElementById(parentPanel.id);
          if (panelElement) {
            targetElement = panelElement.querySelector(
              `[data-name="${question.name}"]`
            ) as HTMLElement;
          }
        }
      }

      if (!targetElement && question.id) {
        const allWithName = document.querySelectorAll(
          `[data-name="${question.name}"]`
        );
        for (let i = 0; i < allWithName.length; i++) {
          const el = allWithName[i] as HTMLElement;
          const container = el.closest(
            `#${question.id}, [id*="${question.id}"]`
          );
          if (container) {
            targetElement = el;
            break;
          }
        }
      }

      if (targetElement) {
        targetElement.style.display = display;
      }
    }, 50);
  }

  async loadQuestionData(question: any, valueFromQuestionData?: any) {
    // Check both dataSource property and the JSON property
    // For paneldynamic template questions, also check the template question and parent paneldynamic template
    const templateQuestion = (question as any).templateQuestion;

    // Check if question is inside a paneldynamic panel
    // Structure: paneldynamic → panel → question
    // So question.parent is the panel, and panel.parentQuestion is the paneldynamic
    let paneldynamicTemplateQuestion = null;
    let paneldynamicQuestion: any = null;

    // First check if direct parent is a paneldynamic (for some edge cases)
    if (question.parent && question.parent.getType() === 'paneldynamic') {
      paneldynamicQuestion = question.parent;
    }
    // Check if parent is a panel inside a paneldynamic
    else if (
      question.parentQuestion &&
      question.parentQuestion.getType() === 'panel'
    ) {
      const panel = question.parentQuestion;
      if (
        panel.parentQuestion &&
        panel.parentQuestion.getType() === 'paneldynamic'
      ) {
        paneldynamicQuestion = panel.parentQuestion;
      }
    }
    // Also check question.parent.parentQuestion for nested panels
    else if (
      question.parent &&
      question.parent.parentQuestion &&
      question.parent.parentQuestion.getType() === 'paneldynamic'
    ) {
      paneldynamicQuestion = question.parent.parentQuestion;
    }

    // Get template question from the paneldynamic
    if (paneldynamicQuestion && paneldynamicQuestion.template) {
      paneldynamicTemplateQuestion =
        paneldynamicQuestion.template.questions?.find(
          (q: any) => q.name === question.name
        );
      if (paneldynamicTemplateQuestion) {
      }
    }

    const dataSourceName =
      question['dataSource'] ||
      question.getPropertyValue('dataSource') ||
      question['jsonObj']?.dataSource ||
      templateQuestion?.dataSource ||
      templateQuestion?.getPropertyValue?.('dataSource') ||
      templateQuestion?.jsonObj?.dataSource ||
      paneldynamicTemplateQuestion?.dataSource ||
      paneldynamicTemplateQuestion?.getPropertyValue?.('dataSource') ||
      paneldynamicTemplateQuestion?.jsonObj?.dataSource;

    if (!dataSourceName) {
      console.warn(
        `[loadQuestionData] No dataSource found for question ${question.name}`
      );
      return;
    }

    const valueField =
      question['dataSourceValueField'] ||
      question.getPropertyValue('dataSourceValueField') ||
      question['jsonObj']?.dataSourceValueField ||
      templateQuestion?.dataSourceValueField ||
      templateQuestion?.getPropertyValue?.('dataSourceValueField') ||
      templateQuestion?.jsonObj?.dataSourceValueField ||
      paneldynamicTemplateQuestion?.dataSourceValueField ||
      paneldynamicTemplateQuestion?.getPropertyValue?.(
        'dataSourceValueField'
      ) ||
      paneldynamicTemplateQuestion?.jsonObj?.dataSourceValueField;
    const keyField =
      question['dataSourceKeyField'] ||
      question.getPropertyValue('dataSourceKeyField') ||
      question['jsonObj']?.dataSourceKeyField ||
      templateQuestion?.dataSourceKeyField ||
      templateQuestion?.getPropertyValue?.('dataSourceKeyField') ||
      templateQuestion?.jsonObj?.dataSourceKeyField ||
      paneldynamicTemplateQuestion?.dataSourceKeyField ||
      paneldynamicTemplateQuestion?.getPropertyValue?.('dataSourceKeyField') ||
      paneldynamicTemplateQuestion?.jsonObj?.dataSourceKeyField;

    const questionType = question.getType();
    const isDualPanel = questionType === 'dualpanel';
    const isSeriesPlanner = questionType === 'eventseriesplanner';
    // Custom widgets load their own data via SurveyJS afterRender — do not bootstrap again here.
    if (isDualPanel || isSeriesPlanner) {
      return;
    }

    if (dataSourceName) {

      if (valueFromQuestionData) {
      }

      const dataSource =
        this.componentDataSourceService.getDataSource(dataSourceName);
      if (dataSource) {
        try {
          // Save the original selected value before loading data
          const originalValue = question.value;

          // Format the data using LoadDataParams interface with the correct structure
          const params: LoadDataParams = {
            dataSourceKey: dataSourceName,
            filters: [
              { column: 'pageSize', value: 50, operator: 'equals' },
              { column: 'pageIndex', value: 0, operator: 'equals' },
            ],
            queryParams: {
              pageSize: 50,
              pageIndex: 0,
            },
          };
          this.mergeEventFormOrganizationIntoParams(params);

          // Handle different types of valueFromQuestionData
          if (valueFromQuestionData) {
            if (
              typeof valueFromQuestionData === 'object' &&
              valueFromQuestionData.searchQuery
            ) {
              // Handle search query from dropdown search
              const searchText = valueFromQuestionData.searchQuery;

              // Add searchQuery at both top level and in queryParams
              params.searchQuery = searchText;
              if (params.queryParams) {
                params.queryParams['searchQuery'] = searchText;
              }

            } else {
              // Handle value from another question - add as filter/param properly
              if (params.queryParams) {
                // Try to determine the parent question name
                const parentQuestionName =
                  this.findParentQuestionName(question);
                if (parentQuestionName) {
                  // Use the parent question name as the parameter name if available
                  params.queryParams[parentQuestionName] =
                    valueFromQuestionData;

                  // Also add as a filter - ensure filters array exists
                  if (
                    params.filters &&
                    !params.filters.some((f) => f.column === parentQuestionName)
                  ) {
                    params.filters.push({
                      column: parentQuestionName,
                      value: valueFromQuestionData,
                      operator: 'equals',
                    });
                  }

                } else {
                  // Fallback to using the question name directly
                  params.queryParams[question.name] = valueFromQuestionData;
                }
              }
            }
          }

          // Special handling for locations_rooms in paneldynamic contexts
          // If this is locations_rooms and we're in a paneldynamic, get location from parent question
          if (dataSourceName === 'locations_rooms') {
            let locationValue = null;

            // First, check if valueFromQuestionData is a string (location GUID from onChangeUpdateQuestion)
            if (
              valueFromQuestionData &&
              typeof valueFromQuestionData === 'string' &&
              !valueFromQuestionData.includes('searchQuery')
            ) {
              locationValue = valueFromQuestionData;
            } else {
            }

            // If not provided via valueFromQuestionData, try to get from the same panel
            if (!locationValue) {
              locationValue = this.getLocationValueForRoomQuestion(question);
              if (locationValue) {
              }
            }

            // If not found in panel, try the question's onChangeUpdateQuestion target (e.g. root "location")
            if (!locationValue) {
              const updateTargetName =
                this.getOnChangeUpdateTargetName(question);
              if (updateTargetName && this.surveyModel) {
                const targetQuestion =
                  this.surveyModel.getQuestionByName(updateTargetName);
                if (targetQuestion?.value) {
                  locationValue = targetQuestion.value;
                }
              }
            }

            // If still not found, try reverse parent lookup (e.g. location dropdown that updates "room")
            if (!locationValue) {
              const parentQuestionName = this.findParentQuestionName(question);
              if (parentQuestionName) {
                const parentQuestion =
                  this.surveyModel.getQuestionByName(parentQuestionName);
                if (parentQuestion && parentQuestion.value) {
                  locationValue = parentQuestion.value;
                }
              }
            }

            // Add location to query params if found
            if (locationValue) {
              params.queryParams = params.queryParams || {};
              params.queryParams['location'] = locationValue;
            } else {
            }
          }

          // Log the params being sent to the data source

          // Add extra debugging for dual_panel_subtypes
          if (dataSourceName === 'dual_panel_subtypes') {
          }

          // Execute the data source to get the data (deduped across identical panel dropdowns)
          const data: any = await this.executeDataSourceWithCache(
            dataSourceName,
            params,
            () => dataSource.execute(params)
          );

          if (dataSourceName === 'event_price_templates') {
            this.cacheCoursePriceTemplatesFromData(data);
            if (this.surveyModel) {
              this.applyCoursePricingLogic(this.surveyModel, {
                name: 'course_price_template_ids',
              });
            }
          }
          if (dataSourceName === 'event_price_groups') {
            this.cachePriceGroupsFromData(data);
            if (this.surveyModel) {
              this.applyCoursePricingLogic(this.surveyModel, {
                name: 'course_price_group_id',
              });
            }
          }
          if (dataSourceName === 'customers') {
            this.cacheCustomersFromData(data);
          }

          // Debug logging for locations_rooms
          if (dataSourceName === 'locations_rooms') {
          }

          // More debugging for dual_panel_subtypes
          if (dataSourceName === 'dual_panel_subtypes') {
          }

          // Helper function to get nested property value using dot notation
          const getNestedValue = (obj: any, path: string) => {
            return path
              .split('.')
              .reduce((current, key) => current?.[key], obj);
          };

          // Process the data into choices
          let choices: any[] = [];
          if (Array.isArray(data)) {
            choices = data.map((item: any) => {
              let value, text;

              // First determine the value to be stored
              if (valueField) {
                value = getNestedValue(item, valueField);
              } else if (keyField) {
                // Fall back to keyField if no valueField
                value = getNestedValue(item, keyField);
              } else {
                value = item.id || item.value || item;
              }

              // Then determine what to display
              if (keyField) {
                text = getNestedValue(item, keyField);
              } else {
                text = item.name || item.text || item.label || String(value);
              }

              const choice = { value, text };
              return choice;
            });
          } else if (data && data.items && Array.isArray(data.items)) {
            choices = data.items.map((item: any) => {
              let value, text;

              // First determine the value to be stored
              if (valueField) {
                value = getNestedValue(item, valueField);
              } else if (keyField) {
                // Fall back to keyField if no valueField
                value = getNestedValue(item, keyField);
              } else {
                value = item.id || item.value || item;
              }

              // Then determine what to display
              if (keyField) {
                text = getNestedValue(item, keyField);
              } else {
                text = item.name || item.text || item.label || String(value);
              }

              const choice = { value, text };
              return choice;
            });
          } else if (data && data.data && Array.isArray(data.data)) {
            choices = data.data.map((item: any) => {
              let value, text;

              // First determine the value to be stored
              if (valueField) {
                value = getNestedValue(item, valueField);
              } else if (keyField) {
                // Fall back to keyField if no valueField
                value = getNestedValue(item, keyField);
              } else {
                value = item.id || item.value || item;
              }

              // Then determine what to display
              if (keyField) {
                text = getNestedValue(item, keyField);
              } else {
                text = item.name || item.text || item.label || String(value);
              }

              const choice = { value, text };
              return choice;
            });
          } else {
          }

          if (choices && choices.length > 0) {

            // Log if original value will be preserved
            if (originalValue !== undefined && originalValue !== null) {
              const valueExists = choices.some((choice: any) => {
                const match = choice.value == originalValue; // Use loose equality to handle numeric/string conversion
                if (match) {
                }
                return match;
              });

              if (!valueExists) {
              }
            }

            // Check if we should merge with static choices
            const shouldMerge =
              question['mergeWithStaticChoices'] ||
              question.getPropertyValue('mergeWithStaticChoices') ||
              question['jsonObj']?.mergeWithStaticChoices;

            // Check if this is a search operation
            const isSearchOperation =
              valueFromQuestionData &&
              typeof valueFromQuestionData === 'object' &&
              valueFromQuestionData.searchQuery;

            if (isSearchOperation) {
              // For search operations, replace the choices completely

              // Get the stored search text for this question
              const storedSearchText = this.searchTextMap.get(question.name);

              // If search returned no results, add a "No results" option
              if (choices.length === 0) {
                question.choices = [
                  {
                    value: null,
                    text: `No results found for "${storedSearchText}"`,
                  },
                ];
              } else {
                question.choices = choices;
              }

              // After updating choices, restore the search text
              setTimeout(() => {
                // Get the stored search text for this question
                const storedSearchText = this.searchTextMap.get(question.name);
                if (storedSearchText) {
                  // Find the input element
                  const inputElement = document.querySelector(
                    `#${question.id} input.sv-dropdown__filter-string`
                  ) as HTMLInputElement;
                  if (inputElement) {
                    inputElement.value = storedSearchText;

                    // Trigger input event to ensure the dropdown filters correctly
                    const event = new Event('input', { bubbles: true });
                    inputElement.dispatchEvent(event);
                  }
                }
              }, 50);
            } else if (
              shouldMerge &&
              question['choices'] &&
              Array.isArray(question['choices'])
            ) {
              // Create a Set of existing values to avoid duplicates
              const existingValues = new Set(
                question['choices'].map((c: any) => c.value)
              );
              // Only add new choices that don't exist
              const newChoices = choices.filter(
                (choice: any) => !existingValues.has(choice.value)
              );
              question.choices = [...question['choices'], ...newChoices];
            } else {
              const oldLength = question.choices ? question.choices.length : 0;
              question.choices = choices;

              // If this is a room question and we have choices, show only when location is selected
              if (question.name.includes('room') && choices.length > 0) {
                if (!this.shouldRevealLocationDependentQuestion(question)) {
                  question.value = null;
                  question.clearValue();
                  this.setQuestionElementDisplay(question, 'none');
                } else {
                  this.setQuestionElementDisplay(question, '');
                }
              }
            }

            // Restore the original value if it exists (for standard controls)
            if (originalValue !== undefined && originalValue !== null) {

              // Check if the original value is in the new choices using loose equality
              const valueExists = choices.some(
                (choice: any) => choice.value == originalValue
              );

              if (valueExists) {
                // Use setTimeout to ensure the UI has updated
                setTimeout(() => {
                  question.value = originalValue;
                }, 50);
              } else {
                //console.warn(`WARNING: Could not restore original value ${originalValue} for ${question.name} - not found in choices`);
              }
            }

            // Check if there are any dependent questions that need to be updated
            const dependents = this.findDependentQuestions(question);
            if (dependents.length > 0) {
              // If this question has a value, update its dependents
              if (question.value) {
                for (const dependent of dependents) {
                  await this.loadQuestionData(dependent, question.value);
                }
              }
            }

            if (question.name === 'event_category') {
              this.ensureEventCategoryQuestionState(question);
            }

            if (
              this.isEventExtraRoomQuestion(question) &&
              question['choices']?.length
            ) {
              this.cacheExtraRoomBaseChoices(question);
              this.syncExtraRoomDropdownChoices(question);
            }
          } else {
            // locations_rooms without a location param — defer; parent may not be ready yet
            if (
              dataSourceName === 'locations_rooms' &&
              !params.queryParams?.['location']
            ) {
              return;
            }

            // No choices found — reset choices; clear value safely (avoid SurveyJS null edge cases)
            question.choices = [];
            if (question.name === 'event_category') {
              this.ensureEventCategoryQuestionState(question);
              return;
            }
            if (
              question.value !== undefined &&
              question.value !== null &&
              question.value !== ''
            ) {
              try {
                question.clearValue();
              } catch {
                this.surveyModel?.setValue(question.name, undefined);
              }
            }

            // Force hide the question via DOM manipulation for room questions
            // IMPORTANT: Only hide THIS specific question instance, not all questions with the same name
            if (question.name.includes('room')) {

              // Find the question element and hide it - scoped to THIS specific question instance
              setTimeout(() => {
                // Use the question's unique ID to target ONLY this instance
                // This is critical for paneldynamic questions where multiple instances exist
                let targetElement: HTMLElement | null = null;

                // First try to find by the question's unique ID
                if (question.id) {
                  targetElement = document.getElementById(
                    question.id
                  ) as HTMLElement;
                  if (targetElement) {
                  }
                }

                // If not found by ID, try to find within the question's parent panel
                if (!targetElement && question.parent) {
                  // For paneldynamic, find the panel that contains this question
                  const parentPanel = question.parent;
                  if (parentPanel && parentPanel.id) {
                    const panelElement = document.getElementById(
                      parentPanel.id
                    );
                    if (panelElement) {
                      targetElement = panelElement.querySelector(
                        `[data-name="${question.name}"]`
                      ) as HTMLElement;
                      if (targetElement) {
                      }
                    }
                  }
                }

                // Last resort: try to find by data-name but only if we can verify it's the right instance
                // This is less reliable but better than querySelectorAll which affects all instances
                if (!targetElement && question.id) {
                  // Try to find element that has both the name and is within the question's container
                  const allWithName = document.querySelectorAll(
                    `[data-name="${question.name}"]`
                  );
                  for (let i = 0; i < allWithName.length; i++) {
                    const el = allWithName[i] as HTMLElement;
                    // Check if this element is within a container that matches our question ID
                    const container = el.closest(
                      `#${question.id}, [id*="${question.id}"]`
                    );
                    if (container) {
                      targetElement = el;
                      break;
                    }
                  }
                }

                // Only hide if we found the specific element
                if (targetElement) {
                  targetElement.style.display = 'none';
                } else {
                  console.warn(
                    `[loadQuestionData] Could not find specific element for room question ${question.name} (ID: ${question.id}) - skipping DOM manipulation to avoid affecting other panels`
                  );
                }
              }, 50);
            }
          }
        } catch (error) {
          console.error(
            `Error loading data for question ${question.name}:`,
            error
          );
        }
      } else {
        console.error(
          `Data source '${dataSourceName}' not found for question ${question.name}`
        );
      }
    } else {
    }
  }

  async ngOnInit() {

    // Add width fix for SurveyJS
    this.addSurveyJSWidthFix();

    // Make Angular services available globally for SurveyJS custom components
    (window as any).surveyInjector = this.injector;
    (window as any).surveyAppRef = this.appRef;
    (window as any).surveyEnvInjector = this.environmentInjector;

    // Add global fix for SurveyJS dropdowns
    // (removed: addGlobalSurveyJSFix / addSurveyJSDropdownDiagnostics — caused popup flicker in modals)

    // Removed MutationObserver to prevent interference with page rendering

    // Removed aggressive periodic checks to improve performance

    // Clear the adjustment tracker at startup
    this.adjustedDatetimes.clear();

    try {
      // Register component data source service and dual panel selector in window object
      // This allows them to be used by SurveyJS custom widgets
      (window as any).componentDataSourceService =
        this.componentDataSourceService;
      (window as any).dualPanelSelector = dualPanelSelector;
      (window as any).dynamicTableQuestion = dynamicTableQuestion;

      // Set default authentication status
      this.isAuthenticated = false; // Default to not authenticated

      // Set up authentication subscription with a safer approach
      let authInitialized = false;

      this.authService.isAuthenticated$
        .pipe(
          take(1), // Only take the first emission
          timeout(2000), // Add a timeout
          catchError((error) => {
            console.error('[AUTH-DEBUG] Error in authentication check:', error);
            return of(false); // Default to not authenticated on error
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (isAuthenticated) => {
            this.isAuthenticated = isAuthenticated;
            authInitialized = true;

            // Update page visibility if survey is already initialized
            if (this.surveyModel) {
              this.updatePageVisibility(this.surveyModel);
              // Also check for submit button after auth changes
              this.checkAndShowSubmitButton(this.surveyModel);
              // If the current page was hidden by the auth rule change, navigate to the first
              // visible page so the user is never left on an invisible page (which would cause
              // navigation buttons to disappear).
              const authVP = this.surveyModel.visiblePages;
              if (
                authVP.length > 0 &&
                !authVP.includes(this.surveyModel.currentPage)
              ) {
                try {
                  const firstIdx = this.surveyModel.pages.indexOf(authVP[0]);
                  if (firstIdx >= 0) {
                    this.surveyModel.currentPageNo = firstIdx;
                  }
                  this.surveyModel.currentPage = authVP[0];
                } catch {}
              }
            } else {
            }
          },
          error: (error) => {
            console.error(
              '[AUTH-DEBUG] Error in authentication subscription:',
              error
            );
            // Keep default authentication status (false)
            authInitialized = true;
          },
        });

      // Set up ongoing authentication subscription
      this.authService.isAuthenticated$
        .pipe(
          skip(1), // Skip the first emission as we already handled it
          catchError((error) => {
            console.error(
              '[AUTH-DEBUG] Error in ongoing authentication subscription:',
              error
            );
            return of(false); // Default to not authenticated on error
          }),
          takeUntil(this.destroy$)
        )
        .subscribe((isAuthenticated) => {
          this.isAuthenticated = isAuthenticated;

          // Update page visibility when authentication changes
          if (this.surveyModel) {
            this.updatePageVisibility(this.surveyModel);
            // Also check for submit button after auth changes
            this.checkAndShowSubmitButton(this.surveyModel);
            // Navigate away from any page that was hidden by the auth change so the user
            // is never left on an invisible page (which would cause navigation buttons to
            // disappear).  Only move when the current page is no longer visible — do NOT
            // use the "new pages appeared before current position" heuristic here, because
            // that is only appropriate during data-source loading, not auth changes.
            const ongoingVP = this.surveyModel.visiblePages;
            if (
              ongoingVP.length > 0 &&
              !ongoingVP.includes(this.surveyModel.currentPage)
            ) {
              try {
                const firstIdx = this.surveyModel.pages.indexOf(ongoingVP[0]);
                if (firstIdx >= 0) {
                  this.surveyModel.currentPageNo = firstIdx;
                }
                this.surveyModel.currentPage = ongoingVP[0];
              } catch {}
            }
          } else {
          }
        });

      // Continue with survey initialization without waiting for authentication
      const jsonObj = this.resolveSurveyJsonForInit();

      // Ensure the authVisibility property is registered with SurveyJS
      if (!Serializer.findProperty('page', 'authVisibility')) {
        Serializer.addProperty('page', {
          name: 'authVisibility',
          default: 0,
          category: 'general',
          visibleIndex: 3,
        });
      }
      // Custom data-source-driven dropdowns (RNG): SurveyJS must persist these on the model/jsonObj
      const questionDataSourceProps: Array<{
        name: string;
        type: 'string' | 'boolean';
      }> = [
        { name: 'dataSource', type: 'string' },
        { name: 'dataSourceKeyField', type: 'string' },
        { name: 'dataSourceValueField', type: 'string' },
        { name: 'mergeWithStaticChoices', type: 'boolean' },
        { name: 'allowCreate', type: 'boolean' },
        { name: 'onChangeUpdateQuestion', type: 'string' },
      ];
      for (const p of questionDataSourceProps) {
        if (!Serializer.findProperty('question', p.name)) {
          Serializer.addProperty('question', {
            name: p.name,
            type: p.type,
            category: 'data',
            visibleIndex: 2,
          });
        }
      }

      // Store a DEEP COPY before SurveyModel creation — SurveyJS mutates jsonObj during new SurveyModel(jsonObj),
      // stripping custom properties (like visibilityDataSource) from the raw page objects. The deep copy preserves
      // the original values so loadFormData can scan them correctly.
      this.originalSurveyJSON = JSON.parse(JSON.stringify(jsonObj));

      // Store navigationButtonsLocation from JSON before creating model (SurveyJS should apply it automatically, but we'll ensure it's set)
      this.navigationButtonsLocationFromJSON = this.readSurveyJsonString(
        jsonObj,
        'navigationButtonsLocation'
      );

      // CRITICAL: Ensure navigationButtonsLocation is in the JSON before creating the model
      // SurveyJS v2.0.1 needs this property in the JSON object itself
      if (
        this.navigationButtonsLocationFromJSON &&
        jsonObj['navigationButtonsLocation'] === undefined
      ) {
        jsonObj['navigationButtonsLocation'] =
          this.navigationButtonsLocationFromJSON;
      }

      // Create the survey model
      this.surveyModel = new SurveyModel(jsonObj as any);
      this.setupEventFormCssClassHooks(this.surveyModel);
      this.setupModalDropdownBehavior();
      this.setupDropdownPopupLayering();

      // When the form has a dataSource (e.g. event_form_data) or modalData (e.g. group-calendar
      // dialog), do NOT reveal yet. surveyReady is set only after loadFormData() so custom widgets
      // like dual-panel see pre-selected values on first render.
      const deferRevealUntilFormDataLoaded =
        this.shouldDeferRevealUntilFormDataLoaded(jsonObj as Record<string, unknown>);

      // Immediately apply navigationButtonsLocation from JSON if present (before any rendering)
      if (this.navigationButtonsLocationFromJSON) {
        (this.surveyModel as any).navigationButtonsLocation =
          this.navigationButtonsLocationFromJSON;
      }

      // Apply auth visibility before the survey is mounted.
      this.updatePageVisibility(this.surveyModel);

      if (!deferRevealUntilFormDataLoaded) {
        if (!this.surveyReady) {
          this.revealSurvey();
        }
        setTimeout(() => {
          if (!this.surveyReady && this.surveyModel) {
            this.revealSurvey();
          }
        }, 150);
      }

      // Apply default values from JSON to ensure they're set
      // Use setTimeout to ensure the form is fully rendered first
      setTimeout(() => {
        this.applyDefaultValues(jsonObj);
        // Re-apply completeText after localization might have overridden it
        const completeTextAfterDefaults = this.readSurveyJsonString(
          jsonObj,
          'completeText'
        );
        if (completeTextAfterDefaults) {
          this.surveyModel.completeText = completeTextAfterDefaults;
        }
        // Ensure submit button visibility once initial render completes
        this.checkAndShowSubmitButton(this.surveyModel);
      }, 100);

      // Apply optional overrides from component settings (if provided).
      // These two are captured outside try{} so lines below (which run unconditionally) can
      // honour them and not silently overwrite what settings already applied.
      let settingsNavLoc: string | undefined;
      let settingsShowNavExplicitFalse = false;
      try {
        const hasSettings = this.hasInput('settings');
        if (hasSettings) {
          const showNav = this.getSettingValue('showNavigationButtons');
          const navLoc = this.getSettingValue('navigationButtonsLocation');
          const prevText = this.getSettingValue('pagePrevText');
          const nextText = this.getSettingValue('pageNextText');
          const hideSubmit = this.getSettingValue('hideSubmitButton');
          const showEmptyFormSubmit = this.getSettingValue(
            'showEmptyFormSubmit'
          );

          if (showNav !== undefined) {
            this.surveyModel.showNavigationButtons = Boolean(showNav);
            if (Boolean(showNav) === false) {
              settingsShowNavExplicitFalse = true;
            }
          }
          if (navLoc) {
            (this.surveyModel as any).navigationButtonsLocation = navLoc;
            settingsNavLoc = String(navLoc);
          }
          if (prevText) {
            this.surveyModel.pagePrevText = String(prevText);
          }
          if (nextText) {
            this.surveyModel.pageNextText = String(nextText);
          }
          if (hideSubmit === true) {
            (this as any).hideSubmitButtonForThisInstance = true;
            (this.surveyModel as any).showEmptyFormSubmit = false;
          }
          if (showEmptyFormSubmit === false) {
            (this.surveyModel as any).showEmptyFormSubmit = false;
          }
        }

        // Also check JSON object directly for navigationButtonsLocation (SurveyJS Creator saves it here)
        if (
          this.navigationButtonsLocationFromJSON &&
          !(this.surveyModel as any).navigationButtonsLocation
        ) {
          (this.surveyModel as any).navigationButtonsLocation =
            this.navigationButtonsLocationFromJSON;
        }
      } catch {}

      // Ensure navigation button labels are defined (fallbacks)
      if (!this.surveyModel.pagePrevText) {
        this.surveyModel.pagePrevText = 'Forrige';
      }
      if (!this.surveyModel.pageNextText) {
        this.surveyModel.pageNextText = 'Næste';
      }
      if (!this.surveyModel.completeText) {
        this.surveyModel.completeText = 'Gem';
      }

      // Enable the submit button even when there are no questions.
      // Do NOT override an explicit showNavigationButtons:false from settings — that would re-show
      // the nav bar and trigger the forceUpdateNavigationButtons interval flash every ~400 ms.
      this.surveyModel.showCompletedPage = true;
      if (!settingsShowNavExplicitFalse) {
        this.surveyModel.showNavigationButtons = true;
      }
      // Apply navigationButtonsLocation — settings take precedence over JSON.
      // If settings explicitly provided a value (e.g. 'none'), keep it; otherwise fall back to
      // whatever the JSON author specified, and default to "bottom" if neither is present.
      if (!settingsNavLoc && this.navigationButtonsLocationFromJSON) {
        (this.surveyModel as any).navigationButtonsLocation =
          this.navigationButtonsLocationFromJSON;
      } else if (
        !settingsNavLoc &&
        !(this.surveyModel as any).navigationButtonsLocation
      ) {
        (this.surveyModel as any).navigationButtonsLocation = 'bottom';
      }
      // Ensure completeText from JSON is preserved
      const completeTextFromJson = this.readSurveyJsonString(
        jsonObj,
        'completeText'
      );
      if (completeTextFromJson) {
        this.surveyModel.completeText = completeTextFromJson;
      }

      // This is the key setting that enables the submit button even when no questions are visible
      this.surveyModel.checkErrorsMode = 'onComplete';
      this.surveyModel.requiredText = ' *'; // Restore required asterisk indicator
      this.surveyModel.showQuestionNumbers = 'off';

      // Additional critical settings for allowing empty form submission
      this.surveyModel.maxTimeToFinish = 0; // No time limit
      this.surveyModel.maxTimeToFinishPage = 0; // No time limit per page
      this.surveyModel.clearInvisibleValues = 'none'; // Don't clear any values
      if (this.isEventAddEditBundledForm) {
        // Event add/edit: show a clickable stepper (page titles) above the form.
        this.surveyModel.showProgressBar = 'top';
        this.surveyModel.progressBarType = 'buttons';
        this.surveyModel.progressBarShowPageNumbers = true;
        this.surveyModel.progressBarShowPageTitles = true;
      } else {
        this.surveyModel.showProgressBar = 'off'; // Hide progress bar
      }

      // ESSENTIAL: These settings ensure the submit button is available
      this.surveyModel.firstPageIsStarted = false;
      // REMOVED: this.surveyModel.isSinglePage = true; // This was disabling multi-page functionality
      this.surveyShowPageTitlesDefault = !this.isEventAddEditBundledForm;
      this.surveyModel.showPageTitles = this.surveyShowPageTitlesDefault;

      if (jsonObj['showTitle'] === false) {
        this.surveyModel.showTitle = false;
        // Survey-level description above top nav creates a large gap when the title is hidden.
        this.surveyModel.description = '';
      }

      this.surveyModel.onAfterRenderSurvey.add((_sender, options) => {
        this.compactTopSurveyChrome(
          options.htmlElement as HTMLElement | undefined
        );
      });

      // Critical setting: allow empty surveys
      // @ts-ignore - This is a valid setting but might not be in type definitions
      this.surveyModel.allowCompleteSurveyAutomatic = true;

      // Force the form to be completable at all times
      // @ts-ignore - This is a valid setting but might not be in type definitions
      this.surveyModel.isCompletedBefore = false;

      // Override the canBeCompleted check
      const originalCanBeCompleted = this.surveyModel.canBeCompleted;
      this.surveyModel.canBeCompleted = function () {
        // Always return true to allow completion
        return true;
      };

      // Keep only the essential override for form completion
      // Don't override rendering methods as they break normal functionality

      // Load the authVisibility property from the JSON
      this.ensureAuthVisibilityLoaded(this.surveyModel, jsonObj);

      // Submit button text: "Afmeld" on cancel page (page6), otherwise JSON completeText (e.g. "Bekræft")
      const syncCompleteTextForCancelPage = () => {
        const curPage = this.surveyModel.currentPage;
        if (curPage?.name === 'page6') {
          this.surveyModel.completeText = 'Afmeld';
        } else if (this.originalSurveyJSON?.completeText) {
          this.surveyModel.completeText = this.originalSurveyJSON.completeText;
        }
      };
      this.surveyModel.onAfterRenderSurvey.add(() =>
        setTimeout(syncCompleteTextForCancelPage, 0)
      );
      this.surveyModel.onCurrentPageChanged.add((sender) => {
        syncCompleteTextForCancelPage();
        this.syncEventSeriesPlannerWithCurrentPage(sender as SurveyModel);
      });

      // If submit must be hidden in this instance, hide only the complete/submit button — keep prev/next.
      if ((this as any).hideSubmitButtonForThisInstance) {
        (this.surveyModel as any).showPreviewBeforeComplete = false;
        (this.surveyModel as any).showCompleteButton = false;
        // Keep showCompletedPage enabled so SurveyJS still renders page navigation.

        const hideCompleteButtons = () => this.hideSurveySubmitButtonsOnly();
        this.surveyModel.onAfterRenderSurvey.add(() =>
          setTimeout(hideCompleteButtons, 0)
        );
        this.surveyModel.onCurrentPageChanged.add(() => {
          setTimeout(hideCompleteButtons, 0);
          // Ensure navigationButtonsLocation is applied on page change (fixes initial render issue)
          try {
            const effectiveLoc = this.getEffectiveNavigationButtonsLocation();
            if (effectiveLoc) {
              (this.surveyModel as any).navigationButtonsLocation =
                effectiveLoc;
            }
          } catch {}
          // Force update navigation buttons in DOM on page change
          setTimeout(() => {
            this.forceUpdateNavigationButtons();
            this.removeTopNavigationPadding();
          }, 0);
          setTimeout(() => {
            this.forceUpdateNavigationButtons();
            this.removeTopNavigationPadding();
          }, 50);
        });
      }

      // Dynamic table components are now created directly by the widget

      // Add onChoicesSearch event handler at the survey level
      this.surveyModel.onChoicesSearch.add((sender, options) => {
        const question = options.question;
        const filter = options.filter;
        const dataSourceName =
          question['dataSource'] ||
          question.getPropertyValue('dataSource') ||
          question['jsonObj']?.dataSource;

        if (dataSourceName) {
          // Store the current search text for this specific question
          this.searchTextMap.set(question.name, filter || '');

          // Clear any existing timeout for this question
          if (this.searchTimeouts.has(question.name)) {
            clearTimeout(this.searchTimeouts.get(question.name));
          }

          // Set a new timeout for debouncing
          const timeoutId = setTimeout(() => {

            // Load data after debounce
            this.loadQuestionData(question, { searchQuery: filter || '' });

            // Remove the timeout from the map
            this.searchTimeouts.delete(question.name);
          }, this.debounceTime);

          // Store the timeout ID
          this.searchTimeouts.set(question.name, timeoutId);

          // Don't filter choices here, as we'll load them from the data source
          options.filteredChoices = options.choices;
        }
      });

      // Add handler to ensure navigation buttons are set on each page render
      this.surveyModel.onAfterRenderPage.add((sender, options) => {
        // Check if we're on the completion page and hide submit buttons
        if (
          (sender as any).isCompleted ||
          (sender as any).state === 'completed'
        ) {
          setTimeout(() => {
            this.hideSubmitButtonsOnCompletion();
          }, 0);
          setTimeout(() => {
            this.hideSubmitButtonsOnCompletion();
          }, 100);
        }

        // Check for completion page elements in DOM
        const completionPage = document.querySelector(
          '.sv-completedpage, .sv-body__page--completed'
        );
        if (completionPage) {
          setTimeout(() => {
            this.hideSubmitButtonsOnCompletion();
          }, 0);
          setTimeout(() => {
            this.hideSubmitButtonsOnCompletion();
          }, 100);
        }

        // Ensure navigationButtonsLocation is applied on every page render
        this.syncSurveyNavigationModel();
        const effectiveNavLoc = this.getEffectiveNavigationButtonsLocation();
        if (effectiveNavLoc) {
          (sender as any).navigationButtonsLocation = effectiveNavLoc;
        }
        // Force update navigation buttons in DOM after page render
        setTimeout(() => {
          this.forceUpdateNavigationButtons();
          this.removeTopNavigationPadding();
        }, 0);
        setTimeout(() => {
          this.forceUpdateNavigationButtons();
          this.removeTopNavigationPadding();
        }, 50);

        if (
          this.isEventAddEditBundledForm &&
          options.htmlElement &&
          sender.currentPage
        ) {
          const pageEl = options.htmlElement as HTMLElement;
          const questions = sender.currentPage.questions;
          const reattach = () => reattachPageInfoModals(pageEl, questions);
          setTimeout(reattach, 0);
          setTimeout(reattach, 120);
        }
      });

      // Configure dropdown and tagbox settings
      this.surveyModel.onAfterRenderQuestion.add(async (sender, options) => {
        const question = options.question as SurveyQuestion;

        if (options.htmlElement) {
          const htmlEl = options.htmlElement as HTMLElement;
          if (this.isEventAddEditBundledForm && question.name) {
            const qRoot = (
              htmlEl.classList.contains('sd-question') ||
              htmlEl.classList.contains('sv-question')
                ? htmlEl
                : htmlEl.closest('.sd-question, .sv-question')
            ) as HTMLElement | null;
            (qRoot ?? htmlEl).setAttribute('data-name', question.name);
          }
          if (question.getType() !== 'infomodal') {
            const tuckStar = () => tuckRequiredStarIntoTitle(htmlEl);
            tuckStar();
            setTimeout(tuckStar, 0);
            setTimeout(tuckStar, 120);
          }
          if (question.getType() === 'infomodal') {
            attachInfomodalBadgeToPartner(question, htmlEl);
          } else if (
            question.name &&
            !question.name.startsWith('info_') &&
            question.survey?.getQuestionByName(`info_${question.name}`)
          ) {
            attachInfomodalFromPartnerQuestion(question, htmlEl);
          }
        }

        if (
          question.name === 'users' &&
          this.originalSurveyJSON?.dataSource === 'event_participate_form_data' &&
          options.htmlElement
        ) {
          this.scheduleParticipateUsersActionBarOverflowFix(
            question,
            options.htmlElement as HTMLElement
          );
        }

        if (
          this.originalSurveyJSON?.dataSource === 'event_participate_form_data' &&
          (question.name === 'path_module_completed_warning' ||
            question.name === 'path_module_completed_warning_disabled')
        ) {
          const html = String(
            this.surveyModel?.data?.['path_module_completed_warning_html'] ?? ''
          );
          const htmlQuestion = question as SurveyQuestion & { html?: string };
          if (html && htmlQuestion.html !== html) {
            htmlQuestion.html = html;
          }
        }

        if (question.name === 'linked_events_info' && options.htmlElement) {
          this.wireUnlinkEventFromGroupButton(
            sender as SurveyModel,
            options.htmlElement as HTMLElement
          );
        }

        if (question.name === 'delete_event_section' && options.htmlElement) {
          this.wireDeleteEventButton(
            sender as SurveyModel,
            options.htmlElement as HTMLElement
          );
        }

        if (question.name === 'catering_wishes_launch' && options.htmlElement) {
          this.wireCateringWishesButton(
            sender as SurveyModel,
            options.htmlElement as HTMLElement
          );
        }

        if (question.name === 'customer_guid_stamdata') {
          void this.refreshCustomerStamdataDisplay(sender as SurveyModel);
        }

        if (question.name === 'course_revenue_model' && options.htmlElement) {
          this.syncCourseRevenueModelChoiceUi(
            sender as SurveyModel,
            options.htmlElement as HTMLElement
          );
        }

        const allowCreate =
          question['allowCreate'] ||
          question.getPropertyValue?.('allowCreate') ||
          question['jsonObj']?.allowCreate;
        if (
          allowCreate &&
          question.getType() === 'dropdown' &&
          options.htmlElement
        ) {
          this.wireCreateCustomerButton(
            sender as SurveyModel,
            question,
            options.htmlElement as HTMLElement
          );
        }

        let dataSourceName =
          question['dataSource'] ||
          question.getPropertyValue('dataSource') ||
          question['jsonObj']?.dataSource;

        // Check if this question is inside a paneldynamic panel
        // Structure: paneldynamic → panel → question
        // So question.parent or question.parentQuestion is the panel, and panel.parentQuestion is the paneldynamic
        let isInPaneldynamic = false;
        let paneldynamicQuestion: any = null;

        // Check if direct parent is a paneldynamic
        if (question.parent && question.parent.getType() === 'paneldynamic') {
          isInPaneldynamic = true;
          paneldynamicQuestion = question.parent;
        }
        // Check if parent is a panel inside a paneldynamic
        else if (
          (question as any).parentQuestion &&
          (question as any).parentQuestion.getType() === 'panel'
        ) {
          const panel = (question as any).parentQuestion;
          if (
            panel.parentQuestion &&
            panel.parentQuestion.getType() === 'paneldynamic'
          ) {
            isInPaneldynamic = true;
            paneldynamicQuestion = panel.parentQuestion;
          }
        }
        // Also check question.parent.parentQuestion for nested panels
        else if (
          question.parent &&
          (question.parent as any).parentQuestion &&
          (question.parent as any).parentQuestion.getType() === 'paneldynamic'
        ) {
          isInPaneldynamic = true;
          paneldynamicQuestion = (question.parent as any).parentQuestion;
        }

        // If inside paneldynamic and no dataSource found, check the paneldynamic's template
        if (!dataSourceName && isInPaneldynamic && paneldynamicQuestion) {
          if (paneldynamicQuestion.template) {
            const templateQuestion =
              paneldynamicQuestion.template.questions?.find(
                (q: any) => q.name === question.name
              );
            if (templateQuestion) {
              dataSourceName =
                templateQuestion.dataSource ||
                templateQuestion.getPropertyValue?.('dataSource') ||
                templateQuestion.jsonObj?.dataSource;
            }
          }
        }

        // Force width for paneldynamic dropdown questions
        if (isInPaneldynamic && question.getType() === 'dropdown') {
          // Use setTimeout to ensure the DOM is rendered - apply multiple times
          const questionElement = options.htmlElement as
            | HTMLElement
            | undefined;
          [50, 150, 300].forEach((delay) => {
            setTimeout(() => {
              this.applyDropdownWidthToElement(questionElement);
              if (question.id) {
                this.applyDropdownWidthToElement(
                  document.getElementById(question.id)
                );
              }
              this.applyWidthToAllPaneldynamicDropdowns();
            }, delay);
          });
        }

        // Handle dataSource loading for paneldynamic template questions
        if (isInPaneldynamic && dataSourceName) {
          if (
            dataSourceName === 'locations_rooms' &&
            this.isEventExtraRoomQuestion(question)
          ) {
            await this.loadEventExtraRoomQuestion(question);
            return;
          }
          if (
            dataSourceName === 'locations_rooms' &&
            !this.hasSelectedPanelLocation(question)
          ) {
            return;
          }
          const locationHint =
            dataSourceName === 'locations_rooms'
              ? this.getLocationValueForRoomQuestion(question)
              : undefined;
          await this.loadQuestionData(question, locationHint);
        }

        // Special handling for radiogroup questions to ensure defaults are applied
        if (question.getType() === 'radiogroup') {
          setTimeout(() => {
            this.ensureRadiogroupDefault(question);
          }, 100);
        }

        // Handle datetime constraint functionality
        if (
          question.getType() === 'text' &&
          question['inputType'] === 'datetime-local'
        ) {
          const constraintEnabled =
            question['datetimeMaxConstraintEnabled'] ||
            question.getPropertyValue('datetimeMaxConstraintEnabled') ||
            question['jsonObj']?.datetimeMaxConstraintEnabled;

          if (constraintEnabled) {
            const sourceQuestionName =
              question['datetimeMaxConstraintQuestion'] ||
              question.getPropertyValue('datetimeMaxConstraintQuestion') ||
              question['jsonObj']?.datetimeMaxConstraintQuestion;

            const showCopyButton =
              question['datetimeShowCopyButton'] !== false &&
              question.getPropertyValue('datetimeShowCopyButton') !== false &&
              question['jsonObj']?.datetimeShowCopyButton !== false;

            if (sourceQuestionName) {
              // Wait for DOM to be fully rendered
              setTimeout(() => {
                this.setupDatetimeConstraint(
                  question,
                  sourceQuestionName,
                  showCopyButton
                );
              }, 500);

              // Also setup constraint after form data is loaded (with longer delay)
              setTimeout(() => {
                this.setupDatetimeConstraint(
                  question,
                  sourceQuestionName,
                  showCopyButton
                );
              }, 2000);
            } else {
              console.warn(
                `No source question specified for datetime constraint on ${question.name}`
              );
            }
          }
        }

        // Fix for datetime-local inputs
        if (
          question.getType() === 'text' &&
          question['inputType'] === 'datetime-local'
        ) {
          this.applyDateTimeLocalDefault(question);

          // Allow time for the input to be rendered
          setTimeout(() => {
            // Don't fix if no value
            if (!question.value) return;

            // Skip if the value is already adjusted
            const uniqueKey = `adjust:${question.value}`;
            if (this.adjustedDatetimes.has(uniqueKey)) {
              return;
            }

            // CRITICAL FIX: Only apply timezone conversion for API data, not user input
            // Check if this is a user input (from datetime-local picker) or API data
            const isUserInput =
              !question.value.endsWith('Z') && !this.isFromApiLoad;

            if (isUserInput) {
              // For user inputs, don't apply any timezone conversion
              return;
            }

            // Determine if we need to adjust the time
            let fixedValue = question.value;
            let needsAdjustment = false;

            // For API data only, handle timezone adjustments
            this.isFromApiLoad = true;

            // Handle different formats
            if (question.value.endsWith('Z')) {
              // For UTC ISO strings
              fixedValue = this.convertUTCToLocalDateTime(question.value);
              needsAdjustment = true;
            } else if (
              /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(question.value) &&
              !this.isDatetimeAlreadyAdjusted(question.value)
            ) {
              // Only adjust for API data, not for user inputs
              fixedValue = this.adjustDatetimeHours(question.value);
              needsAdjustment = true;
            }

            // Reset flag
            this.isFromApiLoad = false;

            if (needsAdjustment && fixedValue !== question.value) {
              // Apply the fix directly to the DOM
              this.fixDatetimeInputInDOM(question.id, fixedValue);

              // Also update the model
              question.value = fixedValue;

              // Mark as adjusted
              this.adjustedDatetimes.add(`adjust:${fixedValue}`);

            }
          }, 200); // Allow time for the input to be rendered

          // Use onPropertyChanged event to fix timezone issues
          question.onPropertyChanged.add((sender, options) => {
            if (options.name === 'value' && options.newValue) {
              const newValue = options.newValue;

              // Skip if already adjusted
              const uniqueKey = `adjust:${newValue}`;
              if (this.adjustedDatetimes.has(uniqueKey)) {
                return;
              }

              // CRITICAL FIX: Only apply timezone conversion for API data, not user input
              // Check if this is a user input (from datetime-local picker) or API data
              const isUserInput =
                !newValue.endsWith('Z') && !this.isFromApiLoad;

              if (isUserInput) {
                // For user inputs, don't apply any timezone conversion
                return;
              }

              // If it's a UTC ISO string (from API), convert to local time
              if (typeof newValue === 'string' && newValue.endsWith('Z')) {
                // Set isFromApiLoad to true for API data
                this.isFromApiLoad = true;
                const localDateTimeValue =
                  this.convertUTCToLocalDateTime(newValue);

                // Mark as adjusted
                this.adjustedDatetimes.add(`adjust:${localDateTimeValue}`);

                // Use setTimeout to avoid triggering the event again
                setTimeout(() => {
                  question.value = localDateTimeValue;

                  // Also fix in DOM
                  this.fixDatetimeInputInDOM(question.id, localDateTimeValue);
                  // Reset flag
                  this.isFromApiLoad = false;
                }, 0);
              }
              // Also check for non-Z timestamps from API that might need adjustment
              // Skip user inputs which don't need adjusting as browser handles timezone
              else if (
                typeof newValue === 'string' &&
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(newValue) &&
                !this.isDatetimeAlreadyAdjusted(newValue) &&
                this.isFromApiLoad
              ) {
                const localDateTimeValue = this.adjustDatetimeHours(newValue);

                // Mark as adjusted
                this.adjustedDatetimes.add(`adjust:${localDateTimeValue}`);

                // Use setTimeout to avoid triggering the event again
                setTimeout(() => {
                  question.value = localDateTimeValue;

                  // Also fix in DOM
                  this.fixDatetimeInputInDOM(question.id, localDateTimeValue);
                }, 0);
              }
            }
          });
        }

        // Add onValueChanged event handler for the question
        question.registerFunctionOnPropertyValueChanged('value', async () => {
          // Get onChangeUpdateQuestion from question or template
          let onChangeUpdateQuestionName =
            question['onChangeUpdateQuestion'] ||
            question.getPropertyValue('onChangeUpdateQuestion') ||
            question['jsonObj']?.onChangeUpdateQuestion;

          // Also check paneldynamic template for the property
          if (
            !onChangeUpdateQuestionName &&
            isInPaneldynamic &&
            paneldynamicQuestion?.template
          ) {
            const templateQ = paneldynamicQuestion.template.questions?.find(
              (q: any) => q.name === question.name
            );
            if (templateQ) {
              onChangeUpdateQuestionName =
                templateQ.onChangeUpdateQuestion ||
                templateQ.getPropertyValue?.('onChangeUpdateQuestion') ||
                templateQ.jsonObj?.onChangeUpdateQuestion;
            }
          }

          if (onChangeUpdateQuestionName) {
            // Add a small delay to allow visibility conditions to be re-evaluated first
            await new Promise((resolve) => setTimeout(resolve, 100));

            let targetQuestion: any = null;

            // For paneldynamic questions, find the target question within the same panel
            if (isInPaneldynamic) {
              // Get the panel this question belongs to - try multiple approaches
              let panel = (question as any).parentQuestion || question.parent;

              // If parent is the paneldynamic itself, we need to find the actual panel
              if (panel && panel.getType() === 'paneldynamic') {
                // Get the panel that contains this question from paneldynamic's panels
                const panels = panel.panels || panel.visiblePanels;

                if (panels) {
                  for (const p of panels) {
                    const foundQ = p.questions?.find(
                      (q: any) => q.name === question.name && q === question
                    );
                    if (foundQ) {
                      panel = p;
                      break;
                    }
                  }
                }
              }

              if (panel && (panel.getType?.() === 'panel' || panel.questions)) {
                // Find the target question in the same panel (include hidden questions)
                const questions = panel.questions || panel.elements || [];

                targetQuestion = questions.find(
                  (q: any) => q.name === onChangeUpdateQuestionName
                );
                if (targetQuestion) {
                }
              }

              // If still not found, try finding via the paneldynamic's panels
              if (!targetQuestion && paneldynamicQuestion) {
                const panels =
                  paneldynamicQuestion.panels ||
                  paneldynamicQuestion.visiblePanels;
                if (panels) {
                  for (const p of panels) {
                    // Check if this panel contains our source question
                    const hasSource = p.questions?.some(
                      (q: any) => q.name === question.name && q === question
                    );
                    if (hasSource) {
                      targetQuestion = p.questions?.find(
                        (q: any) => q.name === onChangeUpdateQuestionName
                      );
                      if (targetQuestion) {
                        break;
                      }
                    }
                  }
                }
              }
            }

            // For paneldynamic questions, ONLY update if we found the target in the same panel
            // Do NOT fall back to global search as it will affect other panels
            if (isInPaneldynamic && !targetQuestion) {
              return; // Don't update anything if we can't find it in the same panel
            }

            // Fallback to global search ONLY for non-paneldynamic questions
            if (!targetQuestion && !isInPaneldynamic) {
              targetQuestion = this.surveyModel.getQuestionByName(
                onChangeUpdateQuestionName
              );
            }

            if (targetQuestion) {
              await this.loadQuestionData(targetQuestion, question.value);

              // For room dropdowns (both paneldynamic and regular), handle visibility based on choices
              if (onChangeUpdateQuestionName.includes('room')) {
                setTimeout(() => {
                  const hasChoices =
                    targetQuestion.choices && targetQuestion.choices.length > 0;
                  const shouldShow =
                    hasChoices &&
                    this.shouldRevealLocationDependentQuestion(targetQuestion);

                  if (!shouldShow) {
                    targetQuestion.value = null;
                    targetQuestion.clearValue();
                    if (!hasChoices) {
                      targetQuestion.choices = [];
                    }
                    this.setQuestionElementDisplay(targetQuestion, 'none');
                  } else {
                    this.setQuestionElementDisplay(targetQuestion, '');
                  }
                }, 150);
              }
            } else {
            }
          }
        });

        if (question.getType() === 'dropdown') {
          const dropdownQuestion = question as QuestionDropdownModel;

          // Basic configuration
          dropdownQuestion.allowClear = true;
          dropdownQuestion.searchEnabled = true;
          dropdownQuestion.searchMode = 'contains';

          // Force standard rendering
          dropdownQuestion.renderAs = 'default';

          // Always load data if there's a data source
          if (dataSourceName) {
            await this.loadQuestionData(dropdownQuestion);
          } else if (isInPaneldynamic) {
            // For paneldynamic questions, try to get dataSource from the question's JSON definition
            const questionJson =
              question['jsonObj'] ||
              (question as any).templateQuestion?.jsonObj;
            if (questionJson?.dataSource) {
              await this.loadQuestionData(dropdownQuestion);
            }
          }

          if (dataSourceName) {
            // Add onSearchInputChange event handler to send search text to data source
            dropdownQuestion['onSearchInputChange'] = (text: string) => {

              // Store the search text
              this.searchTextMap.set(dropdownQuestion.name, text);

              // Clear any existing timeout for this question
              if (this.searchTimeouts.has(dropdownQuestion.name)) {
                clearTimeout(this.searchTimeouts.get(dropdownQuestion.name));
              }

              // Set a new timeout for debouncing
              const timeoutId = setTimeout(() => {

                // Load data after debounce
                this.loadQuestionData(dropdownQuestion, {
                  searchQuery: text,
                }).then(() => {
                  // Restore the search text after loading data
                  setTimeout(() => {
                    const inputElement = document.querySelector(
                      `#${dropdownQuestion.id} input.sv-dropdown__filter-string`
                    ) as HTMLInputElement;
                    if (inputElement) {
                      inputElement.value = text;
                      const event = new Event('input', { bubbles: true });
                      inputElement.dispatchEvent(event);
                    }
                  }, 50);
                });

                // Remove the timeout from the map
                this.searchTimeouts.delete(question.name);
              }, this.debounceTime);

              // Store the timeout ID
              this.searchTimeouts.set(question.name, timeoutId);

              // Return false to prevent default filtering
              return false;
            };
          }
        }
        if (question.getType() === 'tagbox') {
          question['searchEnabled'] = true;
          question['allowAddTags'] = true;
          question['placeholder'] = 'Vælg eller skriv tags...';

          // Always load data if there's a data source
          if (dataSourceName) {
            await this.loadQuestionData(question);
          }

          if (dataSourceName) {
            // Add onSearchInputChange event handler to send search text to data source
            question['onSearchInputChange'] = (text: string) => {

              // Store the search text
              this.searchTextMap.set(question.name, text);

              // Clear any existing timeout for this question
              if (this.searchTimeouts.has(question.name)) {
                clearTimeout(this.searchTimeouts.get(question.name));
              }

              // Set a new timeout for debouncing
              const timeoutId = setTimeout(() => {

                // Load data after debounce
                this.loadQuestionData(question, { searchQuery: text }).then(
                  () => {
                    // Restore the search text after loading data
                    setTimeout(() => {
                      const inputElement = document.querySelector(
                        `#${question.id} input.sv-dropdown__filter-string`
                      ) as HTMLInputElement;
                      if (inputElement) {
                        inputElement.value = text;
                        const event = new Event('input', { bubbles: true });
                        inputElement.dispatchEvent(event);
                      }
                    }, 50);
                  }
                );

                // Remove the timeout from the map
                this.searchTimeouts.delete(question.name);
              }, this.debounceTime);

              // Store the timeout ID
              this.searchTimeouts.set(question.name, timeoutId);

              // Return false to prevent default filtering
              return false;
            };
          }
        }
      });

      // Handle dataSource loading for paneldynamic template questions
      this.surveyModel.onAfterRenderPanel.add(async (sender, options) => {
        const panel = options.panel;
        if (panel && panel.questions) {
          // Check if this panel is inside a paneldynamic
          const paneldynamicQuestion = panel.parentQuestion;
          const isPaneldynamicPanel =
            paneldynamicQuestion &&
            paneldynamicQuestion.getType() === 'paneldynamic';

          // Apply width to all paneldynamic dropdowns whenever a panel renders
          if (isPaneldynamicPanel) {
            setTimeout(() => this.applyWidthToAllPaneldynamicDropdowns(), 50);
            setTimeout(() => this.applyWidthToAllPaneldynamicDropdowns(), 200);

            // Add copy button to sub_events paneldynamic panels
            const paneldynamicName =
              paneldynamicQuestion.name ||
              paneldynamicQuestion.getPropertyValue?.('name');
            if (paneldynamicName === 'sub_events') {
              setTimeout(
                () =>
                  this.addCopyButtonToPaneldynamicPanel(
                    panel,
                    paneldynamicQuestion,
                    options.htmlElement,
                    'Kopiér'
                  ),
                100
              );
            }
            if (
              paneldynamicName === 'users' &&
              this.originalSurveyJSON?.dataSource === 'event_participate_form_data'
            ) {
              this.scheduleParticipateUsersActionBarOverflowFix(
                paneldynamicQuestion as SurveyQuestion,
                (options.htmlElement as HTMLElement | undefined) ??
                  undefined
              );
            }
          }

          for (const question of panel.questions) {
            // For paneldynamic panels, also check the template
            let dataSourceName =
              question['dataSource'] ||
              question.getPropertyValue('dataSource') ||
              question['jsonObj']?.dataSource;

            // If not found and this is a paneldynamic panel, check the template
            if (!dataSourceName && isPaneldynamicPanel) {
              const paneldynamicQuestionTyped = paneldynamicQuestion as any;
              if (paneldynamicQuestionTyped.template) {
                const templateQuestion =
                  paneldynamicQuestionTyped.template.questions?.find(
                    (q: any) => q.name === question.name
                  );
                if (templateQuestion) {
                  dataSourceName =
                    templateQuestion.dataSource ||
                    templateQuestion.getPropertyValue?.('dataSource') ||
                    templateQuestion.jsonObj?.dataSource;
                }
              }
            }

            if (
              dataSourceName &&
              (question.getType() === 'dropdown' ||
                question.getType() === 'tagbox')
            ) {
              // Load data for dropdown/tagbox questions in paneldynamic panels
              try {
                await this.loadQuestionData(question);
              } catch (error) {
                console.error(
                  `Error loading data for paneldynamic question ${question.name}:`,
                  error
                );
              }
            }
          }
        }
      });

      // Handle dynamically added panels in paneldynamic - apply width to dropdowns
      this.surveyModel.onDynamicPanelAdded.add((sender, options) => {
        const panel = options.panel;

        if (
          options.question?.name === 'additional_dates' &&
          panel
        ) {
          this.seedAdditionalDatePanelDefaults(
            sender,
            options.panelIndex ?? 0,
            panel as {
              getQuestionByName?: (name: string) => { value: unknown } | null;
            }
          );
        }

        if (panel?.questions) {
          panel.questions.forEach((q: any) =>
            this.applyDateTimeLocalDefault(q)
          );
          void (async () => {
            for (const q of panel.questions as SurveyQuestion[]) {
              if (this.isEventExtraRoomQuestion(q)) {
                await this.loadEventExtraRoomQuestion(q);
              }
            }
            this.syncExtraRoomDropdownChoices();
            this.applyExtraRoomPanelLayoutFix();
            this.cdr.detectChanges();
          })();
        }

        // Apply width to all dropdown questions in the new panel
        setTimeout(() => {
          this.applyWidthToAllPaneldynamicDropdowns();
        }, 100);

        // Also apply after a longer delay to catch late renders
        setTimeout(() => {
          this.applyWidthToAllPaneldynamicDropdowns();
        }, 300);
      });

      // Event participate (guest): panel/tab titles from names + collapse when fields are valid.
      const participateUsersTabTitleMaxLen = 28;
      const formatParticipateUsersPanelDisplayTitle = (
        panel: { questions?: SurveyQuestion[] },
        panelIndex: number
      ): string => {
        const questions = panel.questions ?? [];
        const firstName = String(
          questions.find((q) => q.name === 'firstName')?.value ?? ''
        ).trim();
        const lastName = String(
          questions.find((q) => q.name === 'lastName')?.value ?? ''
        ).trim();
        let fullName = `${firstName} ${lastName}`.trim();
        if (!fullName) {
          fullName = firstName || lastName;
        }
        const displayIndex = panelIndex >= 0 ? panelIndex + 1 : 1;
        if (!fullName) {
          return `Deltager #${displayIndex}`;
        }
        if (fullName.length <= participateUsersTabTitleMaxLen) {
          return fullName;
        }
        return `${fullName.slice(0, participateUsersTabTitleMaxLen - 1).trimEnd()}…`;
      };

      const applyParticipateUsersPanelTitle = (
        panel: {
          title?: string;
          questions?: SurveyQuestion[];
          locStrsChanged?: () => void;
        },
        panelIndex: number
      ): boolean => {
        const title = formatParticipateUsersPanelDisplayTitle(panel, panelIndex);
        if (panel.title === title) {
          return false;
        }
        panel.title = title;
        panel.locStrsChanged?.();
        return true;
      };

      this.surveyModel.onGetDynamicPanelTabTitle.add((_sender, options) => {
        if (this.originalSurveyJSON?.dataSource !== 'event_participate_form_data') {
          return;
        }
        if (options.question?.name !== 'users' || !options.panel) {
          return;
        }
        options.title = formatParticipateUsersPanelDisplayTitle(
          options.panel as { questions?: SurveyQuestion[] },
          options.visiblePanelIndex ?? 0
        );
      });

      this.surveyModel.onDynamicPanelAdded.add((_sender, options) => {
        if (this.originalSurveyJSON?.dataSource !== 'event_participate_form_data') {
          return;
        }
        if (options.question?.name !== 'users' || !options.panel) {
          return;
        }
        applyParticipateUsersPanelTitle(
          options.panel as { title?: string; questions?: SurveyQuestion[]; locStrsChanged?: () => void },
          options.panelIndex ?? 0
        );
        (options.question as { locStrsChanged?: () => void }).locStrsChanged?.();
        this.scheduleParticipateUsersActionBarOverflowFix(
          options.question as SurveyQuestion
        );
      });

      this.surveyModel.onDynamicPanelValueChanged.add((sender, options) => {
        if (
          options.question?.name === 'additional_dates' &&
          options.name === 'same_catering_as_previous' &&
          options.value !== true &&
          options.value !== 'true' &&
          options.panel
        ) {
          const panelIndex = options.panelIndex ?? 0;
          const prevCatering = previousCateringBeforeAdditionalIndex(
            (sender.data || {}) as Record<string, unknown>,
            panelIndex
          );
          const cateringQ = (
            options.panel as {
              getQuestionByName?: (name: string) => { value: unknown } | null;
            }
          ).getQuestionByName?.('sub_event_catering');
          if (cateringQ && !String(cateringQ.value ?? '').trim()) {
            cateringQ.value = prevCatering;
          }
        }

        if (this.originalSurveyJSON?.dataSource !== 'event_participate_form_data') {
          return;
        }
        if (options.question?.name !== 'users') {
          return;
        }
        const panel = options.panel as {
          collapse?: () => void;
          isCollapsed?: boolean;
          questions?: SurveyQuestion[];
          title?: string;
          locStrsChanged?: () => void;
        };
        if (!panel?.questions?.length) {
          return;
        }
        if (options.name === 'firstName' || options.name === 'lastName') {
          const titleChanged = applyParticipateUsersPanelTitle(
            panel,
            options.panelIndex ?? 0
          );
          if (titleChanged && options.question) {
            (options.question as { locStrsChanged?: () => void }).locStrsChanged?.();
          }
        }
        if (options.question) {
          this.scheduleParticipateUsersActionBarOverflowFix(
            options.question as SurveyQuestion
          );
        }

        const requiredNames = ['firstName', 'lastName', 'email', 'cpr'];
        const complete = requiredNames.every((fieldName) => {
          const q = panel.questions!.find((pq) => pq.name === fieldName);
          if (!q) {
            return false;
          }
          const value = q.value;
          if (value === undefined || value === null || String(value).trim() === '') {
            return false;
          }
          return !q.errors || q.errors.length === 0;
        });
        if (!complete) {
          return;
        }
        const usersQuestion = options.question as { displayMode?: string };
        if (usersQuestion.displayMode !== 'list') {
          return;
        }
        setTimeout(() => {
          if (typeof panel.collapse === 'function' && panel.isCollapsed !== true) {
            panel.collapse();
            this.cdr.detectChanges();
          }
        }, 0);
      });

      // Add a periodic check for paneldynamic dropdown widths (runs every 500ms for first 5 seconds)
      let widthCheckCount = 0;
      const widthCheckInterval = setInterval(() => {
        this.applyWidthToAllPaneldynamicDropdowns();
        widthCheckCount++;
        if (widthCheckCount >= 10) {
          clearInterval(widthCheckInterval);
        }
      }, 500);

      // Flag to prevent re-entry when doComplete() triggers onCompleting again
      let isSubmitting = false;

      this.surveyModel.onNavigateToUrl.add((_sender, options) => {
        if (this.suppressSurveyNavigateToUrlOnce) {
          this.suppressSurveyNavigateToUrlOnce = false;
          options.url = '';
        }
      });

      // Use onCompleting to handle async submission and prevent completion on error
      this.surveyModel.onCompleting.add((sender, options) => {
        // Prevent re-entry - if we're already submitting, allow completion
        if (isSubmitting) {
          return; // Allow completion (don't set allowComplete = false)
        }

        const formReceiveFunction = this.getInput('formReceiveFunction');
        if (formReceiveFunction) {
          const dataDest =
            this.componentDataDestService.getDataDest(formReceiveFunction);
          if (dataDest) {

            // Set flag BEFORE calling data destination to prevent any re-entry
            isSubmitting = true;

            let result;
            try {
              result = dataDest(sender.data);
            } catch (error: any) {
              console.error('Error calling data destination:', error);
              isSubmitting = false; // Reset flag on error
              this.isFormSubmitting = false; // Reset class flag on error
              options.allowComplete = false;
              const errorMessage =
                error?.message ||
                error?.error?.message ||
                'An error occurred while submitting the form';
              this.showFormError(errorMessage);
              return;
            }

            if (result && typeof result.subscribe === 'function') {
              // Handle Observable result - prevent completion until we know the result
              options.allowComplete = false; // Prevent completion for now

              let submissionHandled = false;

              // Subscribe to the Observable to get the response
              // Data destinations that return Observables should use shareReplay(1) internally
              // to prevent duplicate HTTP requests when both they and the form subscribe
              result.subscribe({
                next: (response: any) => {
                  submissionHandled = true;

                  // Check if response indicates success
                  if (
                    response &&
                    (response.status === 'error' || response.error)
                  ) {
                    // Response indicates an error
                    isSubmitting = false; // Reset flag on error
                    this.isFormSubmitting = false; // Reset class flag on error
                    const errorMessage =
                      response?.message ||
                      response?.error ||
                      'An error occurred while submitting the form';
                    this.showFormError(errorMessage);
                    // Don't complete the form - keep it editable
                    this.restoreSurveyAfterFailedSubmit(sender);
                  } else {
                    // Form submission was successful - allow completion
                    // isSubmitting stays true so the re-entry check allows completion
                    // Reset class flag after successful completion
                    this.isFormSubmitting = false;
                    let openedCateringDialog = false;
                    if (formReceiveFunction === 'add_event') {
                      openedCateringDialog = this.handlePostSubmitCatering(
                        sender as SurveyModel,
                        response
                      );
                    }
                    if (openedCateringDialog) {
                      this.suppressSurveyNavigateToUrlOnce = true;
                      (sender as SurveyModel).navigateToUrl = '';
                      isSubmitting = false;
                    }
                    sender.doComplete();
                    // Auto-scroll to top of modal after successful submission
                    setTimeout(() => this.scrollToTopOfModal(), 100);
                  }
                },
                error: (error: any) => {
                  console.error('Data destination error:', error);
                  submissionHandled = true;
                  isSubmitting = false; // Reset flag on error
                  this.isFormSubmitting = false; // Reset class flag on error

                  // Extract error message from various possible error formats
                  let errorMessage =
                    'An error occurred while submitting the form';

                  // Handle HTTP error responses
                  if (error?.error) {
                    if (error.error.message) {
                      errorMessage = error.error.message;
                      const detail =
                        typeof error.error.error === 'string'
                          ? error.error.error.trim()
                          : '';
                      if (
                        detail &&
                        detail !== errorMessage &&
                        errorMessage === 'Database error'
                      ) {
                        errorMessage = `${errorMessage}: ${detail}`;
                      }
                    } else if (typeof error.error === 'string') {
                      errorMessage = error.error;
                    } else if (error.error.error) {
                      errorMessage = error.error.error;
                    }
                  } else if (error?.message) {
                    errorMessage = error.message;
                  } else if (typeof error === 'string') {
                    errorMessage = error;
                  }

                  console.error('Form submission error:', errorMessage);
                  this.showFormError(errorMessage);
                  if (formReceiveFunction === 'add_event') {
                    this.openCateringAfterSave = false;
                  }
                  this.restoreSurveyAfterFailedSubmit(sender);
                },
              });

              // Set a timeout to handle cases where the observable never emits
              setTimeout(() => {
                if (!submissionHandled) {
                  console.warn(
                    'Form submission did not complete within timeout'
                  );
                  isSubmitting = false; // Reset flag on timeout
                  this.isFormSubmitting = false; // Reset class flag on timeout
                  this.showFormError(
                    'Form submission timed out. Please try again.'
                  );
                  if (formReceiveFunction === 'add_event') {
                    this.openCateringAfterSave = false;
                  }
                  this.restoreSurveyAfterFailedSubmit(sender);
                }
              }, 30000); // 30 second timeout

              return; // Prevent default completion
            } else {
              // Non-Observable result or undefined
              // Data destinations that don't return Observables handle everything internally
              // Just allow the form to complete
              // isSubmitting stays true - form will complete normally
              // Reset class flag after successful completion
              setTimeout(() => {
                this.isFormSubmitting = false;
              }, 100);
            }
          } else {
            console.error('Data destination not found:', formReceiveFunction);
            this.showFormError(
              'Form submission configuration error: Data destination not found'
            );
            options.allowComplete = false;
            return;
          }
        } else {
        }
      });

      // Handle successful completion (for non-async cases)
      this.surveyModel.onComplete.add((sender) => {
        // Reset submission flag on completion
        this.isFormSubmitting = false;
        // Hide all submit buttons on completion page
        this.hideSubmitButtonsOnCompletion();
        // Auto-scroll to top of modal after form submission
        this.scrollToTopOfModal();
      });

      // Add handler for empty forms and page visibility issues
      this.surveyModel.onAfterRenderSurvey.add((sender, options) => {
        // Fallback: ensure form becomes visible if the main path never runs. Do NOT run this when
        // we defer reveal until loadFormData() (dataSource or modalData).
        const deferReveal = this.shouldDeferRevealUntilFormDataLoaded();
        setTimeout(() => {
          if (!this.surveyReady && this.surveyModel && !deferReveal) {
            this.revealSurvey();
          }
        }, 600);
        // If reveal is deferred, fall back after 3s if loadFormData never completes.
        if (deferReveal) {
          setTimeout(() => {
            if (!this.surveyReady && this.surveyModel) {
              this.ensureCurrentPageIsFirstVisible();
              this.revealSurvey();
            }
          }, 3000);
        }

        // Always restore navigation after render (also when hideSubmitButton is set).
        try {
          this.syncSurveyNavigationModel();
          const effectiveNavLoc = this.getEffectiveNavigationButtonsLocation();
          if (effectiveNavLoc) {
            (sender as any).navigationButtonsLocation = effectiveNavLoc;
          }
          this.forceUpdateNavigationButtons();
        } catch {}

        const effectiveNavLocForRetry =
          this.getEffectiveNavigationButtonsLocation();
        if (effectiveNavLocForRetry) {
          setTimeout(() => {
            this.syncSurveyNavigationModel();
            (sender as any).navigationButtonsLocation = effectiveNavLocForRetry;
            this.forceUpdateNavigationButtons();
          }, 0);
          setTimeout(() => {
            this.syncSurveyNavigationModel();
            (sender as any).navigationButtonsLocation = effectiveNavLocForRetry;
            this.forceUpdateNavigationButtons();
          }, 50);
          setTimeout(() => {
            this.syncSurveyNavigationModel();
            (sender as any).navigationButtonsLocation = effectiveNavLocForRetry;
            this.forceUpdateNavigationButtons();
          }, 100);
          setTimeout(() => {
            this.forceUpdateNavigationButtons();
            this.removeTopNavigationPadding();
          }, 200);
          setTimeout(() => {
            this.forceUpdateNavigationButtons();
            this.removeTopNavigationPadding();
          }, 500);
        }

        if ((this as any).hideSubmitButtonForThisInstance) {
          setTimeout(() => this.hideSurveySubmitButtonsOnly(), 0);
          return;
        }
        this.ensureNativeCompleteButtonVisible();

        this.applyAddUsersToEventEnrollmentDefaults(sender);

        // Check for empty form scenarios immediately and with multiple checks
        this.checkAndShowSubmitButton(sender);

        // Also check after a delay to catch any late-rendering scenarios
        setTimeout(() => {
          this.checkAndShowSubmitButton(sender);
        }, 100);

        setTimeout(() => {
          this.checkAndShowSubmitButton(sender);
        }, 500);
      });

      // Re-apply visibilityDataSource-driven visibility after each value change so we override
      // SurveyJS's visibleIf re-evaluation (which can set page.visible back to true).
      this.surveyModel.onValueChanged.add((sender, options) => {
        if (isPlannerSyncingToSurvey(sender as SurveyModel)) {
          return;
        }
        // event_type refresh is async; re-applying stored visibility here can resurrect page2 with stale visible:true.
        if (options?.name === 'event_type') {
          return;
        }
        setTimeout(() => this.updatePageVisibility(this.surveyModel), 0);
      });

      this.surveyModel.onValueChanged.add((_sender, options) => {
        const refreshFields = new Set([
          'organization_id',
          'organizations_id',
          'organizationId',
          'location',
          'room',
        ]);
        if (!refreshFields.has(options.name)) {
          return;
        }
        setTimeout(() => void this.refreshBookingFieldVisibility(), 0);
      });

      this.surveyModel.onValueChanged.add((_sender, options) => {
        if (options.name !== 'event_type') {
          return;
        }
        void this.refreshEventTypeDependentVisibility();
      });

      this.surveyModel.onValueChanged.add((_sender, options) => {
        if (
          !options.question ||
          !this.isEventExtraRoomQuestion(options.question as SurveyQuestion)
        ) {
          return;
        }
        setTimeout(() => {
          this.syncExtraRoomDropdownChoices(options.question as SurveyQuestion);
          this.cdr.detectChanges();
        }, 0);
      });

      // Participate form: self_participate is hidden until add_others=true, so defaultValue is not applied by SurveyJS.
      this.surveyModel.onValueChanged.add((sender, options) => {
        if (options.name !== 'add_others' || options.value !== true) {
          return;
        }
        if (
          this.originalSurveyJSON?.dataSource !== 'event_participate_form_data'
        ) {
          return;
        }
        const current = sender.data?.['self_participate'];
        if (current === undefined || current === null || current === '') {
          sender.setValue('self_participate', 'true');
        }
        setTimeout(() => {
          const dualPanel = sender.getQuestionByName('add_event_users');
          if (!dualPanel || dualPanel.getType() !== 'dualpanel') {
            return;
          }
          delete (dualPanel as { __dualPanelInitialized?: boolean })
            .__dualPanelInitialized;
          const runtime = (dualPanel as { __dualPanelRuntime?: { handlersReady?: boolean; rendering?: boolean } })
            .__dualPanelRuntime;
          if (runtime) {
            runtime.handlersReady = false;
            runtime.rendering = false;
          }
          (dualPanel as { render?: () => void }).render?.();
          this.cdr.detectChanges();
        }, 0);
      });

      this.surveyModel.onValueChanged.add((sender, options) => {
        this.applyCoursePricingLogic(sender as SurveyModel, options);
      });

      this.surveyModel.onValueChanged.add((sender, options) => {
        if (
          options.name !== 'customer_guid' &&
          options.name !== 'course_price_type'
        ) {
          return;
        }
        setTimeout(() => {
          void this.refreshCustomerStamdataDisplay(sender as SurveyModel);
        }, 0);
      });

      this.surveyModel.onValueChanged.add((sender, options) => {
        this.handleGuidedScheduleValueChange(sender as SurveyModel, options);
      });

      this.surveyModel.onCurrentPageChanging.add((sender, options) => {
        if (!options.isGoingForward) {
          return;
        }
        const data = (sender.data || {}) as Record<string, unknown>;
        if (
          data['schedule_mode'] === 'multi_dates' &&
          !hasSeriesPlannerDates(data)
        ) {
          const message =
            'Vælg mindst én dato i serieplanlæggeren, før du går videre.';
          options.allow = false;
          options.message = message;
          this.showFormError(message);
        }
      });

      // Add survey-wide handler for datetime values
      this.surveyModel.onValueChanged.add((sender, options) => {
        if (
          options.question &&
          options.question.getType() === 'text' &&
          options.question['inputType'] === 'datetime-local' &&
          options.value &&
          typeof options.value === 'string' &&
          options.value.endsWith('Z')
        ) {

          // CRITICAL FIX: Only apply timezone conversion for API data, not user input
          // Check if this is a user input (from datetime-local picker) or API data
          const isUserInput =
            !options.value.endsWith('Z') && !this.isFromApiLoad;

          if (isUserInput) {
            // For user inputs, don't apply any timezone conversion
            return;
          }

          const localDateTimeValue = this.convertUTCToLocalDateTime(
            options.value
          );

          // Use setTimeout to avoid interference with SurveyJS internal logic
          setTimeout(() => {
            options.question.value = localDateTimeValue;
          }, 0);
        }
      });

      // Apply the panelless theme
      this.surveyModel.applyTheme(DefaultLightPanelless);

      // CRITICAL: Apply navigationButtonsLocation immediately after theme (themes can affect navigation rendering)
      if (this.navigationButtonsLocationFromJSON) {
        (this.surveyModel as any).navigationButtonsLocation =
          this.navigationButtonsLocationFromJSON;
      }

      // Set the model first so the form renders

      // Add a targeted approach to handle empty forms - using existing method
      // The handler is already added in the onAfterRenderSurvey event above

      // Load form data from data source if specified
      await this.loadFormData();

      if (this.originalSurveyJSON) {
        this.applyDefaultValues(this.originalSurveyJSON);
      }

      this.applyEventSeriesPlannerSurveyChrome(this.surveyModel);

      // Apply visibility rules based on authVisibility (and visibleIf, e.g. catering page)
      this.updatePageVisibility(this.surveyModel);
      // Set current page to first visible *before* revealing the form so the user never sees
      // the wrong page or a missing "Videre" button (e.g. when event has catering, page2 becomes visible).
      this.syncSurveyNavigationModel();
      this.ensureCurrentPageIsFirstVisible();
      this.checkAndShowSubmitButton(this.surveyModel);
      this.ensureNativeCompleteButtonVisible();
      this.forceUpdateNavigationButtons();
      // Reveal form only after data and correct page are set (avoids flash of wrong page)
      if (this.shouldDeferRevealUntilFormDataLoaded() && !this.surveyReady) {
        this.revealSurvey();
      }

      // First, just log the current state
      this.surveyModel.pages.forEach((page) => {
        const authVisibility = page.getPropertyValue('authVisibility');
      });

      // Apply visibility rules again before deferred force-check
      this.updatePageVisibility(this.surveyModel);

      this.surveyModel.pages.forEach((page) => {
        const authVisibility = page.getPropertyValue('authVisibility');
      });

      // Add one final force check for page visibility - this is critical

      // Force a re-evaluation of all pages' visibility
      setTimeout(() => {

        // First apply normal visibility rules
        this.updatePageVisibility(this.surveyModel);

        // Extra check: re-apply stored visibility only for template-defined visibilityDataSource pages; then authVisibility for others
        const jsonPagesExtra = (this.originalSurveyJSON?.pages || []) as Array<
          Record<string, unknown>
        >;
        const templatePageNamesWithDsExtra = new Set<string>();
        jsonPagesExtra.forEach((p: Record<string, unknown>) => {
          if (
            typeof p['visibilityDataSource'] === 'string' &&
            typeof p['name'] === 'string'
          ) {
            templatePageNamesWithDsExtra.add(p['name'] as string);
          }
        });
        this.surveyModel.pages.forEach((page) => {
          const fromMap = this.visibilityFromDataSource.get(
            `page:${page.name}`
          );
          const fromPage =
            (page as any).getPropertyValue?.('_visibilityDsResult') ??
            (page as any)._visibilityDsResult;
          const stored =
            fromMap !== undefined
              ? fromMap
              : typeof fromPage === 'boolean'
              ? fromPage
              : undefined;
          if (stored !== undefined) {
            const visibleToApply =
              stored && this.pageAuthVisibilityAllows(page);
            const visibleIfValue = this.pageVisibleIfAfterDataSource(
              page.name,
              visibleToApply
            );
            if (typeof (page as any).setPropertyValue === 'function') {
              (page as any).setPropertyValue('visibleIf', visibleIfValue);
              (page as any).setPropertyValue('visible', visibleToApply);
            } else {
              (page as any).visibleIf = visibleIfValue;
              page.visible = visibleToApply;
            }
            return;
          }
          // Page has visibilityDataSource but no stored value yet. Do NOT set visible = false here
          // while waiting for the API, except when auth already forbids this user.
          if (templatePageNamesWithDsExtra.has(page.name)) {
            if (!this.pageAuthVisibilityAllows(page)) {
              const visibleIfValue =
                DynamicFormComponent.VISIBLEIF_ALWAYS_FALSE;
              if (typeof (page as any).setPropertyValue === 'function') {
                (page as any).setPropertyValue('visibleIf', visibleIfValue);
                (page as any).setPropertyValue('visible', false);
              } else {
                (page as any).visibleIf = visibleIfValue;
                page.visible = false;
              }
            }
            return;
          }

          const authVisibility = page.getPropertyValue('authVisibility');
          if (authVisibility === 1) {
            // Only logged in
            const shouldBeVisible = this.isAuthenticated;
            page.visible = shouldBeVisible;
          } else if (authVisibility === 2) {
            // Only NOT logged in
            const shouldBeVisible = !this.isAuthenticated;
            page.visible = shouldBeVisible;
          } else if (authVisibility === 3) {
            // Nobody - never show (e.g. Godkend betingelser)
            page.visible = false;
          }
        });

        // Re-apply stored visibility so visibilityDataSource results always win (fixes refresh race where
        // FORCE-CHECK could run before or overwrite loadFormData visibility)
        this.updatePageVisibility(this.surveyModel);

        // Check for submit button after visibility changes
        this.checkAndShowSubmitButton(this.surveyModel);

        // Force SurveyJS to fully re-evaluate everything by applying more aggressive changes
        try {

          // Get the visible pages after our changes
          const visiblePages = this.surveyModel.visiblePages;

          if (visiblePages.length > 0) {
            this.noVisiblePages = false;
            this.noVisiblePagesHint = null;
            // Determine whether we need to move the user to visiblePages[0].
            // We navigate when:
            //  (a) The current page is no longer visible (was hidden by an authVisibility rule), OR
            //  (b) New pages appeared *before* the current page in the visible order — meaning the
            //      user has skipped past pages they haven't filled in yet.  This commonly happens
            //      when the data-source loads and makes a previously-empty page (e.g. forplejning /
            //      page2) visible: without this check the user would stay on a later page while a
            //      required earlier page goes unfilled.
            const currentPageStillVisible = visiblePages.includes(
              this.surveyModel.currentPage
            );
            const currentPageIndexInVisible = currentPageStillVisible
              ? visiblePages.indexOf(this.surveyModel.currentPage)
              : -1;
            // Only jump back when the current page is invisible — not when the user has moved forward.
            const shouldNavigateToFirst = !currentPageStillVisible;

            if (shouldNavigateToFirst) {
              try {
                // Try to set the current page by index first
                const firstVisibleIndex = this.surveyModel.pages.indexOf(
                  visiblePages[0]
                );
                if (firstVisibleIndex >= 0) {
                  this.surveyModel.currentPageNo = firstVisibleIndex;
                }

                // Also try to set directly by reference
                this.surveyModel.currentPage = visiblePages[0];
              } catch (error) {
                //console.error('[FORCE-CHECK] Error setting current page:', error);
              }
            } else {
            }

            // Force the model to process any changes
            setTimeout(() => {

              // Apply the same logic: navigate to first visible page if the current page is
              // no longer visible or if pages appeared before it.
              const latestVisiblePages = this.surveyModel.visiblePages;
              const latestCurrentStillVisible = latestVisiblePages.includes(
                this.surveyModel.currentPage
              );
              const latestCurrentIndex = latestCurrentStillVisible
                ? latestVisiblePages.indexOf(this.surveyModel.currentPage)
                : -1;
              if (!latestCurrentStillVisible) {
                this.surveyModel.currentPageNo = this.surveyModel.pages.indexOf(
                  latestVisiblePages[0]
                );
              }

              // Final check for submit button
              this.checkAndShowSubmitButton(this.surveyModel);
            }, 100);
          } else {
            // No visible pages: do not fall back to forcing a page visible (e.g. betingelser with authVisibility "Nobody").
            // Show "no options" message instead so the user cannot use the form.
            this.noVisiblePages = true;
            this.cdr.detectChanges();
          }
        } catch (error) {
          //console.error('[FORCE-CHECK] Error during aggressive fix:', error);
        }

        // Log the final state
        this.surveyModel.pages.forEach((page, index) => {
        });

        // Reveal the survey now that data is loaded, visibility rules are applied, and the
        // correct first page is set.  Keeping it hidden until this point prevents any flash
        // of the wrong page or submit button during async initialisation.
        this.revealSurvey();

        // On refresh, visibilityDataSource can complete after the FORCE-CHECK; re-apply stored
        // visibility once more so cancel/visibility pages show correctly (e.g. event_allow_cancel_24h).
        setTimeout(() => {
          if (this.surveyModel && this.visibilityFromDataSource.size > 0) {
            this.updatePageVisibility(this.surveyModel);
            this.cdr.detectChanges();
          }
        }, 600);
      }, 500);

      // Final verification of data source loading
      const questionsWithDataSources = this.surveyModel
        .getAllQuestions()
        .filter((q) => {
          const dataSource =
            q['dataSource'] ||
            q.getPropertyValue('dataSource') ||
            q['jsonObj']?.dataSource;
          return !!dataSource;
        });

      for (const q of questionsWithDataSources) {
        const hasChoices = q['choices'] && q['choices'].length > 0;

        // If no choices, try to load one more time (skip dualpanel — it loads its own data)
        if (
          !hasChoices &&
          !['dualpanel', 'eventseriesplanner', 'eventmaterialplanner'].includes(q.getType())
        ) {
          await this.loadQuestionData(q);
        }
      }

      // When no pages are visible after init, show "no options" message (avoids "Bekræft" with no page on refresh)
      setTimeout(() => {
        if (!this.surveyModel) return;
        const vp = this.surveyModel.visiblePages;
        if (this.surveyModel.pages.length > 0 && vp.length === 0) {
          this.noVisiblePages = true;
        } else {
          this.noVisiblePages = false;
          this.noVisiblePagesHint = null;
          if (
            vp.length > 0 &&
            (!this.surveyModel.currentPage ||
              !vp.includes(this.surveyModel.currentPage))
          ) {
            this.surveyModel.currentPage = vp[0];
            const idx = this.surveyModel.pages.indexOf(vp[0]);
            if (idx >= 0) {
              this.surveyModel.currentPageNo = idx;
            }
          }
        }
        this.cdr.detectChanges();
        this.checkAndShowSubmitButton(this.surveyModel);
      }, 500);

      // No need for safety checks since we're forcing all pages to be visible
    } catch (error) {
      console.error('Error initializing survey:', error);
      // Ensure the form is always revealed even if initialisation fails
      this.revealSurvey();
    }
  }

  ngAfterViewInit(): void {
    // Additional setup after view initialization
    // Hide submit button if requested via settings
    if ((this as any).hideSubmitButtonForThisInstance) {
      const hideNow = () => this.hideSurveySubmitButtonsOnly();
      setTimeout(hideNow, 0);
      setTimeout(hideNow, 250);
      setTimeout(hideNow, 1000);
    }

    // On container resize (e.g. window smaller then back to bigger), SurveyJS can leave
    // the form with squished layout or wrong responsive classes. Refresh layout and nav.
    const host = this.el?.nativeElement;
    if (host && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) this.resizeSubject.next(entry);
      });
      this.resizeObserver.observe(host);
      this.resizeSubject
        .pipe(takeUntil(this.destroy$), debounceTime(200))
        .subscribe((entry) => this.onResizeForLayout(entry));
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearDataSourceCaches();

    // Clear any remaining timeouts
    this.searchTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.searchTimeouts.clear();

    // Disconnect navigation buttons observer
    if (this.navigationButtonsObserver) {
      this.navigationButtonsObserver.disconnect();
      this.navigationButtonsObserver = null;
    }

    if (this.participateUsersDotsObserver) {
      this.participateUsersDotsObserver.disconnect();
      this.participateUsersDotsObserver = null;
      this.participateUsersDotsObserverRoot = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.resizeSubject.complete();
    if (this.resizeRestoreIntervalId) {
      clearInterval(this.resizeRestoreIntervalId);
      this.resizeRestoreIntervalId = null;
    }
    if (this.keepNavVisibleIntervalId) {
      clearInterval(this.keepNavVisibleIntervalId);
      this.keepNavVisibleIntervalId = null;
    }
    this.paneldynamicObserver?.disconnect();
    this.paneldynamicObserver = null;
    this.datetimeObserver?.disconnect();
    this.datetimeObserver = null;

    // Dynamic table components are now cleaned up by the widget itself

    // Removed interval cleanup as we removed the aggressive periodic checks
  }

  /**
   * Ensures that an empty page exists when there are no visible pages
   * This gives SurveyJS the structure it needs to render the form container
   */
  private ensureEmptyPageExists(): void {

    try {
      const visiblePages = this.surveyModel.visiblePages || [];

      if (visiblePages.length === 0) {

        // Create a minimal empty page
        const emptyPageJson = {
          name: 'emptySubmitPage',
          title: '',
          elements: [],
        };

        // Add the page to the survey
        const newPage = new PageModel(JSON.stringify(emptyPageJson));
        this.surveyModel.addPage(newPage);

        // Make sure the page is visible
        const addedPage = this.surveyModel.getPageByName('emptySubmitPage');
        if (addedPage) {
          addedPage.visible = true;
          this.surveyModel.currentPage = addedPage;
        }
      } else {
      }
    } catch (error) {
      console.error('[EMPTY-FORM] Error creating empty page:', error);
    }
  }

  /**
   * Ensures the survey's current page is the first visible page (by visibility rules and auth).
   * Call after loading form data so the user is never on an invisible page and the "Videre" button shows.
   */
  private ensureCurrentPageIsFirstVisible(): void {
    if (!this.surveyModel) return;
    const visiblePages = this.surveyModel.visiblePages;
    if (visiblePages.length === 0) return;
    const currentPageStillVisible = visiblePages.includes(
      this.surveyModel.currentPage
    );
    const currentPageIndexInVisible = currentPageStillVisible
      ? visiblePages.indexOf(this.surveyModel.currentPage)
      : -1;
    const shouldNavigateToFirst =
      !currentPageStillVisible || currentPageIndexInVisible > 0;
    if (shouldNavigateToFirst) {
      try {
        const firstVisibleIndex = this.surveyModel.pages.indexOf(
          visiblePages[0]
        );
        if (firstVisibleIndex >= 0) {
          this.surveyModel.currentPageNo = firstVisibleIndex;
        }
        this.surveyModel.currentPage = visiblePages[0];
      } catch {}
    }
  }

  /**
   * Defaults for add-users-to-event form (waitlist visibility and enrollment default).
   */
  private applyAddUsersToEventEnrollmentDefaults(sender: any): void {
    if (!sender?.getQuestionByName) {
      return;
    }
    if (!sender.getQuestionByName('dynamic_table_users')) {
      return;
    }
    if (!sender.getQuestionByName('enrollment_status')) {
      return;
    }

    const obs = this.componentDataSourceService.executeDataSource('event', {});
    if (!obs) {
      return;
    }

    obs.subscribe({
      next: (event: { wait_list_type?: string }) => {
        const waitlistEnabled = event?.wait_list_type !== 'no_wait_list';
        const enrollmentQuestion = sender.getQuestionByName('enrollment_status');
        const baseChoices = [
          { value: 'participant', text: 'Deltager' },
          { value: 'instructor', text: 'Instruktør' },
        ];
        const choices = waitlistEnabled
          ? [
              baseChoices[0],
              { value: 'waitlist', text: 'Venteliste' },
              baseChoices[1],
            ]
          : baseChoices;
        try {
          sender.setValue('event_waitlist_enabled', waitlistEnabled);
          if (enrollmentQuestion) {
            enrollmentQuestion.choices = choices;
          }
          const current = sender.getValue('enrollment_status');
          if (!waitlistEnabled && current === 'waitlist') {
            sender.setValue('enrollment_status', 'participant');
          } else if (current !== 'instructor') {
            if (event?.wait_list_type === 'everyone_on_wait_list') {
              sender.setValue('enrollment_status', 'waitlist');
            } else if (waitlistEnabled) {
              sender.setValue('enrollment_status', 'participant');
            }
          }
        } catch {
          // ignore survey timing issues
        }
      },
      error: () => {},
    });
  }

  /**
   * Checks if submit button should be shown and shows it if needed
   */
  private checkAndShowSubmitButton(sender: any): void {
    // Do not attempt to add or reveal submit UI when submit must be hidden
    if ((this as any).hideSubmitButtonForThisInstance) {
      return;
    }

    // First, check if showEmptyFormSubmit is disabled - if so, hide any existing custom buttons and return early
    // Use stored original JSON object if available, otherwise parse from input
    const jsonObj =
      this.originalSurveyJSON ||
      (() => {
        const surveyJSON = this.getInput('surveyJSON');
        return surveyJSON
          ? typeof surveyJSON === 'string'
            ? JSON.parse(surveyJSON)
            : surveyJSON
          : {};
      })();
    const showEmptyFormSubmit = jsonObj.showEmptyFormSubmit;
    const modelShowEmptyFormSubmit = this.surveyModel.getPropertyValue(
      'showEmptyFormSubmit'
    );
    const jsonValue =
      showEmptyFormSubmit !== undefined
        ? showEmptyFormSubmit
        : modelShowEmptyFormSubmit;

    if (jsonValue === false) {
      const customSubmitButtons = document.querySelectorAll('.form-submit-btn');
      customSubmitButtons.forEach((btn) => {
        (btn as HTMLElement).style.display = 'none';
      });
      const submitContainer = document.querySelector('.form-submit-container');
      if (submitContainer) {
        (submitContainer as HTMLElement).style.display = 'none';
      }
      this.ensureNativeCompleteButtonVisible();
      return;
    }

    // Don't show submit button on the completion/thank you page
    if (sender.isCompleted || (sender as any).state === 'completed') {
      this.hideSubmitButtonsOnCompletion();
      return;
    }

    // Check if we're on the completion page by looking for completion page elements
    const completionPage = document.querySelector(
      '.sv-completedpage, .sv-body__page--completed'
    );
    if (completionPage) {
      this.hideSubmitButtonsOnCompletion();
      return;
    }

    const currentPage = sender.currentPage;
    const visiblePages = sender.visiblePages || [];

    let shouldShowSubmitButton = false;
    let isLastPage = false;

    // Check if this is the last visible page
    if (visiblePages.length > 0 && currentPage) {
      const currentPageIndex = visiblePages.indexOf(currentPage);
      isLastPage = currentPageIndex === visiblePages.length - 1;
    }

    // Check if form is actually empty (no visible questions across all pages)
    let totalVisibleQuestions = 0;
    if (visiblePages.length > 0) {
      visiblePages.forEach((page: any) => {
        if (page.visible) {
          const pageQuestions = page.questions || [];
          totalVisibleQuestions += pageQuestions.filter(
            (q: any) => q.visible
          ).length;
        }
      });
    }

    const isFormEmpty = totalVisibleQuestions === 0;

    // Check if there are any pages at all (not just visible ones)
    // If pages exist but are hidden due to authVisibility, don't treat as empty form
    const allPages = sender.pages || [];
    const hasAnyPages = allPages.length > 0;

    if (!hasAnyPages) {
      shouldShowSubmitButton = true;
    } else if (!currentPage || visiblePages.length === 0) {
      // Pages exist but none are visible - this could be due to authVisibility
      // Only treat as empty if we're sure there are no questions at all
      // Don't automatically show submit button - let the totalVisibleQuestions check decide
    } else if (currentPage && currentPage.visible) {
      const visibleQuestions = currentPage.questions.filter(
        (q: any) => q.visible
      );
      if (visibleQuestions.length === 0) {
        shouldShowSubmitButton = true;
      }
      // Don't set shouldShowSubmitButton for last page with visible questions
      // Only show custom button if form is actually empty
    }

    // Check for the "no visible questions" message
    const stringViewers = document.querySelectorAll('.sv-string-viewer');
    let foundEmptyMessage = false;
    stringViewers.forEach((viewer) => {
      const text = viewer.textContent || '';
      if (text.includes('synlige spørgsmål') || text.includes('Der er ingen')) {
        (viewer as HTMLElement).style.display = 'none';
        foundEmptyMessage = true;
        shouldShowSubmitButton = true;
      }
    });

    // Check if SurveyJS's native submit button exists and is visible
    const nativeSubmitButton = document.querySelector(
      '.sv_complete_btn, button[type="submit"]'
    );
    const nativeButtonExistsAndVisible =
      nativeSubmitButton &&
      (nativeSubmitButton as HTMLElement).offsetParent !== null;

    if (nativeSubmitButton && !nativeButtonExistsAndVisible) {
      (nativeSubmitButton as HTMLElement).style.display = 'inline-block';
      (nativeSubmitButton as HTMLElement).style.visibility = 'visible';
    }

    // Show custom submit button whenever feature is enabled and native button isn't visible.
    // We no longer require the form to be empty; if showEmptyFormSubmit is true, always ensure a button is available.
    const shouldShowCustomButton = !nativeButtonExistsAndVisible;

    if (shouldShowCustomButton) {
      // Check if the feature is enabled from JSON (read directly from JSON, not from survey model)
      // Use stored original JSON object if available, otherwise parse from input
      const jsonObj =
        this.originalSurveyJSON ||
        (() => {
          const surveyJSON = this.getInput('surveyJSON');
          return surveyJSON
            ? typeof surveyJSON === 'string'
              ? JSON.parse(surveyJSON)
              : surveyJSON
            : {};
        })();
      const showEmptyFormSubmit = jsonObj.showEmptyFormSubmit;

      // Also check the survey model property as fallback (in case it was set via settings)
      const modelShowEmptyFormSubmit = this.surveyModel.getPropertyValue(
        'showEmptyFormSubmit'
      );

      // Determine if we should show:
      // - If JSON has showEmptyFormSubmit set to false, don't show
      // - If JSON has showEmptyFormSubmit set to true or undefined, check model
      // - If model has it set to false, don't show
      // - Default to true if neither is explicitly false
      const jsonValue =
        showEmptyFormSubmit !== undefined
          ? showEmptyFormSubmit
          : modelShowEmptyFormSubmit;
      const shouldShow = jsonValue !== false; // Only false if explicitly set to false

      if (shouldShow) {
        this.ensureSubmitButtonVisible();
      } else {
      }
    }
  }

  /**
   * Ensures the submit button is visible even when there are no visible questions
   */
  private ensureSubmitButtonVisible(): void {
    // If this instance requires hiding submit, bail out early
    if ((this as any).hideSubmitButtonForThisInstance) {
      return;
    }

    // Check if showEmptyFormSubmit is disabled in JSON
    // Use stored original JSON object if available, otherwise parse from input
    const jsonObj =
      this.originalSurveyJSON ||
      (() => {
        const surveyJSON = this.getInput('surveyJSON');
        return surveyJSON
          ? typeof surveyJSON === 'string'
            ? JSON.parse(surveyJSON)
            : surveyJSON
          : {};
      })();
    const showEmptyFormSubmit = jsonObj.showEmptyFormSubmit;
    const modelShowEmptyFormSubmit = this.surveyModel.getPropertyValue(
      'showEmptyFormSubmit'
    );
    const jsonValue =
      showEmptyFormSubmit !== undefined
        ? showEmptyFormSubmit
        : modelShowEmptyFormSubmit;

    if (jsonValue === false) {
      const customSubmitButtons = document.querySelectorAll('.form-submit-btn');
      customSubmitButtons.forEach((btn) => {
        (btn as HTMLElement).style.display = 'none';
      });
      const submitContainer = document.querySelector('.form-submit-container');
      if (submitContainer) {
        (submitContainer as HTMLElement).style.display = 'none';
      }
      this.ensureNativeCompleteButtonVisible();
      return;
    }

    // Don't show submit button on the completion/thank you page
    if (
      (this.surveyModel as any).isCompleted ||
      (this.surveyModel as any).state === 'completed'
    ) {
      return;
    }

    // Check if we're on the completion page by looking for completion page elements
    const completionPage = document.querySelector(
      '.sv-completedpage, .sv-body__page--completed'
    );
    if (completionPage) {
      return;
    }

    try {
      // Force SurveyJS to render the complete button by ensuring the survey knows it can be completed
      // This is especially important for forms with only custom questions
      (this.surveyModel as any).showCompleteButton = true;
      (this.surveyModel as any).showPreviewButton = false;

      // Try to find and show the native SurveyJS submit button first
      // Use a more comprehensive selector and scope to the component
      const hostEl = this.el?.nativeElement as HTMLElement | null;

      // CRITICAL: Find the modal context - footer might be outside modal-body
      const modalContent = hostEl?.closest(
        '.modal-content'
      ) as HTMLElement | null;
      const modalBody = hostEl?.closest('.modal-body') as HTMLElement | null;

      // CRITICAL: First, log the entire SurveyJS DOM structure to understand what's actually rendered
      if (hostEl) {
        const surveyRoot = hostEl.querySelector(
          '.sd-root, .sv-root, .sd-root-modern, .sv-root-modern'
        ) as HTMLElement | null;
        if (surveyRoot) {

          // Check all elements in the root for any buttons
          const allElements = surveyRoot.querySelectorAll('*');
          const buttons = Array.from(allElements).filter(
            (el) => el.tagName === 'BUTTON'
          );
        } else {
          console.warn('[EMPTY-FORM] No SurveyJS root found in host element!');
        }
      }

      // Early check: if custom button already exists and is visible, we should still check for native button first
      // Only skip if we've already confirmed there's no native button
      const existingCustomButton = hostEl?.querySelector(
        '.form-submit-btn'
      ) as HTMLElement | null;
      if (existingCustomButton && existingCustomButton.offsetParent !== null) {
        // Don't return early - continue to search for native button
      }

      // Ensure the navigation container is visible (where SurveyJS places the submit button)
      const navContainerElements = document.querySelectorAll(
        '.sv-action-bar, .sv-footer, .sv-nav-btn-container, .sd-footer__complete, .sd-footer__navigation'
      );
      navContainerElements.forEach((container) => {
        (container as HTMLElement).style.display = 'block';
        (container as HTMLElement).style.visibility = 'visible';
      });

      // Check if SurveyJS has rendered any footer/action bar elements at all
      // For forms with only custom questions, SurveyJS might not render the footer
      // Search in hostEl first, then broader if not found
      let footerElement = hostEl?.querySelector(
        '.sv-footer, .sv-action-bar, .sd-footer, .sd-action-bar'
      ) as HTMLElement | null;

      // If not found in hostEl, search more broadly
      if (!footerElement) {
        footerElement = document.querySelector(
          'app-dynamic-form .sv-footer, app-dynamic-form .sv-action-bar, app-dynamic-form .sd-footer, app-dynamic-form .sd-action-bar'
        ) as HTMLElement | null;
      }

      // Also check at the SurveyJS root level (footer might be sibling to body, not child)
      if (!footerElement) {
        const surveyRoot = hostEl?.querySelector(
          '.sd-root, .sv-root, .sd-root-modern, .sv-root-modern'
        ) as HTMLElement | null;
        if (surveyRoot) {
          footerElement = surveyRoot.querySelector(
            '.sv-footer, .sv-action-bar, .sd-footer, .sd-action-bar'
          ) as HTMLElement | null;

          // If still not found, check siblings of the body
          if (!footerElement) {
            const bodyElement = surveyRoot.querySelector('.sd-body, .sv-body');
            if (bodyElement && bodyElement.nextElementSibling) {
              const sibling = bodyElement.nextElementSibling as HTMLElement;
              if (
                sibling.classList.contains('sv-footer') ||
                sibling.classList.contains('sv-action-bar') ||
                sibling.classList.contains('sd-footer') ||
                sibling.classList.contains('sd-action-bar')
              ) {
                footerElement = sibling;
              }
            }
          }
        }
      }

      // CRITICAL: Search within modal context (footer might be in modal-content but outside modal-body)
      if (!footerElement && modalContent) {
        footerElement = modalContent.querySelector(
          '.sv-footer, .sv-action-bar, .sd-footer, .sd-action-bar'
        ) as HTMLElement | null;
        if (footerElement) {
        }
      }

      // Comprehensive search - check entire document for SurveyJS footer within modal or component
      if (!footerElement) {
        const allFooters = document.querySelectorAll(
          '.sv-footer, .sv-action-bar, .sd-footer, .sd-action-bar'
        );
        for (const footer of Array.from(allFooters)) {
          const footerEl = footer as HTMLElement;
          // Check if it's within our component OR within the modal
          const isInComponent =
            hostEl?.contains(footerEl) || footerEl.closest('app-dynamic-form');
          const isInModal =
            modalContent?.contains(footerEl) ||
            footerEl.closest('app-dynamic-modal');

          if (isInComponent || isInModal) {
            footerElement = footerEl;
            break;
          }
        }
      }

      const hasFooter = footerElement !== null;
      const allQuestions = this.surveyModel.getAllQuestions();
      const hasStandardQuestions = allQuestions.some(
        (q) =>
          !['dynamictable', 'dualpanel', 'eventseriesplanner', 'eventmaterialplanner'].includes(
            q.getType()
          )
      );

      // CRITICAL: Force footer to be visible if it exists
      if (footerElement) {
        // Force visibility on footer and all parent containers
        footerElement.style.display = 'block';
        footerElement.style.visibility = 'visible';
        footerElement.style.opacity = '1';

        // CRITICAL: If footer is inside modal-body, ensure it's not clipped
        // The modal-body has overflow-y: auto which might clip the footer
        if (modalBody && modalBody.contains(footerElement)) {
          // Ensure modal-body allows footer to be visible
          modalBody.style.paddingBottom = '80px'; // Add space for footer
          // Ensure footer is positioned correctly
          footerElement.style.position = 'relative';
          footerElement.style.zIndex = '10';
        }

        // If footer is in modal-content but outside modal-body, ensure modal-content shows it
        if (
          modalContent &&
          modalContent.contains(footerElement) &&
          (!modalBody || !modalBody.contains(footerElement))
        ) {
          // Ensure modal-content shows the footer
          footerElement.style.position = 'relative';
          footerElement.style.zIndex = '10';
          footerElement.style.marginTop = '16px';
        }

        // Also ensure parent containers are visible
        let parent = footerElement.parentElement;
        while (parent && parent !== hostEl && parent !== document.body) {
          const computedStyle = window.getComputedStyle(parent);
          if (
            computedStyle.display === 'none' ||
            computedStyle.visibility === 'hidden'
          ) {
            parent.style.display = 'block';
            parent.style.visibility = 'visible';
          }
          // If parent is modal-body, ensure it doesn't clip the footer
          if (parent === modalBody) {
            const parentStyle = window.getComputedStyle(parent);
            if (
              parentStyle.overflowY === 'auto' ||
              parentStyle.overflowY === 'scroll'
            ) {
              // Add padding to ensure footer is visible
              (parent as HTMLElement).style.paddingBottom = '80px';
            }
          }
          parent = parent.parentElement;
        }

      }

      // If footer exists, check inside it for buttons
      let buttonsInFooter: NodeListOf<HTMLButtonElement> | null = null;
      if (footerElement) {
        buttonsInFooter = footerElement.querySelectorAll('button');
      }

      // Try to force SurveyJS to render the footer by ensuring navigation is enabled
      if (!hasFooter) {

        // Ensure navigation buttons are enabled (they should be, but double-check)
        this.surveyModel.showNavigationButtons = true;
        (this.surveyModel as any).showCompleteButton = true;
        (this.surveyModel as any).showPreviewButton = false;
        (this.surveyModel as any).showCompletedPage = true;

        // CRITICAL: Check if SurveyJS has internal methods to render footer
        // SurveyJS v2+ might have different API
        try {
          // Try to access SurveyJS's renderFooter or similar method
          const surveyModelAny = this.surveyModel as any;
          if (typeof surveyModelAny.renderFooter === 'function') {
            surveyModelAny.renderFooter();
          }

          // Try to trigger a full re-render
          if (typeof surveyModelAny.render === 'function') {
            surveyModelAny.render();
            setTimeout(() => {
              if (this.surveyModel) this.updatePageVisibility(this.surveyModel);
            }, 0);
          }

          // Try to access the renderer and force footer render
          if (
            surveyModelAny.renderer &&
            typeof surveyModelAny.renderer.renderFooter === 'function'
          ) {
            surveyModelAny.renderer.renderFooter();
          }
        } catch (error) {
          console.warn(
            '[EMPTY-FORM] Error calling SurveyJS render methods:',
            error
          );
        }

        // Force SurveyJS to re-render by triggering page change
        try {
          const currentPage = this.surveyModel.currentPage;
          if (currentPage) {
            // Force a re-render by setting the page again
            this.surveyModel.currentPage = currentPage;
          }

          // Also try to trigger afterRenderSurvey event
          setTimeout(() => {
            // Check again after a delay - SurveyJS might render footer asynchronously
            const delayedFooter = hostEl?.querySelector(
              '.sv-footer, .sv-action-bar, .sd-footer, .sd-action-bar'
            ) as HTMLElement | null;
            if (delayedFooter) {
              footerElement = delayedFooter;
            } else {
              console.warn(
                '[EMPTY-FORM] Footer still not found after delay - SurveyJS may not render footer for this form type'
              );
            }
          }, 500);
        } catch (error) {
          console.warn('[EMPTY-FORM] Error forcing footer render:', error);
        }
      }

      // Try to find and show the native SurveyJS submit button first
      // Use a more comprehensive selector and scope to the component AND modal
      // Also check inside footer elements specifically
      // IMPORTANT: Use querySelectorAll to find ALL buttons, even hidden ones
      let nativeSubmitButton = hostEl?.querySelector(
        '.sv_complete_btn, .sv-footer__complete-btn, .sd-footer__complete-btn, button[type="submit"], .sv-action-bar button, .sv-footer button, button.sv-btn'
      ) as HTMLElement | null;

      // Also search in modal context if not found in host
      if (!nativeSubmitButton && modalContent) {
        nativeSubmitButton = modalContent.querySelector(
          '.sv_complete_btn, .sv-footer__complete-btn, .sd-footer__complete-btn, button[type="submit"], .sv-action-bar button, .sv-footer button, button.sv-btn'
        ) as HTMLElement | null;
        if (nativeSubmitButton) {
        }
      }

      // If not found, check ALL buttons in footer (including hidden ones)
      if (!nativeSubmitButton && footerElement) {
        const allButtons = footerElement.querySelectorAll('button');

        // Look for the actual SurveyJS complete button (even if hidden)
        for (const btn of Array.from(allButtons)) {
          const btnEl = btn as HTMLElement;
          if (
            btnEl.classList.contains('sv_complete_btn') ||
            btnEl.classList.contains('sv-footer__complete-btn') ||
            btnEl.classList.contains('sd-footer__complete-btn') ||
            btnEl.getAttribute('type') === 'submit'
          ) {
            nativeSubmitButton = btnEl;
            break;
          }
        }

        // If still not found, use first button as fallback
        if (!nativeSubmitButton && allButtons.length > 0) {
          nativeSubmitButton = allButtons[0] as HTMLElement;
        }
      }

      // If not found in host, try broader search but still scoped (including hidden buttons)
      if (!nativeSubmitButton) {
        // Search in component first
        const componentButtons = Array.from(
          document.querySelectorAll(
            'app-dynamic-form .sv_complete_btn, app-dynamic-form .sv-footer__complete-btn, app-dynamic-form .sd-footer__complete-btn, app-dynamic-form button[type="submit"], app-dynamic-form .sv-action-bar button, app-dynamic-form .sv-footer button, app-dynamic-form button.sv-btn'
          )
        );

        // Also search in modal if available
        let allButtons = [...componentButtons];
        if (modalContent) {
          const modalButtons = Array.from(
            modalContent.querySelectorAll(
              '.sv_complete_btn, .sv-footer__complete-btn, .sd-footer__complete-btn, button[type="submit"], .sv-action-bar button, .sv-footer button, button.sv-btn'
            )
          );
          // Combine both sets, avoiding duplicates
          allButtons = [...new Set([...componentButtons, ...modalButtons])];
        }

        for (const btn of allButtons) {
          const btnEl = btn as HTMLElement;
          // Prefer buttons with SurveyJS complete button classes
          if (
            btnEl.classList.contains('sv_complete_btn') ||
            btnEl.classList.contains('sv-footer__complete-btn') ||
            btnEl.classList.contains('sd-footer__complete-btn')
          ) {
            nativeSubmitButton = btnEl;
            break;
          }
        }

        // If still not found, use first button
        if (!nativeSubmitButton && allButtons.length > 0) {
          nativeSubmitButton = allButtons[0] as HTMLElement;
        }
      }

      // Final check: Look for ANY button in the entire SurveyJS structure (even outside footer)
      if (!nativeSubmitButton) {
        const surveyRoot = hostEl?.querySelector(
          '.sd-root, .sv-root, .sd-root-modern, .sv-root-modern'
        ) as HTMLElement | null;
        if (surveyRoot) {
          const allButtonsInSurvey = surveyRoot.querySelectorAll('button');

          // Log ALL buttons with full details
          for (const btn of Array.from(allButtonsInSurvey)) {
            const btnEl = btn as HTMLElement;
            const btnText = btnEl.textContent?.trim() || '';
            const btnTextLower = btnText.toLowerCase();
            const btnClasses = btnEl.className || '';
            const btnType = btnEl.getAttribute('type');
            const btnAriaLabel = btnEl.getAttribute('aria-label') || '';

            // Comprehensive check for submit/complete buttons
            const isSubmitButton =
              btnType === 'submit' ||
              btnEl.classList.contains('sv_complete_btn') ||
              btnEl.classList.contains('sv-footer__complete-btn') ||
              btnEl.classList.contains('sd-footer__complete-btn') ||
              btnEl.classList.contains('sd_complete_btn') ||
              btnClasses.includes('complete-btn') ||
              btnClasses.includes('complete_btn') ||
              btnTextLower.includes('tilføj') ||
              btnTextLower.includes('send') ||
              btnTextLower.includes('submit') ||
              btnTextLower.includes('complete') ||
              btnAriaLabel.toLowerCase().includes('complete') ||
              btnAriaLabel.toLowerCase().includes('submit');

            if (isSubmitButton) {
              nativeSubmitButton = btnEl;
              break;
            }
          }

          // If still not found but we have buttons, check if any button has the completeText
          if (!nativeSubmitButton && allButtonsInSurvey.length > 0) {
            const completeText =
              this.surveyModel.completeText || 'Tilføj' || 'Send';
            const completeTextLower = completeText.toLowerCase();

            for (const btn of Array.from(allButtonsInSurvey)) {
              const btnEl = btn as HTMLElement;
              const btnText = btnEl.textContent?.trim().toLowerCase() || '';

              if (
                btnText === completeTextLower ||
                btnText.includes(completeTextLower)
              ) {
                nativeSubmitButton = btnEl;
                break;
              }
            }
          }
        }
      }

      // Debug: Log SurveyJS model state to understand why button might not be rendered
      if (!nativeSubmitButton) {

        // Check if SurveyJS root structure exists
        const surveyRoot = hostEl?.querySelector(
          '.sd-root, .sv-root, .sd-root-modern, .sv-root-modern'
        ) as HTMLElement | null;
        if (surveyRoot) {
        }
      }

      // If footer exists but has no buttons, force SurveyJS to render its native button
      if (
        !nativeSubmitButton &&
        footerElement &&
        buttonsInFooter &&
        buttonsInFooter.length === 0
      ) {

        // Force SurveyJS to render the complete button by triggering a re-render
        // First ensure all necessary properties are set
        this.surveyModel.showNavigationButtons = true;
        (this.surveyModel as any).showCompleteButton = true;
        (this.surveyModel as any).showPreviewButton = false;

        // Force footer to be visible
        footerElement.style.display = 'block';
        footerElement.style.visibility = 'visible';
        footerElement.style.opacity = '1';

        // Trigger SurveyJS to re-render the footer/action bar
        // Try multiple approaches to force SurveyJS to render the button
        try {
          // Method 1: Force current page change to trigger re-render
          const currentPage = this.surveyModel.currentPage;
          if (currentPage) {
            this.surveyModel.currentPage = currentPage;
          }

          // Method 2: Call render method if available
          if (typeof (this.surveyModel as any).render === 'function') {
            (this.surveyModel as any).render();
            setTimeout(() => {
              if (this.surveyModel) this.updatePageVisibility(this.surveyModel);
            }, 0);
          }
        } catch (error) {
          console.warn('[EMPTY-FORM] Error forcing SurveyJS re-render:', error);
        }

        // Wait a bit and check again for the native button
        setTimeout(() => {
          const nativeBtn = hostEl?.querySelector(
            '.sv_complete_btn, .sv-footer__complete-btn, .sd-footer__complete-btn, button[type="submit"], .sv-action-bar button, .sv-footer button, button.sv-btn'
          ) as HTMLElement | null;

          if (nativeBtn) {
            nativeSubmitButton = nativeBtn;
          } else {
          }
        }, 100);
      }

      // If still not found, wait a bit for SurveyJS to render (especially for custom-only forms)
      if (!nativeSubmitButton) {
        setTimeout(() => {
          const delayedButton = hostEl?.querySelector(
            '.sv_complete_btn, .sv-footer__complete-btn, .sd-footer__complete-btn, button[type="submit"], .sv-action-bar button, .sv-footer button'
          ) as HTMLElement | null;

          if (delayedButton && delayedButton.offsetParent !== null) {
            const customSubmitButtons =
              document.querySelectorAll('.form-submit-btn');
            customSubmitButtons.forEach((btn) => {
              (btn as HTMLElement).style.display = 'none';
            });
            const submitContainer = document.querySelector(
              '.form-submit-container'
            );
            if (submitContainer) {
              (submitContainer as HTMLElement).style.display = 'none';
            }
          }
        }, 200);
      }

      // Check if native button is visible
      // For newly created buttons, they should be visible, but offsetParent might not update immediately
      let nativeButtonExistsAndVisible = false;
      if (nativeSubmitButton) {
        // CRITICAL: Ensure footer is visible before checking button visibility
        if (footerElement) {
          footerElement.style.display = 'block';
          footerElement.style.visibility = 'visible';
          footerElement.style.opacity = '1';
          footerElement.style.removeProperty('display');
          footerElement.style.removeProperty('visibility');
        }

        // Ensure button itself is visible
        nativeSubmitButton.style.display = 'inline-block';
        nativeSubmitButton.style.visibility = 'visible';
        nativeSubmitButton.style.opacity = '1';

        // Check if button is in the DOM and footer is visible
        const isInDOM = nativeSubmitButton.parentElement !== null;
        const footerVisible = footerElement
          ? footerElement.offsetParent !== null
          : true; // Default to true if no footer element
        const buttonHasStyles =
          nativeSubmitButton.style.display !== 'none' &&
          nativeSubmitButton.style.visibility !== 'hidden';
        const buttonOffsetParent = nativeSubmitButton.offsetParent !== null;

        // Button is visible if it has offsetParent OR (it's in DOM and footer is visible and has correct styles)
        nativeButtonExistsAndVisible =
          buttonOffsetParent || (isInDOM && footerVisible && buttonHasStyles);

      }

      // If we found a native button (even if hidden), make it visible and DON'T create a custom one
      if (nativeSubmitButton) {

        // CRITICAL: First ensure footer is visible
        if (footerElement) {
          footerElement.style.display = 'block';
          footerElement.style.visibility = 'visible';
          footerElement.style.opacity = '1';
          footerElement.style.removeProperty('display');
          footerElement.style.removeProperty('visibility');

          // Also ensure parent containers are visible
          let parent = footerElement.parentElement;
          while (parent && parent !== hostEl && parent !== document.body) {
            const computedStyle = window.getComputedStyle(parent);
            if (
              computedStyle.display === 'none' ||
              computedStyle.visibility === 'hidden'
            ) {
              parent.style.display = 'block';
              parent.style.visibility = 'visible';
            }
            parent = parent.parentElement;
          }
        }

        // Then ensure button is visible
        nativeSubmitButton.style.display = 'inline-block';
        nativeSubmitButton.style.visibility = 'visible';
        nativeSubmitButton.style.opacity = '1';
        nativeSubmitButton.style.removeProperty('display');
        nativeSubmitButton.style.removeProperty('visibility');

        // Hide any existing custom submit buttons since we're using the native one
        const customSubmitButtons =
          document.querySelectorAll('.form-submit-btn');
        customSubmitButtons.forEach((btn) => {
          (btn as HTMLElement).style.display = 'none';
        });
        const submitContainer = document.querySelector(
          '.form-submit-container'
        );
        if (submitContainer) {
          (submitContainer as HTMLElement).style.display = 'none';
        }

        // Verify it's now visible
        const buttonToCheck = nativeSubmitButton; // Capture for setTimeout
        setTimeout(() => {
          if (buttonToCheck) {
            const isNowVisible = buttonToCheck.offsetParent !== null;
            const footerNowVisible = footerElement
              ? footerElement.offsetParent !== null
              : true;

            if (!isNowVisible && !footerNowVisible) {
              console.warn(
                '[EMPTY-FORM] Native button still not visible after forcing - may need custom fallback'
              );
            }
          }
        }, 50);

        return; // Don't create custom button if native one exists
      }

      // Only create custom button if NO native button was found at all
      // (This should rarely happen if SurveyJS is working correctly)
      if (!nativeSubmitButton) {
      } else {
        // We already handled the native button case above and returned early
        // This code should not be reached, but just in case:
        return;
      }

      // Continue to create custom button only if native button doesn't exist

      // Hide the "no visible questions" message if it exists
      const noQuestionsMessage = document.querySelector(
        '.sv-body__page .sv-string-viewer'
      );
      if (
        noQuestionsMessage &&
        noQuestionsMessage.textContent?.includes('synlige spørgsmål')
      ) {
        (noQuestionsMessage as HTMLElement).style.display = 'none';
      }

      // Find the form container and add submit button there
      // Reuse hostEl from earlier in the function
      const surveyContainer =
        (hostEl &&
          (hostEl.querySelector('.sv-root-modern') ||
            hostEl.querySelector('.sv-container-modern') ||
            hostEl.querySelector('[class*="survey"]') ||
            hostEl.querySelector('.sv-root') ||
            hostEl.querySelector('.sd-root-modern'))) ||
        document.querySelector('app-dynamic-form .sv-root-modern') ||
        document.querySelector('app-dynamic-form .sv-container-modern') ||
        document.querySelector('app-dynamic-form [class*="survey"]') ||
        document.querySelector('app-dynamic-form .sv-root') ||
        document.querySelector('app-dynamic-form .sd-root-modern');
      // Scope queries to the host/survey to avoid attaching far from the form
      let navContainer =
        surveyContainer?.querySelector('.form-submit-container') ||
        hostEl?.querySelector('.form-submit-container') ||
        document.querySelector('app-dynamic-form .form-submit-container');

      if (!navContainer) {
        if (surveyContainer) {
          navContainer = document.createElement('div');
          navContainer.className = 'form-submit-container';
          (navContainer as HTMLElement).style.marginTop = '16px';
          surveyContainer.appendChild(navContainer);
        } else if (hostEl) {
          navContainer = document.createElement('div');
          navContainer.className = 'form-submit-container';
          (navContainer as HTMLElement).style.maxWidth = '600px';
          (navContainer as HTMLElement).style.margin = '16px auto 0';
          hostEl.appendChild(navContainer);
        } else {
          navContainer = document.createElement('div');
          navContainer.className = 'form-submit-container';
          (navContainer as HTMLElement).style.maxWidth = '600px';
          (navContainer as HTMLElement).style.margin = '16px auto 0';
          document.body.appendChild(navContainer);
        }
      }

      if (navContainer) {
        // Container is already styled, just ensure it's visible
        (navContainer as HTMLElement).style.display = 'block';
        (navContainer as HTMLElement).style.visibility = 'visible';

        // Check if submit button already exists (prevent duplicates)
        let submitButton = navContainer.querySelector('.form-submit-btn');

        // Also check if button exists elsewhere in the component
        if (!submitButton) {
          submitButton = hostEl?.querySelector(
            '.form-submit-btn'
          ) as HTMLElement | null;
        }

        if (!submitButton) {
          // Create submit button
          submitButton = document.createElement('button');
          // Get completeText directly from the original JSON to bypass localization
          const surveyJSON = this.getInput('surveyJSON');
          const jsonObj = surveyJSON
            ? typeof surveyJSON === 'string'
              ? JSON.parse(surveyJSON)
              : surveyJSON
            : {};
          const buttonText =
            jsonObj.completeText ||
            jsonObj.emptyFormSubmitText || // fallback to old property name
            this.surveyModel.completeText ||
            this.getSettingValue('completeText') ||
            'Send';
          (submitButton as HTMLButtonElement).textContent = buttonText;
          submitButton.className = 'form-submit-btn';

          // Hover effects are now handled by CSS

          // Add click handler
          submitButton.addEventListener('click', () => {
            this.handleFormSubmission();
          });

          navContainer.appendChild(submitButton);

          // Double-check after creating button that we're not on completion page
          setTimeout(() => {
            if (
              (this.surveyModel as any).isCompleted ||
              (this.surveyModel as any).state === 'completed'
            ) {
              this.hideSubmitButtonsOnCompletion();
            } else {
              const completionPage = document.querySelector(
                '.sv-completedpage, .sv-body__page--completed'
              );
              if (completionPage) {
                this.hideSubmitButtonsOnCompletion();
              }
            }
          }, 0);
        }
      } else {
      }
    } catch (error) {
      console.error('[EMPTY-FORM] Error creating submit button:', error);
    } finally {
      // render() / DOM fixes can reset SurveyJS page visibility; re-apply auth + data-source rules.
      if (this.surveyModel) {
        this.updatePageVisibility(this.surveyModel);
      }
    }
  }

  /**
   * Hides all submit buttons when form is on completion page
   */
  private hideSubmitButtonsOnCompletion(): void {
    // Hide all submit buttons
    const submitButtons = document.querySelectorAll(
      '.form-submit-btn, .sv_complete_btn, button[type="submit"]'
    );
    submitButtons.forEach((btn) => {
      (btn as HTMLElement).style.display = 'none';
      (btn as HTMLElement).style.visibility = 'hidden';
    });

    // Also hide the submit container
    const submitContainer = document.querySelector('.form-submit-container');
    if (submitContainer) {
      (submitContainer as HTMLElement).style.display = 'none';
    }

    // Hide navigation containers that might contain submit buttons
    const navContainers = document.querySelectorAll(
      '.sv-action-bar, .sv-footer, .sv-nav-btn-container'
    );
    navContainers.forEach((container) => {
      const hasSubmitButton = container.querySelector(
        '.sv_complete_btn, button[type="submit"]'
      );
      if (hasSubmitButton) {
        (container as HTMLElement).style.display = 'none';
      }
    });
  }

  /**
   * Handles form submission when submit button is clicked
   */
  private handleFormSubmission(): void {
    // Prevent double submission
    if (this.isFormSubmitting) {
      return;
    }

    // Set flag to prevent duplicate submissions
    this.isFormSubmitting = true;

    try {
      // Try the standard SurveyJS completion method first
      // This will trigger onCompleting which handles the actual data submission
      this.surveyModel.doComplete();
    } catch (error) {
      // Reset flag on error so user can try again
      this.isFormSubmitting = false;

      // Only do manual completion if standard completion fails
      const formReceiveFunction = this.getInput('formReceiveFunction');
      if (formReceiveFunction) {
        const dataDest =
          this.componentDataDestService.getDataDest(formReceiveFunction);
        if (dataDest) {
          const dataToSubmit = this.surveyModel.data || {};
          dataDest(dataToSubmit);
        }
      } else {
      }

      // Auto-scroll to top after manual submission
      this.scrollToTopOfModal();
    }

    // Note: We don't reset isFormSubmitting here because onCompleting handler
    // will handle the submission and reset it when done (or on error)
  }

  /**
   * Forces the form to have the correct state by ensuring page visibility matches authentication
   * and the current page is set to a visible page
   */
  private forceCorrectFormState(): void {

    if (!this.surveyModel) {
      console.warn('[FORCE-STATE] No survey model!');
      return;
    }

    try {
      // First make sure page visibility is correctly set based on authentication

      // Re-apply visibility rules directly
      this.surveyModel.pages.forEach((page) => {
        const authVisibility = page.getPropertyValue('authVisibility');
        if (authVisibility === 1) {
          // Only logged in
          page.visible = this.isAuthenticated;
        } else if (authVisibility === 2) {
          // NOT logged in
          page.visible = !this.isAuthenticated;
        } else if (authVisibility === 3) {
          // Nobody
          page.visible = false;
        } else {
          // Everyone (0) or undefined
          page.visible = true;
        }
      });

      // Get the visible pages
      const visiblePages = this.surveyModel.visiblePages;

      // If we have no visible pages, do not force any page (e.g. Godkend betingelser / authVisibility 3).
      if (visiblePages.length === 0) {
        this.noVisiblePages = true;
        this.cdr.detectChanges();
      } else {
        this.noVisiblePages = false;
        this.noVisiblePagesHint = null;
        // Set the current page to the first visible page
        if (this.surveyModel.currentPage !== visiblePages[0]) {
          this.surveyModel.currentPage = visiblePages[0];
        }
      }

      // Force the submit button to be visible

      // Set model properties to ensure navigation buttons are available
      this.surveyModel.showNavigationButtons = true;
      // ALWAYS respect JSON value if present (source of truth from SurveyJS Creator)
      if (this.navigationButtonsLocationFromJSON) {
        (this.surveyModel as any).navigationButtonsLocation =
          this.navigationButtonsLocationFromJSON;
      } else if (!(this.surveyModel as any).navigationButtonsLocation) {
        // Only set to "bottom" if JSON doesn't have a value AND it's not already set from settings
        (this.surveyModel as any).navigationButtonsLocation = 'bottom';
      }

      // Force update navigation buttons in DOM
      setTimeout(() => {
        this.forceUpdateNavigationButtons();
      }, 0);

      // Force update navigation buttons in DOM immediately after setting property
      setTimeout(() => {
        this.forceUpdateNavigationButtons();
      }, 0);

      // Set up MutationObserver to watch for navigation buttons being added to DOM
      this.setupNavigationButtonsObserver();
      const __hideSubmit =
        (this as any).hideSubmitButtonForThisInstance === true;
      if (!__hideSubmit) {
        this.surveyModel.completeText = this.surveyModel.completeText || 'Send';
      }
      const __prev = this.getSettingValue('pagePrevText');
      const __next = this.getSettingValue('pageNextText');
      if (__prev) this.surveyModel.pagePrevText = String(__prev);
      if (__next) this.surveyModel.pageNextText = String(__next);

      // Ensure the model believes it can be completed
      this.surveyModel.canBeCompleted = function () {
        return true;
      };

      // Add our submit button via DOM manipulation - only when allowed
      if (!__hideSubmit)
        setTimeout(() => {
          try {
            // First try to find the standard navigation container
            const navContainer = document.querySelector(
              '.sv-footer.sv-action-bar'
            );

            if (navContainer) {

              // Check if there's already a submit button
              const submitButton =
                navContainer.querySelector('.sv_complete_btn');

              if (!submitButton) {

                // Create a submit button
                const button = document.createElement('input');
                button.type = 'button';
                button.value = this.surveyModel.completeText || 'Send'; // Changed to Danish
                button.className = 'sv_complete_btn';
                button.style.cssText =
                  'background-color: #1ab394; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin: 10px;';

                // Add click handler
                button.addEventListener('click', () => {
                  // First try to complete via the API
                  this.surveyModel.doComplete();

                  // If that doesn't work (no event fired), create and trigger the completion event manually
                  setTimeout(() => {
                    // Check if the survey reported itself as complete
                    const formReceiveFunction = this.getInput(
                      'formReceiveFunction'
                    );
                    if (formReceiveFunction) {
                      const dataDest =
                        this.componentDataDestService.getDataDest(
                          formReceiveFunction
                        );
                      if (dataDest) {
                        dataDest(this.surveyModel.data);
                      }
                    }

                    // Auto-scroll to top after manual submission
                    this.scrollToTopOfModal();
                  }, 100);
                });

                // Add to navigation container
                navContainer.appendChild(button);
              } else {
                (submitButton as HTMLElement).style.display = 'inline-block';
              }
            } else {

              // If no navigation container, add one to the form
              const surveyContainer = document.querySelector('.sv_body');

              if (surveyContainer) {
                // Create a custom navigation container
                const customNavContainer = document.createElement('div');
                customNavContainer.className =
                  'sv-footer sv-action-bar custom-nav-container';
                customNavContainer.style.cssText =
                  'text-align: center; margin-top: 20px; padding: 10px;';

                // Create a submit button
                const button = document.createElement('input');
                button.type = 'button';
                button.value = this.surveyModel.completeText || 'Send'; // Changed to Danish
                button.className = 'sv_complete_btn custom-submit-btn';
                button.style.cssText =
                  'background-color: #1ab394; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 16px;';

                // Add a more powerful click handler to guarantee form submission
                button.addEventListener('click', () => {

                  // Try all methods to complete the survey
                  try {
                    // First try the standard method
                    this.surveyModel.doComplete();
                  } catch (e) {
                  }

                  // Always trigger the completion handler directly
                  setTimeout(() => {
                    const formReceiveFunction = this.getInput(
                      'formReceiveFunction'
                    );
                    if (formReceiveFunction) {
                      const dataDest =
                        this.componentDataDestService.getDataDest(
                          formReceiveFunction
                        );
                      if (dataDest) {
                        // Use either the current data or an empty object
                        const dataToSubmit = this.surveyModel.data || {};
                        dataDest(dataToSubmit);
                      }
                    } else {
                    }

                    // Auto-scroll to top after manual submission
                    this.scrollToTopOfModal();
                  }, 100);
                });

                // Add button to container
                customNavContainer.appendChild(button);

                // Add container to survey
                surveyContainer.appendChild(customNavContainer);

              } else {
              }
            }
          } catch (error) {
            console.error(
              '[FORCE-STATE] Error ensuring submit button visibility:',
              error
            );
          }
        }, 300);

      // Log final state
      //this.surveyModel.pages.forEach((page, index) => {
      //});
    } catch (error) {
      console.error('[FORCE-STATE] Error forcing form state:', error);
    }
  }

  /**
   * Whether the page's authVisibility allows showing it to the current user (ignores visibilityDataSource).
   * 0/default = everyone, 1 = logged in only, 2 = guests only, 3 = nobody.
   */
  private pageAuthVisibilityAllows(page: any): boolean {
    const raw =
      typeof page.getPropertyValue === 'function'
        ? page.getPropertyValue('authVisibility')
        : (page as any).authVisibility;
    if (raw === undefined || raw === null) return true;
    const n = Number(raw);
    switch (n) {
      case 1:
        return this.isAuthenticated;
      case 2:
        return !this.isAuthenticated;
      case 3:
        return false;
      default:
        return true;
    }
  }

  /**
   * When visibilityDataSource allows a page, keep its original visibleIf from survey JSON
   * (e.g. page2 catering == lokation). When hidden by the data source, force visibleIf false.
   */
  private pageVisibleIfAfterDataSource(
    pageName: string,
    dataSourceAllows: boolean
  ): string {
    if (!dataSourceAllows) {
      return DynamicFormComponent.VISIBLEIF_ALWAYS_FALSE;
    }
    const jsonPages = (this.originalSurveyJSON?.pages || []) as Array<
      Record<string, unknown>
    >;
    const jsonPage = jsonPages.find((p) => p['name'] === pageName);
    const originalVisibleIf = jsonPage?.['visibleIf'];
    if (
      typeof originalVisibleIf === 'string' &&
      originalVisibleIf.trim() !== ''
    ) {
      return originalVisibleIf;
    }
    return '';
  }

  /**
   * When visibilityDataSource allows a question, keep its original visibleIf from survey JSON
   * (e.g. additional_rooms requires predefined location + room). When hidden by the data source, force false.
   */
  private questionVisibleIfAfterDataSource(
    questionName: string,
    dataSourceAllows: boolean
  ): string {
    if (!dataSourceAllows) {
      return DynamicFormComponent.VISIBLEIF_ALWAYS_FALSE;
    }
    const def = this.findElementDefinitionInOriginalJson(questionName);
    const originalVisibleIf = def?.['visibleIf'];
    if (
      typeof originalVisibleIf === 'string' &&
      originalVisibleIf.trim() !== ''
    ) {
      return originalVisibleIf;
    }
    return '';
  }

  private runSurveyVisibilityExpressions(): void {
    if (!this.surveyModel) {
      return;
    }
    if (typeof (this.surveyModel as any).runExpressions === 'function') {
      (this.surveyModel as any).runExpressions();
    }
  }

  /**
   * Re-apply visibility from visibilityFromDataSource map and page._visibilityDsResult.
   * Call after a short delay so visibility survives any overwrites on refresh (e.g. FORCE-CHECK or SurveyJS).
   */
  private reapplyVisibilityFromStored(): void {
    if (!this.surveyModel?.pages?.length) return;
    const jsonPages = (this.originalSurveyJSON?.pages || []) as Array<
      Record<string, unknown>
    >;
    const templateDsPageNames = new Set<string>();
    jsonPages.forEach((p: Record<string, unknown>) => {
      if (
        typeof p['visibilityDataSource'] === 'string' &&
        typeof p['name'] === 'string'
      ) {
        templateDsPageNames.add(p['name'] as string);
      }
    });
    this.surveyModel.pages.forEach((page: any) => {
      if (!templateDsPageNames.has(page.name)) return;
      const fromMap = this.visibilityFromDataSource.get(`page:${page.name}`);
      const fromPage =
        page.getPropertyValue?.('_visibilityDsResult') ??
        page._visibilityDsResult;
      const visible =
        fromMap !== undefined
          ? fromMap
          : typeof fromPage === 'boolean'
          ? fromPage
          : undefined;
      if (visible === undefined) return;
      const visibleIfValue = this.pageVisibleIfAfterDataSource(
        page.name,
        visible
      );
      if (typeof page.setPropertyValue === 'function') {
        page.setPropertyValue('visibleIf', visibleIfValue);
        page.setPropertyValue('visible', visible);
      } else {
        page.visibleIf = visibleIfValue;
        page.visible = visible;
      }
    });
    this.updatePageVisibility(this.surveyModel);
    this.cdr.detectChanges();
  }

  /**
   * Updates the visibility of survey pages based on authentication status
   */
  updatePageVisibility(survey: Model) {
    if (!survey || !survey.pages || survey.pages.length === 0) {
      console.warn(
        'Cannot update page visibility: survey or pages not initialized'
      );
      return;
    }

    // Log the current visibility state of all pages
    survey.pages.forEach((page, index) => {
      const authVisibility = page.getPropertyValue('authVisibility');
    });

    // Apply visibility rules. Only treat as visibilityDataSource page if the TEMPLATE has visibilityDataSource for this page name (so we target page5, not page4 from merge).
    const jsonPagesArr = (this.originalSurveyJSON?.pages || []) as Array<
      Record<string, unknown>
    >;
    const templatePageNamesWithVisibilityDsSet = new Set<string>();
    jsonPagesArr.forEach((p: Record<string, unknown>) => {
      if (
        typeof p['visibilityDataSource'] === 'string' &&
        typeof p['name'] === 'string'
      ) {
        templatePageNamesWithVisibilityDsSet.add(p['name'] as string);
      }
    });
    survey.pages.forEach((page, index) => {
      // Re-apply stored visibility for any page we ran visibilityDataSource for (stored value takes precedence over template set)
      // Fall back to value persisted on page (survives map clear / ordering on refresh)
      const fromMap = this.visibilityFromDataSource.get(`page:${page.name}`);
      const fromPage =
        (page as any).getPropertyValue?.('_visibilityDsResult') ??
        (page as any)._visibilityDsResult;
      const stored =
        fromMap !== undefined
          ? fromMap
          : typeof fromPage === 'boolean'
          ? fromPage
          : undefined;
      if (stored !== undefined) {
        // Data source result must still respect authVisibility (e.g. logged-in-only pages for guests).
        const visibleToApply = stored && this.pageAuthVisibilityAllows(page);
        const visibleIfBefore =
          (page as any).visibleIf ??
          (page as any).getPropertyValue?.('visibleIf');
        const visibleIfValue = this.pageVisibleIfAfterDataSource(
          page.name,
          visibleToApply
        );
        if (typeof (page as any).setPropertyValue === 'function') {
          (page as any).setPropertyValue('visibleIf', visibleIfValue);
          (page as any).setPropertyValue('visible', visibleToApply);
        } else {
          (page as any).visibleIf = visibleIfValue;
          page.visible = visibleToApply;
        }
        return;
      }
      if (templatePageNamesWithVisibilityDsSet.has(page.name)) {
        // Page has visibilityDataSource but no stored value yet. Do not force visible = false
        // while waiting for the API, except when auth already forbids this user (e.g. guest on authVisibility 1).
        if (!this.pageAuthVisibilityAllows(page)) {
          const visibleIfValue = DynamicFormComponent.VISIBLEIF_ALWAYS_FALSE;
          if (typeof (page as any).setPropertyValue === 'function') {
            (page as any).setPropertyValue('visibleIf', visibleIfValue);
            (page as any).setPropertyValue('visible', false);
          } else {
            (page as any).visibleIf = visibleIfValue;
            page.visible = false;
          }
        }
        return;
      }

      const authVisibility = page.getPropertyValue('authVisibility');

      // Only apply visibility rules if authVisibility is explicitly set
      if (authVisibility !== undefined && authVisibility !== null) {
        // Ensure authVisibility is a number
        const authVisibilityNum = Number(authVisibility);

        // 0 = Everyone, 1 = Only logged in, 2 = NOT logged in, 3 = Nobody
        switch (authVisibilityNum) {
          case 1: // Only logged in
            page.visible = this.isAuthenticated;
            break;
          case 2: // NOT logged in
            page.visible = !this.isAuthenticated;
            break;
          case 3: // Nobody
            page.visible = false;
            break;
          default: // Everyone (0) or any other value
            page.visible = true;
            break;
        }
      } else {
        // If authVisibility is not set, make the page visible
        page.visible = true;
      }
    });

    // When no pages are visible, show message and hide survey (never show "Bekræft" with no page).
    const visiblePages = survey.visiblePages;
    if (visiblePages.length === 0 && survey.pages.length > 0) {
      this.noVisiblePages = true;
    } else {
      this.noVisiblePages = false;
      this.noVisiblePagesHint = null;
      // Ensure current page is always a visible page (avoids "Bekræft" with no page content on refresh)
      if (
        visiblePages.length > 0 &&
        (!survey.currentPage || !visiblePages.includes(survey.currentPage))
      ) {
        try {
          survey.currentPage = visiblePages[0];
          const idx = survey.pages.indexOf(visiblePages[0]);
          if (idx >= 0) {
            survey.currentPageNo = idx;
          }
        } catch (_e) {
          /* ignore */
        }
      }
    }
    this.reapplyQuestionVisibilityFromStored();
  }

  /**
   * Ensures the authVisibility property is properly loaded from JSON for each page
   */
  private ensureAuthVisibilityLoaded(survey: Model, jsonObj: any) {
    if (!jsonObj.pages || !Array.isArray(jsonObj.pages)) {
      console.warn('[VISIBILITY-DEBUG] No pages found in survey JSON');
      return;
    }

    // Match by page name so authVisibility stays correct if survey page order differs from JSON array order.
    jsonObj.pages.forEach((jsonPage: any, index: number) => {
      const pageName =
        typeof jsonPage?.name === 'string' ? jsonPage.name : null;
      const surveyPage = pageName
        ? survey.pages.find((p: any) => p.name === pageName)
        : index < survey.pages.length
        ? survey.pages[index]
        : null;
      if (!surveyPage) {
        return;
      }
      if (jsonPage.authVisibility !== undefined) {
        surveyPage.setPropertyValue(
          'authVisibility',
          Number(jsonPage.authVisibility)
        );
      }
    });

    // Verify all pages in the survey after setting
    survey.pages.forEach((page, index) => {
      const authVisibility = page.getPropertyValue('authVisibility');
    });
  }

  // Add this new method
  private addSurveyJSDropdownDiagnostics() {
    // Add a global click listener to detect dropdown clicks
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      // Check if the click is on a SurveyJS dropdown
      if (
        target.closest('.sv-dropdown__value') ||
        target.closest('.sv-dropdown') ||
        target.classList.contains('sv-dropdown__value')
      ) {

        // Wait for dropdown to open
        setTimeout(() => {
          // Find all dropdown popups
          const popups = document.querySelectorAll(
            '.sv-popup, .sv-dropdown-popup'
          );

          popups.forEach((popup, index) => {

            // Check for list elements
            const lists = popup.querySelectorAll(
              '.sv-list, .sv-dropdown__list'
            );

            lists.forEach((list, listIndex) => {

              // Check for list items
              const items = list.querySelectorAll(
                '.sv-list__item, .sv-dropdown__item, li[role="option"]'
              );

              items.forEach((item, itemIndex) => {
              });
            });

            // If no lists or items found, check all child elements
            if (lists.length === 0) {
              this.logAllChildElements(popup as HTMLElement);
            }
          });

          // If no popups found, check the entire document for dropdown-related elements
          if (popups.length === 0) {
            const allDropdownElements = document.querySelectorAll(
              '.sv-dropdown, .sv-popup, .sv-list, .sv-dropdown__list, .sv-list__item, .sv-dropdown__item'
            );
          }
        }, 500); // Wait for dropdown to open
      }
    });
  }

  // Helper method to log all child elements recursively
  private logAllChildElements(element: HTMLElement, depth = 0) {
    if (depth > 5) return; // Prevent infinite recursion

    const children = element.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;

      this.logAllChildElements(child, depth + 1);
    }
  }

  // Helper method to check if an element is visible
  private isElementVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);

    return !(
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.opacity === '0' ||
      element.offsetWidth === 0 ||
      element.offsetHeight === 0
    );
  }

  // Add this new method
  private addGlobalSurveyJSFix() {
    // Check if the script already exists
    if (document.getElementById('surveyjs-dropdown-fix-script')) {
      return;
    }

    // Add a script to the document head to fix SurveyJS dropdowns
    const script = document.createElement('script');
    script.id = 'surveyjs-dropdown-fix-script';
    const timestamp = Date.now();
    script.textContent = `
      // Wait for SurveyJS to be loaded
      const surveyJSCheckInterval_${timestamp} = setInterval(() => {
        if (window.Survey) {
          clearInterval(surveyJSCheckInterval_${timestamp});
          
          // Add a click event listener to fix dropdowns when they are clicked
          document.addEventListener('click', (event) => {
            if (event.target && (
              event.target.classList.contains('sv-dropdown__value') || 
              event.target.closest('.sv-dropdown__value')
            )) {
              
              // Wait for the dropdown to open
              setTimeout(() => {
                // Find all popups
                const popups = document.querySelectorAll('.sv-popup, .sv-dropdown-popup, .sd-popup, .sd-dropdown-popup');
                
                // Make sure popups and their content are visible
                popups.forEach(popup => {
                  // Make the popup visible
                  popup.style.display = 'block';
                  popup.style.visibility = 'visible';
                  popup.style.opacity = '1';
                  popup.style.setProperty('z-index', '2147483649', 'important');
                  
                  // Make all popup containers visible
                  const containers = popup.querySelectorAll('.sv-popup__container, .sv-dropdown-popup__container, .sv-popup__shadow, .sv-popup__content, .sv-popup__scrolling-content, .sv-popup__body-content');
                  containers.forEach(container => {
                    container.style.display = 'block';
                    container.style.visibility = 'visible';
                    container.style.opacity = '1';
                  });
                  
                  // Find and make the list visible
                  const listElements = popup.querySelectorAll('.sv-list, .sv-dropdown__list, .sv-list__container');
                  listElements.forEach(listElement => {
                    listElement.style.display = 'block';
                    listElement.style.visibility = 'visible';
                    listElement.style.opacity = '1';
                    listElement.style.backgroundColor = 'white';
                    listElement.style.height = 'auto';
                    listElement.style.minHeight = '50px';
                  });
                  
                  // Find and make all items visible
                  const items = popup.querySelectorAll('.sv-list__item, .sv-dropdown__item, li[role="option"]');
                  items.forEach(item => {
                    item.style.display = 'block';
                    item.style.visibility = 'visible';
                    item.style.opacity = '1';
                    item.style.backgroundColor = 'white';
                    item.style.color = 'black';
                    item.style.padding = '8px';
                    item.style.margin = '2px';
                    item.style.borderBottom = '1px solid #f0f0f0';
                  });
                  
                  // Make search input visible
                  const searchInput = popup.querySelector('.sv-dropdown__filter-string-input');
                  if (searchInput) {
                    searchInput.style.display = 'block';
                    searchInput.style.visibility = 'visible';
                    searchInput.style.opacity = '1';
                    searchInput.style.width = '100%';
                    searchInput.style.padding = '8px';
                    searchInput.style.marginBottom = '8px';
                    searchInput.style.border = '1px solid #ccc';
                  }
                  
                  // Log the HTML structure for debugging
                });
              }, 50);
            }
          });
        }
      }, 100);
    `;

    // Add the script to the document head
    document.head.appendChild(script);
  }

  private getSettingValue(key: string): any {
    // Try to get the setting from the component inputs
    const settingInput = this.getInput(key);
    if (settingInput !== undefined) {
      return settingInput;
    }

    // If not found in inputs, check if there's a settings object in the inputs
    const settingsInput = this.getInput('settings');
    const settings =
      settingsInput &&
      typeof settingsInput === 'object' &&
      Object.prototype.hasOwnProperty.call(settingsInput, 'value')
        ? (settingsInput as any).value
        : settingsInput;
    if (
      settings &&
      typeof settings === 'object' &&
      Object.prototype.hasOwnProperty.call(settings, key)
    ) {
      const raw = (settings as any)[key];
      // Support both primitive and { value: any } formats
      if (
        raw &&
        typeof raw === 'object' &&
        Object.prototype.hasOwnProperty.call(raw, 'value')
      ) {
        return (raw as any).value;
      }
      return raw;
    }

    return null;
  }

  /** Settings → model → JSON; used so nav location is applied consistently everywhere. */
  private getEffectiveNavigationButtonsLocation(): string | undefined {
    const settingsLoc = this.getSettingValue('navigationButtonsLocation');
    if (
      settingsLoc !== undefined &&
      settingsLoc !== null &&
      String(settingsLoc).trim() !== ''
    ) {
      return String(settingsLoc);
    }
    const modelLoc = (this.surveyModel as any)?.navigationButtonsLocation;
    if (
      modelLoc !== undefined &&
      modelLoc !== null &&
      String(modelLoc).trim() !== ''
    ) {
      return String(modelLoc);
    }
    if (this.navigationButtonsLocationFromJSON) {
      return this.navigationButtonsLocationFromJSON;
    }
    return undefined;
  }

  private shouldShowSurveyNavigation(): boolean {
    const settingsNavLoc = this.getSettingValue('navigationButtonsLocation');
    if (settingsNavLoc === 'none' && !this.needsNativeCompleteButtonBar()) {
      return false;
    }
    if (
      this.getSettingValue('showNavigationButtons') === false &&
      !this.needsNativeCompleteButtonBar()
    ) {
      return false;
    }
    const pageCount = this.surveyModel?.pages?.length ?? 0;
    // Multi-page forms (e.g. add-event) always need prev/next unless explicitly disabled above.
    if (pageCount > 1) {
      return true;
    }
    // Single-page forms render the native complete/submit button in the navigation bar.
    if (this.needsNativeCompleteButtonBar()) {
      return true;
    }
    if (this.getSettingValue('showNavigationButtons') === true) {
      return true;
    }
    return this.surveyModel?.showNavigationButtons !== false;
  }

  private isSurveySinglePage(): boolean {
    return (this.surveyModel?.pages?.length ?? 0) <= 1;
  }

  /** True when SurveyJS should show its built-in complete button (not our custom empty-form button). */
  private needsNativeCompleteButtonBar(): boolean {
    if ((this as any).hideSubmitButtonForThisInstance) {
      return false;
    }
    if (!this.surveyModel) {
      return false;
    }
    if (
      (this.surveyModel as any).isCompleted ||
      (this.surveyModel as any).state === 'completed'
    ) {
      return false;
    }
    return this.isSurveySinglePage();
  }

  /**
   * Ensures SurveyJS native complete/submit button is visible.
   * showEmptyFormSubmit:false only disables our custom fallback — not SurveyJS's own button.
   */
  private ensureNativeCompleteButtonVisible(): void {
    if (!this.needsNativeCompleteButtonBar()) {
      return;
    }
    this.syncSurveyNavigationModel();
    (this.surveyModel as any).showCompleteButton = true;
    if (!this.surveyModel.completeText) {
      this.surveyModel.completeText = 'Gem';
    }

    const root = this.el?.nativeElement as HTMLElement | undefined;
    if (!root) {
      return;
    }

    this.ensureSurveyNavigationVisible();

    root
      .querySelectorAll(
        '.sd-navigation__complete-btn, .sv-navigation__complete-btn, ' +
          '.sv_complete_btn, .sd_complete_btn, ' +
          '.sv-footer__complete-btn, .sd-footer__complete-btn, ' +
          'button[data-action="complete"], input.sv_complete_btn'
      )
      .forEach((el) => {
        const node = el as HTMLElement;
        node.style.removeProperty('display');
        node.style.removeProperty('visibility');
        node.style.setProperty('display', 'inline-block', 'important');
        node.style.setProperty('visibility', 'visible', 'important');
      });
  }

  /** Keep SurveyJS model flags aligned so prev/next buttons are rendered. */
  private syncSurveyNavigationModel(): void {
    if (!this.surveyModel || !this.shouldShowSurveyNavigation()) {
      return;
    }
    if (this.surveyModel.showNavigationButtons !== true) {
      this.surveyModel.showNavigationButtons = true;
    }
    if (!(this as any).hideSubmitButtonForThisInstance) {
      (this.surveyModel as any).showCompleteButton = true;
    }
    const loc = this.getEffectiveNavigationButtonsLocation();
    if (loc && loc !== 'none') {
      (this.surveyModel as any).navigationButtonsLocation = loc;
    } else if (!(this.surveyModel as any).navigationButtonsLocation) {
      (this.surveyModel as any).navigationButtonsLocation = 'bottom';
    }
  }

  /** Force SurveyJS v2 navigation footer/buttons visible in the DOM (scoped to this form). */
  private ensureSurveyNavigationVisible(): void {
    if (!this.shouldShowSurveyNavigation()) {
      return;
    }
    const root = this.el?.nativeElement as HTMLElement | undefined;
    if (!root) {
      return;
    }
    const containerSelectors = [
      '.sd-body__navigation',
      '.sv-body__navigation',
      '.sd-navigation',
      '.sv-navigation',
      '.sd-footer:not(.sd-footer__nav-top):not(.sd-action-bar--top)',
      '.sv-footer:not(.sv-footer__nav-top):not(.sv-action-bar--top)',
      '.sd-action-bar:not(.sd-action-bar--top)',
      '.sv-action-bar:not(.sv-action-bar--top)',
    ];
    containerSelectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((el) => {
        const node = el as HTMLElement;
        node.classList.remove('sd-action--hidden', 'sv-action--hidden');
        node.style.removeProperty('display');
        node.style.removeProperty('visibility');
        node.style.setProperty('display', 'flex', 'important');
        node.style.setProperty('visibility', 'visible', 'important');
      });
    });

    root
      .querySelectorAll(
        'button[class*="prev"], button[class*="next"], ' +
          '.sd-navigation__prev-btn, .sd-navigation__next-btn, ' +
          '.sv-navigation__prev-btn, .sv-navigation__next-btn, ' +
          '.sd-prev, .sv-prev, .sd-next, .sv-next, ' +
          'button[data-action="prev"], button[data-action="next"], ' +
          '.sd-btn, .sv-btn'
      )
      .forEach((el) => {
        const btn = el as HTMLElement;
        const cls = btn.className || '';
        if (cls.includes('complete') || btn.getAttribute('type') === 'submit') {
          return;
        }
        btn.style.removeProperty('display');
        btn.style.removeProperty('visibility');
        btn.style.setProperty('visibility', 'visible', 'important');
      });

    this.tagSurveyNavigationButtonRoles();
  }

  /** Mark prev/next/complete so CSS can style them independently of SurveyJS variants. */
  private tagSurveyNavigationButtonRoles(): void {
    const root = this.el?.nativeElement as HTMLElement | undefined;
    if (!root) {
      return;
    }

    const mark = (el: Element, role: 'prev' | 'next' | 'complete'): void => {
      const node = el as HTMLElement;
      node.classList.add(`duna-nav-${role}`);
      const action = node.classList.contains('sd-action')
        ? node
        : (node.querySelector('.sd-action, button') as HTMLElement | null);
      action?.classList.add(`duna-nav-${role}`);
    };

    const match = (
      selector: string,
      role: 'prev' | 'next' | 'complete'
    ): void => {
      root.querySelectorAll(selector).forEach((el) => mark(el, role));
    };

    match(
      '#sv-nav-prev, [data-action="prev"], .sd-navigation__prev-btn, .sv-navigation__prev-btn, .sv_prev_btn',
      'prev'
    );
    match(
      '#sv-nav-next, [data-action="next"], .sd-navigation__next-btn, .sv-navigation__next-btn, .sv_next_btn',
      'next'
    );
    match(
      '#sv-nav-complete, [data-action="complete"], .sd-navigation__complete-btn, .sv-navigation__complete-btn, .sv_complete_btn, .form-submit-btn, .custom-submit-btn',
      'complete'
    );

    root
      .querySelectorAll(
        '.sd-body__navigation .sd-action, .sv-body__navigation .sd-action, .sd-footer .sd-action, .sv-footer .sd-action, .form-submit-btn'
      )
      .forEach((el) => {
        if (
          el.classList.contains('duna-nav-prev') ||
          el.classList.contains('duna-nav-next') ||
          el.classList.contains('duna-nav-complete')
        ) {
          return;
        }
        const text = (el.textContent || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
        if (text === 'tilbage' || text === 'previous' || text === 'back') {
          mark(el, 'prev');
        } else if (text === 'videre' || text === 'next') {
          mark(el, 'next');
        } else if (
          text === 'færdig' ||
          text === 'gem' ||
          text === 'send' ||
          text === 'complete' ||
          text === 'done'
        ) {
          mark(el, 'complete');
        }
      });
  }

  private navContainerHasButtons(container: HTMLElement): boolean {
    return !!container.querySelector(
      'button[class*="prev"], button[class*="next"], button[class*="complete"], ' +
        '.sd-prev, .sv-prev, .sd-next, .sv-next, .sd_complete_btn, .sv_complete_btn, ' +
        'button.sd-btn, button.sv-btn, button[data-action]'
    );
  }

  /** Hide SurveyJS complete/submit UI only — never prev/next when navigation is enabled. */
  private hideSurveySubmitButtonsOnly(): void {
    try {
      const root = this.el?.nativeElement ?? document;
      const submitSelectors = [
        '.sv_complete_btn',
        '.sv-footer__complete-btn',
        '.sd-footer__complete-btn',
        '.sd-navigation__complete-btn',
        '.sv-navigation__complete-btn',
        'button[data-action="complete"]',
        'button[type="submit"]',
        '.form-submit-btn',
        'input.sv_complete_btn',
      ];
      root
        .querySelectorAll(submitSelectors.join(','))
        .forEach((el: Element) => {
          const node = el as HTMLElement;
          const classes = node.className || '';
          const isNavButton =
            classes.includes('next') ||
            classes.includes('prev') ||
            classes.includes('sd-next') ||
            classes.includes('sv-next') ||
            classes.includes('sd-prev') ||
            classes.includes('sv-prev');
          if (isNavButton) {
            return;
          }
          node.style.setProperty('display', 'none', 'important');
          node.style.setProperty('visibility', 'hidden', 'important');
        });

      if (!this.shouldShowSurveyNavigation()) {
        const navSelectors = [
          '.sd-action-bar',
          '.sv-action-bar',
          '.sd_nav',
          '.sv_nav',
          '.sv_prev_btn',
          '.sv_next_btn',
          '.sd-body__navigation',
          '.sv-body__navigation',
        ];
        root.querySelectorAll(navSelectors.join(',')).forEach((el: Element) => {
          (el as HTMLElement).style.setProperty('display', 'none', 'important');
          (el as HTMLElement).style.setProperty(
            'visibility',
            'hidden',
            'important'
          );
        });
      }
    } catch {}
  }

  /** Update hidden schedule fields for guided event schedule visibleIf. */
  private updateGuidedScheduleInferredMode(sender: SurveyModel): void {
    const data = (sender.data || {}) as Record<string, unknown>;
    const mode = inferGuidedScheduleInferredMode(data);
    const current = sender.getValue('schedule_inferred_mode');
    if (current !== mode) {
      sender.setValue('schedule_inferred_mode', mode);
    }

    const crossesDays = inferScheduleCrossesDays(data) ? 'true' : 'false';
    const currentCrosses = sender.getValue('schedule_crosses_days');
    if (currentCrosses !== crossesDays) {
      sender.setValue('schedule_crosses_days', crossesDays);
    }
  }

  private cacheCustomersFromData(data: unknown): void {
    const items = this.extractCoursePriceTemplateItems(data);
    for (const item of items) {
      const guid = item['guid'];
      if (!guid) continue;
      this.customerDetailsByGuid.set(
        String(guid),
        item as unknown as OrganizationCustomerDTO
      );
      const pg = item['price_group_id'];
      if (pg != null && pg !== '' && Number(pg) > 0) {
        this.customerPriceGroupByGuid.set(String(guid), Number(pg));
      } else {
        this.customerPriceGroupByGuid.delete(String(guid));
      }
    }
  }

  private async fetchCustomerByGuid(
    guid: string
  ): Promise<OrganizationCustomerDTO | null> {
    const cached = this.customerDetailsByGuid.get(guid);
    if (cached) {
      return cached;
    }

    const dataSource =
      this.componentDataSourceService.getDataSource('customers');
    if (!dataSource) {
      return null;
    }

    const params: LoadDataParams = {
      dataSourceKey: 'customers',
      filters: [
        { column: 'pageSize', value: 500, operator: 'equals' },
        { column: 'pageIndex', value: 0, operator: 'equals' },
      ],
      queryParams: { pageSize: 500, pageIndex: 0 },
    };
    this.mergeEventFormOrganizationIntoParams(params);

    try {
      const data = await firstValueFrom(
        dataSource.execute(params).pipe(catchError(() => of(null)))
      );
      if (data) {
        this.cacheCustomersFromData(data);
      }
      return this.customerDetailsByGuid.get(guid) ?? null;
    } catch {
      return null;
    }
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private formatCustomerAddress(
    customer: OrganizationCustomerDTO
  ): string | null {
    const line1 = customer.address?.trim();
    const line2 = [customer.postal_code, customer.city]
      .filter((part) => part != null && String(part).trim() !== '')
      .join(' ')
      .trim();
    const parts = [line1, line2 || null].filter(
      (part) => part != null && part !== ''
    );
    return parts.length > 0 ? parts.join(', ') : null;
  }

  private buildCustomerStamdataInnerHtml(
    customer: OrganizationCustomerDTO
  ): string {
    const rows: Array<[string, string | null | undefined]> = [
      ['Navn', customer.name],
      ['Att.', customer.att],
      ['Adresse', this.formatCustomerAddress(customer)],
      ['CVR', customer.cvr],
      ['EAN', customer.ean],
      ['E-mail', customer.email],
      ['Telefon', customer.phone],
      ['Mobil', customer.mobile],
      ['Prisgruppe', customer.price_group_label],
    ];
    const lines = rows
      .filter(([, value]) => value != null && String(value).trim() !== '')
      .map(
        ([label, value]) =>
          `<div style="display:flex;gap:8px;margin:3px 0;"><span style="min-width:88px;color:#5a6c7d;font-size:13px;">${this.escapeHtml(label)}:</span><span style="font-size:13px;color:#333;">${this.escapeHtml(String(value))}</span></div>`
      )
      .join('');
    const body =
      lines ||
      '<p style="margin:0;font-size:13px;color:#555;">Ingen stamdata tilgængelig.</p>';
    return `<p style="margin:0 0 8px;font-size:12px;font-weight:600;color:#1a237e;">Valgt kunde</p>${body}`;
  }

  private wrapCustomerStamdataHtml(inner: string): string {
    return `<div class="customer-guid-stamdata-panel" style="margin:8px 0 12px;padding:12px 14px;border:1px solid #e0e0e0;border-radius:8px;background:#f8fafc;">${inner}</div>`;
  }

  private setCustomerStamdataQuestionHtml(
    question: SurveyQuestion,
    html: string
  ): void {
    question['html'] = html;
    const root = document.getElementById(String(question.id));
    const panel = root?.querySelector('.customer-guid-stamdata-panel');
    if (panel) {
      panel.outerHTML = html;
    } else if (root) {
      root.innerHTML = html;
    }
  }

  private async refreshCustomerStamdataDisplay(
    sender: SurveyModel
  ): Promise<void> {
    const stamdataQuestion = sender.getQuestionByName('customer_guid_stamdata');
    if (!stamdataQuestion) {
      return;
    }

    const guidRaw = sender.getValue('customer_guid');
    const guid =
      guidRaw != null && guidRaw !== '' ? String(guidRaw).trim() : '';
    if (!guid) {
      this.setCustomerStamdataQuestionHtml(stamdataQuestion, '');
      return;
    }

    let customer = this.customerDetailsByGuid.get(guid);
    if (!customer) {
      customer = (await this.fetchCustomerByGuid(guid)) ?? undefined;
      if (customer) {
        this.customerDetailsByGuid.set(guid, customer);
        if (
          customer.price_group_id != null &&
          Number(customer.price_group_id) > 0
        ) {
          this.customerPriceGroupByGuid.set(
            String(guid),
            Number(customer.price_group_id)
          );
        }
      }
    }

    let inner: string;
    if (customer) {
      inner = this.buildCustomerStamdataInnerHtml(customer);
    } else {
      const customerQuestion = sender.getQuestionByName(
        'customer_guid'
      ) as QuestionDropdownModel | null;
      const fallbackName =
        customerQuestion?.selectedItem?.text ||
        (typeof customerQuestion?.getDisplayValue === 'function'
          ? String(customerQuestion.getDisplayValue(false) ?? '')
          : '');
      inner =
        fallbackName.trim() !== ''
          ? `<p style="margin:0 0 8px;font-size:12px;font-weight:600;color:#1a237e;">Valgt kunde</p><div style="display:flex;gap:8px;margin:3px 0;"><span style="min-width:88px;color:#5a6c7d;font-size:13px;">Navn:</span><span style="font-size:13px;color:#333;">${this.escapeHtml(fallbackName.trim())}</span></div><p style="margin:8px 0 0;font-size:12px;color:#777;">Yderligere stamdata kunne ikke hentes automatisk.</p>`
          : `<p style="margin:0;font-size:13px;color:#555;">Kunne ikke hente stamdata for valgt kunde.</p>`;
    }
    this.setCustomerStamdataQuestionHtml(
      stamdataQuestion,
      this.wrapCustomerStamdataHtml(inner)
    );
    this.cdr.detectChanges();
  }

  private cacheCoursePriceTemplatesFromData(data: unknown): void {
    const items = this.extractCoursePriceTemplateItems(data);
    this.coursePriceTemplateById.clear();
    for (const item of items) {
      const id = item['id'];
      if (id == null) {
        continue;
      }
      const pricesByGroupId = new Map<
        number,
        {
          fixed_course_amount: number | null;
          per_participant_amount: number | null;
        }
      >();
      const extraOptionsByGroupId = new Map<number, number[]>();
      const prices =
        (item as { prices?: Array<Record<string, unknown>> }).prices ?? [];
      for (const p of prices) {
        const gid = Number(p['price_group_id']);
        if (!Number.isFinite(gid) || gid <= 0) continue;
        const groupLabel = p['price_group_label'];
        if (groupLabel != null && String(groupLabel).trim() !== '') {
          this.priceGroupLabelById.set(gid, String(groupLabel));
          this.standardPriceGroupIdCache = null;
        }
        pricesByGroupId.set(gid, {
          fixed_course_amount:
            p['fixed_course_amount'] != null && p['fixed_course_amount'] !== ''
              ? Number(p['fixed_course_amount'])
              : null,
          per_participant_amount:
            p['per_participant_amount'] != null &&
            p['per_participant_amount'] !== ''
              ? Number(p['per_participant_amount'])
              : null,
        });
        const rowExtraIds = Array.isArray(p['extra_option_ids'])
          ? (p['extra_option_ids'] as unknown[])
              .map((x) => Number(x))
              .filter((n) => Number.isFinite(n) && n > 0)
          : [];
        if (rowExtraIds.length > 0) {
          extraOptionsByGroupId.set(gid, rowExtraIds);
        }
      }
      const legacyExtraOptionIds = Array.isArray(
        (item as { extra_option_ids?: unknown[] }).extra_option_ids
      )
        ? (item as { extra_option_ids: unknown[] }).extra_option_ids
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n > 0)
        : [];
      if (legacyExtraOptionIds.length > 0 && extraOptionsByGroupId.size === 0) {
        for (const gid of pricesByGroupId.keys()) {
          extraOptionsByGroupId.set(gid, [...legacyExtraOptionIds]);
        }
      }
      this.coursePriceTemplateById.set(String(id), {
        pricesByGroupId,
        extraOptionsByGroupId,
      });
    }
  }

  private templateExtraOptionIdsForGroup(
    template:
      | {
          extraOptionsByGroupId: Map<number, number[]>;
        }
      | undefined,
    groupId: number
  ): number[] {
    if (!template || groupId <= 0) {
      return [];
    }
    return template.extraOptionsByGroupId.get(groupId) ?? [];
  }

  private cachePriceGroupsFromData(data: unknown): void {
    const items = this.extractCoursePriceTemplateItems(data);
    for (const item of items) {
      const id = Number(item['id']);
      const label = String(item['label'] ?? '').trim();
      if (Number.isFinite(id) && id > 0 && label !== '') {
        this.priceGroupLabelById.set(id, label);
        this.standardPriceGroupIdCache = null;
      }
    }
  }

  private standardPriceGroupId(): number {
    if (this.standardPriceGroupIdCache != null) {
      return this.standardPriceGroupIdCache;
    }
    for (const [id, label] of this.priceGroupLabelById) {
      if (label.trim().toLowerCase() === 'standard') {
        this.standardPriceGroupIdCache = id;
        return id;
      }
    }
    this.standardPriceGroupIdCache = 0;
    return 0;
  }

  private templatePriceCell(
    templateId: number,
    groupId: number
  ):
    | {
        fixed_course_amount: number | null;
        per_participant_amount: number | null;
      }
    | undefined {
    if (groupId <= 0) {
      return undefined;
    }
    return this.coursePriceTemplateById
      .get(String(templateId))
      ?.pricesByGroupId.get(groupId);
  }

  private readTemplatePriceAmount(
    templateId: number,
    groupId: number,
    kind: 'fixed' | 'per'
  ): number | null {
    const cell = this.templatePriceCell(templateId, groupId);
    if (!cell) {
      return null;
    }
    const raw =
      kind === 'fixed' ? cell.fixed_course_amount : cell.per_participant_amount;
    if (raw == null || raw === ('' as unknown)) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  /** Prefer customer's price group, then Standard, then any group with a value. */
  private resolveTemplatePriceAmount(
    templateId: number,
    preferredGroupId: number,
    kind: 'fixed' | 'per'
  ): number | null {
    const template = this.coursePriceTemplateById.get(String(templateId));
    if (!template) {
      return null;
    }

    const groupOrder: number[] = [];
    if (preferredGroupId > 0) {
      groupOrder.push(preferredGroupId);
    }
    const standardId = this.standardPriceGroupId();
    if (standardId > 0 && !groupOrder.includes(standardId)) {
      groupOrder.push(standardId);
    }
    for (const gid of template.pricesByGroupId.keys()) {
      if (!groupOrder.includes(gid)) {
        groupOrder.push(gid);
      }
    }

    for (const gid of groupOrder) {
      const amount = this.readTemplatePriceAmount(templateId, gid, kind);
      if (amount != null) {
        return amount;
      }
    }
    return null;
  }

  /** Prefer customer's price group; fall back to Standard, then any group with a value. */
  private resolveTemplatePriceCell(
    templateId: number,
    preferredGroupId: number
  ):
    | {
        fixed_course_amount: number | null;
        per_participant_amount: number | null;
      }
    | undefined {
    const fixed = this.resolveTemplatePriceAmount(
      templateId,
      preferredGroupId,
      'fixed'
    );
    const per = this.resolveTemplatePriceAmount(
      templateId,
      preferredGroupId,
      'per'
    );
    if (fixed == null && per == null) {
      return undefined;
    }
    return {
      fixed_course_amount: fixed,
      per_participant_amount: per,
    };
  }

  private templateSupportsRevenueModel(
    templateId: number,
    preferredGroupId: number,
    kind: 'fixed' | 'per'
  ): boolean {
    return (
      this.resolveTemplatePriceAmount(templateId, preferredGroupId, kind) !=
      null
    );
  }

  private extractCoursePriceTemplateItems(
    data: unknown
  ): Array<Record<string, unknown>> {
    if (Array.isArray(data)) {
      return data as Array<Record<string, unknown>>;
    }
    if (data && typeof data === 'object') {
      const items = (data as { items?: unknown }).items;
      if (Array.isArray(items)) {
        return items as Array<Record<string, unknown>>;
      }
    }
    return [];
  }

  private normalizeTemplateIds(value: unknown): number[] {
    if (value == null || value === '') return [];
    const raw = Array.isArray(value) ? value : [value];
    return raw.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0);
  }

  private syncCourseRevenueModelChoiceUi(
    sender: SurveyModel,
    root: HTMLElement
  ): void {
    const revenueQuestion = sender.getQuestionByName('course_revenue_model');
    if (!revenueQuestion) {
      return;
    }

    const fixedAvailable = !!(revenueQuestion as any)
      .courseRevenueFixedAvailable;
    const perAvailable = !!(revenueQuestion as any).courseRevenuePerAvailable;
    const availability: Record<string, boolean> = {
      fixed_course: fixedAvailable,
      per_participant: perAvailable,
    };

    const items = root.querySelectorAll(
      '.sd-selectbase__item, .sd-item--allowhover, fieldset label'
    );
    items.forEach((item) => {
      const el = item as HTMLElement;
      const input =
        el.querySelector('input[type="radio"]') ??
        (el.matches('input[type="radio"]') ? el : null);
      const value =
        input?.getAttribute('value') ??
        el.querySelector('input[type="radio"]')?.getAttribute('value');
      if (!value || !(value in availability)) {
        return;
      }
      const allowed = availability[value];
      const row = (el.closest('.sd-selectbase__item') ??
        el.closest('label') ??
        el) as HTMLElement;
      row.classList.toggle('course-revenue-choice--unavailable', !allowed);
      if (
        input instanceof HTMLInputElement &&
        !revenueQuestion.readOnly &&
        !allowed
      ) {
        input.disabled = true;
      } else if (input instanceof HTMLInputElement && allowed) {
        input.disabled = false;
      }
    });

    root.classList.toggle(
      'course-revenue-model--readonly',
      !!revenueQuestion.readOnly
    );
  }

  private applyCoursePricingLogic(
    sender: SurveyModel,
    options: { name?: string; value?: unknown }
  ): void {
    const name = options.name ?? '';
    const pricingFields = new Set([
      'customer_guid',
      'course_price_type',
      'course_price_template_ids',
      'course_price_group_id',
      'course_revenue_model',
    ]);
    if (!pricingFields.has(name)) {
      return;
    }

    if (name === 'customer_guid') {
      const guid =
        options.value != null && options.value !== ''
          ? String(options.value)
          : '';
      if (guid) {
        const groupId = this.customerPriceGroupByGuid.get(guid);
        if (groupId != null && groupId > 0) {
          sender.setValue('course_price_group_id', groupId);
        }
      }
    }

    const templateIds = this.normalizeTemplateIds(
      sender.getValue('course_price_template_ids')
    );
    const groupId = Number(sender.getValue('course_price_group_id') ?? 0);
    let revenueModel = String(sender.getValue('course_revenue_model') ?? '');

    const fixedAvailable =
      templateIds.length > 0 &&
      templateIds.every((tid) =>
        this.templateSupportsRevenueModel(tid, groupId, 'fixed')
      );
    const perAvailable =
      templateIds.length > 0 &&
      templateIds.every((tid) =>
        this.templateSupportsRevenueModel(tid, groupId, 'per')
      );

    const revenueQuestion = sender.getQuestionByName('course_revenue_model');
    const allRevenueChoices = [
      { value: 'fixed_course', text: 'Fastsat beløb for kursus' },
      { value: 'per_participant', text: 'Per deltagelse per bruger' },
    ];
    const availableModels: string[] = [];
    if (fixedAvailable) availableModels.push('fixed_course');
    if (perAvailable) availableModels.push('per_participant');

    if (revenueQuestion) {
      revenueQuestion['choices'] = allRevenueChoices;
      (revenueQuestion as any).courseRevenueFixedAvailable = fixedAvailable;
      (revenueQuestion as any).courseRevenuePerAvailable = perAvailable;

      const hasTemplateSelection = templateIds.length > 0;
      const singleOptionMode =
        hasTemplateSelection && availableModels.length === 1;
      const bothAvailable = fixedAvailable && perAvailable;

      revenueQuestion.readOnly = singleOptionMode;
      if (bothAvailable) {
        revenueQuestion.readOnly = false;
      }

      if (typeof revenueQuestion['updateElement'] === 'function') {
        revenueQuestion['updateElement']();
      }
    }

    if (!revenueModel && availableModels.length === 1) {
      revenueModel = availableModels[0];
      sender.setValue('course_revenue_model', revenueModel);
    } else if (revenueModel && !availableModels.includes(revenueModel)) {
      revenueModel = availableModels[0] ?? '';
      sender.setValue('course_revenue_model', revenueModel || null);
    } else if (
      availableModels.length === 1 &&
      revenueModel !== availableModels[0]
    ) {
      revenueModel = availableModels[0];
      sender.setValue('course_revenue_model', revenueModel);
    }

    if (revenueQuestion?.id) {
      setTimeout(() => {
        const root = document.getElementById(String(revenueQuestion.id));
        if (root) {
          this.syncCourseRevenueModelChoiceUi(sender, root);
        }
      }, 0);
    }

    if (templateIds.length > 0 && revenueModel) {
      sender.setValue('course_price_type', 'revenue_generating');
      let sum = 0;
      for (const tid of templateIds) {
        const cell = this.resolveTemplatePriceCell(tid, groupId);
        if (!cell) continue;
        if (
          revenueModel === 'fixed_course' &&
          cell.fixed_course_amount != null
        ) {
          sum += Number(cell.fixed_course_amount);
        } else if (
          revenueModel === 'per_participant' &&
          cell.per_participant_amount != null
        ) {
          sum += Number(cell.per_participant_amount);
        }
      }
      sender.setValue('event_price', sum);
    }

    if (
      name === 'course_price_template_ids' ||
      name === 'course_price_group_id' ||
      name === 'customer_guid'
    ) {
      const existingExtras = Array.isArray(
        sender.getValue('course_price_extras')
      )
        ? (sender.getValue('course_price_extras') as Array<{
            extra_option_id?: number;
            quantity?: number;
          }>)
        : [];
      const qtyByExtra = new Map<number, number>();
      for (const row of existingExtras) {
        const eid = Number(row?.extra_option_id);
        if (Number.isFinite(eid) && eid > 0) {
          qtyByExtra.set(eid, Number(row?.quantity ?? 0));
        }
      }
      const effectiveGroupId =
        groupId > 0 ? groupId : this.standardPriceGroupId();
      const unionIds = new Set<number>();
      for (const tid of templateIds) {
        const template = this.coursePriceTemplateById.get(String(tid));
        if (!template) continue;
        for (const eid of this.templateExtraOptionIdsForGroup(
          template,
          effectiveGroupId
        )) {
          unionIds.add(eid);
        }
      }
      const merged = Array.from(unionIds).map((extraId) => ({
        extra_option_id: extraId,
        quantity: qtyByExtra.has(extraId) ? qtyByExtra.get(extraId)! : 0,
      }));
      sender.setValue('course_price_extras', merged);
    }
  }

  /**
   * Event add/edit forms: forward the JSON `cssClass` of questions and panels to the rendered
   * root element so the layout SCSS can target cards, segmented toggles etc. by class.
   */
  private setupEventFormCssClassHooks(survey: SurveyModel): void {
    if (!this.isEventAddEditBundledForm) {
      return;
    }
    survey.onUpdateQuestionCssClasses.add((_sender, options) => {
      const custom = (options.question as { cssClass?: string }).cssClass;
      if (custom && options.cssClasses) {
        options.cssClasses.mainRoot = `${options.cssClasses.mainRoot ?? ''} ${custom}`.trim();
      }
    });
    survey.onUpdatePanelCssClasses.add((_sender, options) => {
      const custom = (options.panel as { cssClass?: string }).cssClass;
      if (!custom || !options.cssClasses) {
        return;
      }
      const panelCss = options.cssClasses.panel ?? options.cssClasses;
      panelCss.container = `${panelCss.container ?? ''} ${custom}`.trim();
    });
  }

  /** Hide «Side N» chrome and skip modul-side when serieplanlægger is active. */
  private applyEventSeriesPlannerSurveyChrome(survey: SurveyModel): void {
    const multi =
      String(survey.getValue('schedule_mode') || '') === 'multi_dates';
    const host = this.el?.nativeElement as HTMLElement | undefined;
    if (multi) {
      survey.showPageTitles = false;
      host?.classList.add('duna-series-planner-mode');
    } else {
      survey.showPageTitles = this.surveyShowPageTitlesDefault;
      host?.classList.remove('duna-series-planner-mode');
    }
    this.runSurveyVisibilityExpressions();
    this.cdr.markForCheck();
  }

  /** Tear down planner when leaving its page so SurveyJS can mount it again on return. */
  private syncEventSeriesPlannerWithCurrentPage(sender: SurveyModel): void {
    const planner = sender.getQuestionByName('event_series_planner');
    if (!planner) {
      return;
    }
    const onCurrentPage =
      sender.currentPage?.getQuestionByName('event_series_planner') != null;
    if (!onCurrentPage || !planner.isVisible) {
      teardownEventSeriesPlanner(planner);
    }
  }

  /** Auto-copy end date from start; keep schedule_inferred_mode in sync. */
  private handleGuidedScheduleValueChange(
    sender: SurveyModel,
    options: { name?: string; value?: unknown }
  ): void {
    if (isPlannerSyncingToSurvey(sender)) {
      return;
    }
    if (options.name === 'start_date' && options.value) {
      if (String(sender.getValue('schedule_mode') || '') === 'multi_dates') {
        // Serieplanlæggeren sætter start/slut — undgå kædereaktioner i SurveyJS.
      } else {
        const start = String(options.value);
        const end = String(sender.getValue('end_date') || '');
        const syncedEnd = syncEndDateFromStart(start, end);
        if (syncedEnd !== end) {
          sender.setValue('end_date', syncedEnd);
        }
      }
    }
    if (options.name === 'schedule_mode') {
      const mode = String(options.value || '');
      if (mode === 'multi_dates') {
        syncPeriodScheduleToSeriesFields(sender);
        teardownEventMaterialPlanner(
          sender.getQuestionByName('event_material_planner')
        );
      } else {
        teardownEventSeriesPlanner(
          sender.getQuestionByName('event_series_planner')
        );
        syncSeriesFieldsToPeriodForm(sender);
      }
      this.applyEventSeriesPlannerSurveyChrome(sender);
      this.updatePageVisibility(sender);
      this.syncSurveyNavigationModel();
      this.ensureCurrentPageIsFirstVisible();
    }
    if (
      options.name === 'start_date' ||
      options.name === 'end_date' ||
      options.name === 'additional_dates' ||
      options.name === 'schedule_mode' ||
      options.name === 'series_planner_active' ||
      options.name === 'series_dates' ||
      options.name === 'series_planning_mode'
    ) {
      this.updateGuidedScheduleInferredMode(sender);
    }
  }

  private seedAdditionalDatePanelDefaults(
    sender: SurveyModel,
    panelIndex: number,
    panel: {
      getQuestionByName?: (name: string) => { value: unknown } | null;
    }
  ): void {
    const data = (sender.data || {}) as Record<string, unknown>;
    const prevCatering = previousCateringBeforeAdditionalIndex(data, panelIndex);
    const set = (name: string, value: unknown): void => {
      const q = panel.getQuestionByName?.(name);
      if (q) {
        q.value = value;
      }
    };
    set('same_as_previous', true);
    set('same_catering_as_previous', true);
    if (prevCatering) {
      set('sub_event_catering', prevCatering);
    }
    const rows = [
      ...(((sender.getValue('additional_dates') as unknown[]) || []) as Record<
        string,
        unknown
      >[]),
    ];
    if (rows[panelIndex] && typeof rows[panelIndex] === 'object') {
      rows[panelIndex] = {
        ...rows[panelIndex],
        same_as_previous: true,
        same_catering_as_previous: true,
        ...(prevCatering ? { sub_event_catering: prevCatering } : {}),
      };
      sender.setValue('additional_dates', rows);
    }
  }

  /** Keep the survey editable after async submit failure (SurveyJS `state` is read-only in newer versions). */
  private restoreSurveyAfterFailedSubmit(sender: SurveyModel): void {
    try {
      (sender as unknown as { isCompleted?: boolean }).isCompleted = false;
    } catch {
      // SurveyJS may expose isCompleted as read-only at runtime
    }
  }

  private wireCateringWishesButton(
    sender: SurveyModel,
    root: HTMLElement
  ): void {
    const button = root.querySelector(
      '#open-catering-wishes-btn'
    ) as HTMLButtonElement | null;
    if (!button || button.dataset['wired'] === 'true') {
      return;
    }
    button.dataset['wired'] = 'true';

    const hint = root.querySelector(
      '#open-catering-wishes-hint'
    ) as HTMLElement | null;

    const refreshState = (): void => {
      const catering = String(sender.getValue('catering') || '');
      const isLocation = catering === 'lokation';
      button.disabled = !isLocation;
      if (hint) {
        hint.style.display = isLocation ? 'block' : 'none';
      }
    };
    refreshState();
    sender.onValueChanged.add((_s, options) => {
      if (options.name === 'catering') {
        refreshState();
      }
    });

    button.addEventListener('click', () => {
      const row = this.resolveCateringWishesRow(sender);
      if (row) {
        this.openCateringWishesDialog(row);
        return;
      }
      this.openCateringAfterSave = true;
      if (hint) {
        hint.textContent =
          'Opretter begivenheden — forplejningsopsætning åbnes om et øjeblik …';
      }
      const valid = sender.validate();
      if (!valid) {
        this.openCateringAfterSave = false;
        this.showFormError(
          'Udfyld alle obligatoriske felter på side 1–3, før du opretter begivenheden via forplejningsknappen.'
        );
        return;
      }
      try {
        sender.tryComplete();
      } catch {
        this.openCateringAfterSave = false;
      }
    });
  }

  private parsePositiveEventId(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
    const n = parseInt(String(value ?? ''), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  private resolveCateringWishesRow(
    sender: SurveyModel,
    createResponse?: unknown
  ): CateringWishesDialogRow | null {
    const data = sender.data as Record<string, unknown>;
    let guid = typeof data['event_guid'] === 'string' ? data['event_guid'] : '';
    let eventId = this.parsePositiveEventId(data['event_id']);

    if (createResponse) {
      let payload = createResponse as Record<string, unknown>;
      if (payload['data'] != null && typeof payload['data'] === 'object') {
        payload = payload['data'] as Record<string, unknown>;
      }
      guid =
        (typeof payload['primary_event_guid'] === 'string'
          ? payload['primary_event_guid']
          : '') ||
        (typeof payload['guid'] === 'string' ? payload['guid'] : '') ||
        (Array.isArray(payload['event_guids'])
          ? String((payload['event_guids'] as string[])[0] || '')
          : '') ||
        guid;
      if (eventId <= 0) {
        const ids = payload['event_ids'];
        if (Array.isArray(ids) && ids.length > 0) {
          eventId = this.parsePositiveEventId(ids[0]);
        } else {
          eventId = this.parsePositiveEventId(payload['event_id']);
        }
      }
    }

    if (!guid) {
      const router = this.injector.get(Router);
      const match = router.url.match(
        /\/edit-event\/([a-fA-F0-9-]{36})/
      );
      if (match) {
        guid = match[1];
      }
    }

    if (!guid || !Number.isFinite(eventId) || eventId <= 0) {
      return null;
    }

    const eventName =
      typeof data['short_description'] === 'string'
        ? data['short_description']
        : typeof data['event_name'] === 'string'
        ? data['event_name']
        : guid;

    return {
      event_id: eventId,
      guid,
      event_name: eventName,
      start_date:
        typeof data['start_date'] === 'string' ? data['start_date'] : null,
      end_date: typeof data['end_date'] === 'string' ? data['end_date'] : null,
      location:
        typeof data['location'] === 'string' ? data['location'] : null,
    };
  }

  private openCateringWishesDialog(row: CateringWishesDialogRow): void {
    const dialog = this.injector.get(MatDialog);
    dialog.open(DunaCateringPreferencesDialogComponent, {
      ...DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
      data: { row },
    });
  }

  private handlePostSubmitCatering(
    sender: SurveyModel,
    response: unknown
  ): boolean {
    if (!this.openCateringAfterSave) {
      return false;
    }
    this.openCateringAfterSave = false;
    if (String(sender.getValue('catering') || '') !== 'lokation') {
      return false;
    }
    const row = this.resolveCateringWishesRow(sender, response);
    if (!row) {
      return false;
    }
    sender.setValue('event_id', row.event_id);
    sender.setValue('event_guid', row.guid);
    this.openCateringWishesDialog(row);
    return true;
  }

  private applyParticipatePathModuleWarningHtml(
    formData: Record<string, unknown>
  ): void {
    if (this.originalSurveyJSON?.dataSource !== 'event_participate_form_data') {
      return;
    }
    const html = String(formData['path_module_completed_warning_html'] ?? '');
    const names = [
      'path_module_completed_warning',
      'path_module_completed_warning_disabled',
    ];
    for (const name of names) {
      const question = this.surveyModel?.getQuestionByName(name) as
        | (SurveyQuestion & { html?: string })
        | null;
      if (question) {
        question.html = html;
      }
    }
  }

  private parsePathModuleFutureSignups(
    formData: Record<string, unknown>
  ): PathModuleFutureRegistration[] {
    const raw = formData['path_module_future_signups'];
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter(
      (item): item is PathModuleFutureRegistration =>
        item != null &&
        typeof item === 'object' &&
        typeof (item as PathModuleFutureRegistration).moduleName === 'string' &&
        typeof (item as PathModuleFutureRegistration).pathName === 'string' &&
        typeof (item as PathModuleFutureRegistration).eventName === 'string' &&
        typeof (item as PathModuleFutureRegistration).eventDate === 'string'
    );
  }

  private maybeOpenPathModuleFutureSignupModal(
    formData: Record<string, unknown>
  ): void {
    if (this.originalSurveyJSON?.dataSource !== 'event_participate_form_data') {
      return;
    }
    if (!this.isAuthenticated || this.participatePathModuleFutureModalOpened) {
      return;
    }
    const items = this.parsePathModuleFutureSignups(formData);
    if (items.length === 0) {
      return;
    }
    this.participatePathModuleFutureModalOpened = true;
    setTimeout(() => {
      const dialog = this.injector.get(MatDialog);
      dialog.open<
        ParticipatePathModuleFutureDialogComponent,
        ParticipatePathModuleFutureDialogData
      >(ParticipatePathModuleFutureDialogComponent, {
        data: { items },
        width: 'min(36rem, 95vw)',
      });
    }, 400);
  }

  private maybeOpenCateringWishesFromLoadedForm(
    formData: Record<string, unknown>
  ): void {
    if (formData['open_catering_wishes'] !== 'true') {
      return;
    }
    if (String(formData['catering'] || '') !== 'lokation') {
      return;
    }
    const row = this.resolveCateringWishesRow(
      { data: formData } as SurveyModel
    );
    if (!row) {
      return;
    }
    setTimeout(() => this.openCateringWishesDialog(row), 400);
  }

  private wireUnlinkEventFromGroupButton(
    sender: SurveyModel,
    root: HTMLElement
  ): void {
    const button = root.querySelector(
      '#unlink-event-from-group-btn'
    ) as HTMLButtonElement | null;
    if (!button || button.dataset['wired'] === 'true') {
      return;
    }
    button.dataset['wired'] = 'true';

    button.addEventListener('click', () => {
      const confirmed = window.confirm(
        'Vil du gøre dette event selvstændigt?\n\n' +
          'Det vil ikke længere dele beskrivelse og moduler med de andre events i gruppen. ' +
          'Eventet slettes ikke.'
      );
      if (!confirmed) {
        return;
      }

      button.disabled = true;
      const statusEl = root.querySelector(
        '#unlink-event-from-group-status'
      ) as HTMLElement | null;
      if (statusEl) {
        statusEl.style.display = 'block';
        statusEl.textContent = 'Løsriver event fra gruppen…';
      }

      unlinkEventFromGroup(this.componentDataDestService)
        .pipe(take(1))
        .subscribe({
          next: () => {
            sender.setValue('linked_event_group_id', null);
            sender.setValue('event_duration_type', 'single_day');
            if (statusEl) {
              statusEl.textContent = 'Eventet er nu selvstændigt.';
              statusEl.style.color = '#2e7d32';
            }
            button.style.display = 'none';
            sender.runExpressions();
          },
          error: (error: Error) => {
            button.disabled = false;
            if (statusEl) {
              statusEl.textContent =
                error?.message || 'Kunne ikke løsrive eventet.';
              statusEl.style.color = '#c62828';
            }
            this.showFormError(
              error?.message || 'Kunne ikke løsrive eventet fra gruppen'
            );
          },
        });
    });
  }

  private wireCreateCustomerButton(
    sender: SurveyModel,
    question: SurveyQuestion,
    root: HTMLElement
  ): void {
    if (root.querySelector('.customer-create-button')) {
      return;
    }

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'customer-create-button-container';
    buttonContainer.style.cssText = 'margin-top: 6px;';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'customer-create-button';
    button.textContent = '+ Opret ny kunde';
    button.style.cssText = `
      background: none;
      border: none;
      color: #1976d2;
      cursor: pointer;
      font-size: 13px;
      padding: 0;
      text-decoration: underline;
    `;

    button.addEventListener('click', () => {
      const searchText = this.searchTextMap.get(question.name)?.trim();
      const dialog = this.injector.get(MatDialog);
      const dialogRef = dialog.open<
        DunaCustomersCreateDialogComponent,
        DunaCustomersCreateDialogData,
        OrganizationCustomerDTO | false
      >(DunaCustomersCreateDialogComponent, {
        width: '520px',
        data: searchText ? { initialName: searchText } : undefined,
      });

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((created) => {
          if (!created) {
            return;
          }

          const customer = created;
          if (
            customer.price_group_id != null &&
            Number(customer.price_group_id) > 0
          ) {
            this.customerPriceGroupByGuid.set(
              String(customer.guid),
              Number(customer.price_group_id)
            );
          }
          this.customerDetailsByGuid.set(String(customer.guid), customer);

          this.searchTextMap.delete(question.name);
          this.invalidateDataSourceCache('customers');
          this.componentDataSourceService.refreshData();

          void this.loadQuestionData(question).then(() => {
            this.ensureCustomerDropdownChoice(question, customer);
            sender.setValue(question.name, customer.guid);
            this.applyCoursePricingLogic(sender, {
              name: 'customer_guid',
              value: customer.guid,
            });
            sender.runExpressions();
            void this.refreshCustomerStamdataDisplay(sender);

            setTimeout(() => {
              const inputElement = document.querySelector(
                `#${question.id} input.sv-dropdown__filter-string`
              ) as HTMLInputElement | null;
              if (inputElement?.value) {
                inputElement.value = '';
                inputElement.dispatchEvent(
                  new Event('input', { bubbles: true })
                );
              }
            }, 50);
          });
        });
    });

    buttonContainer.appendChild(button);
    root.appendChild(buttonContainer);
  }

  private wireDeleteEventButton(sender: SurveyModel, root: HTMLElement): void {
    const button = root.querySelector(
      '#delete-event-btn'
    ) as HTMLButtonElement | null;
    if (!button || button.dataset['wired'] === 'true') {
      return;
    }
    button.dataset['wired'] = 'true';

    const linkedGroupId = sender.getValue('linked_event_group_id');
    const isSeriesEvent =
      linkedGroupId != null && String(linkedGroupId).trim() !== '';

    const seriesHint = root.querySelector(
      '#delete-event-series-hint'
    ) as HTMLElement | null;
    const singleHint = root.querySelector(
      '#delete-event-single-hint'
    ) as HTMLElement | null;
    if (seriesHint) {
      seriesHint.style.display = isSeriesEvent ? 'block' : 'none';
    }
    if (singleHint) {
      singleHint.style.display = isSeriesEvent ? 'none' : 'block';
    }

    button.addEventListener('click', () => {
      const dialog = this.injector.get(MatDialog);
      const dialogRef = dialog.open(EventDeleteConfirmDialogComponent, {
        width: '480px',
        data: { isSeriesEvent } satisfies EventDeleteConfirmDialogData,
      });

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((confirmed: boolean) => {
          if (!confirmed) {
            return;
          }

          button.disabled = true;
          const statusEl = root.querySelector(
            '#delete-event-status'
          ) as HTMLElement | null;
          if (statusEl) {
            statusEl.style.display = 'block';
            statusEl.textContent = 'Sletter begivenhed…';
            statusEl.style.color = '#555';
          }

          deleteEvent(this.componentDataDestService)
            .pipe(take(1))
            .subscribe({
              next: (response) => {
                const store = this.injector.get(Store<AppState>);
                store.dispatch(
                  EventsActions.loadCalendarEvents({ queryParams: {} })
                );
                this.componentDataSourceService.refreshData();

                const deletedCount = response?.deleted_count ?? 1;
                if (statusEl) {
                  statusEl.textContent =
                    deletedCount > 1
                      ? `${deletedCount} begivenheder i serien er slettet.`
                      : 'Begivenheden er slettet.';
                  statusEl.style.color = '#2e7d32';
                }

                const router = this.injector.get(Router);
                setTimeout(() => {
                  router.navigate(['/']);
                }, 600);
              },
              error: (error: Error) => {
                button.disabled = false;
                if (statusEl) {
                  statusEl.textContent =
                    error?.message || 'Kunne ikke slette begivenheden.';
                  statusEl.style.color = '#c62828';
                }
                this.showFormError(
                  error?.message || 'Kunne ikke slette begivenheden'
                );
              },
            });
        });
    });
  }

  // Modify loadFormData method to fix datetime values
  async loadFormData() {
    if (!this.surveyModel) return;

    // Check if the survey has a data source specified
    const dataSourceName =
      this.surveyModel['dataSource'] ||
      this.surveyModel.getPropertyValue('dataSource') ||
      this.surveyModel['jsonObj']?.dataSource ||
      this.getSettingValue('formDataSource');

    const keyField =
      this.surveyModel['dataSourceKeyField'] ||
      this.surveyModel.getPropertyValue('dataSourceKeyField') ||
      this.surveyModel['jsonObj']?.dataSourceKeyField ||
      this.getSettingValue('formDataSourceKeyField');

    const valueField =
      this.surveyModel['dataSourceValueField'] ||
      this.surveyModel.getPropertyValue('dataSourceValueField') ||
      this.surveyModel['jsonObj']?.dataSourceValueField ||
      this.getSettingValue('formDataSourceValueField');

    let formDataToApply: any = null;

    // Step 1: Load the form data from the data source if specified
    if (dataSourceName) {

      const dataSource =
        this.componentDataSourceService.getDataSource(dataSourceName);
      if (dataSource) {
        try {
          // Format the data using LoadDataParams interface
          const params: LoadDataParams = {
            dataSourceKey: dataSourceName,
            filters: [
              { column: 'pageSize', value: 1, operator: 'equals' },
              { column: 'pageIndex', value: 0, operator: 'equals' },
            ],
            queryParams: {
              pageSize: 1,
              pageIndex: 0,
            },
          };

          // Load data from the data source
          const result = await firstValueFrom(dataSource.execute(params));

          // Handle different data source response formats
          if (
            dataSourceName === 'event_form_data' ||
            dataSourceName === 'event_participate_form_data'
          ) {
            // Form data sources return a plain object (e.g. catering, catering_choice)
            formDataToApply = this.fixDateTimeValues(result);
          } else if (result && result.data && result.data.length > 0) {
            const formData = result.data[0];

            // If valueField is specified, use it to extract the form data
            if (valueField && formData[valueField]) {
              try {
                // Try to parse the value field as JSON if it's a string
                formDataToApply =
                  typeof formData[valueField] === 'string'
                    ? JSON.parse(formData[valueField])
                    : formData[valueField];

                formDataToApply = this.fixDateTimeValues(formDataToApply);
              } catch (error) {
                console.error(
                  'Error parsing form data from valueField:',
                  error
                );
              }
            } else {
              // Use the entire form data object
              formDataToApply = this.fixDateTimeValues(formData);
            }
          } else {
          }
        } catch (error: any) {
          console.error('Error loading form data:', error);

          // Check if it's a 403 Forbidden (login required)
          if (error.status === 403 && error.error?.requires_login) {
            // Don't show error for private/exercise events, just skip loading form data
          } else {
            console.error('Unexpected error loading form data:', error);
          }
        }
      } else {
        console.error(`Data source '${dataSourceName}' not found`);
      }
    }

    // Check modal data too (higher priority)
    const modalData = this.getInput('modalData');
    if (modalData && Object.keys(modalData).length > 0) {
      formDataToApply = this.fixDateTimeValues(modalData.data || modalData);
    } else if (modalData && Object.keys(modalData).length === 0) {
    }

    if (!formDataToApply) {
    }

    // Step 2: Preload all data sources for questions
    // - First, gather all select-type questions that have data sources
    const questions = this.surveyModel.getAllQuestions();

    // Group questions by data source dependency hierarchy
    const independentQuestions: SurveyQuestion[] = [];
    const dependentQuestions: SurveyQuestion[] = [];

    for (const question of questions) {
      const onChangeUpdateQuestionName =
        question['onChangeUpdateQuestion'] ||
        question.getPropertyValue('onChangeUpdateQuestion') ||
        question['jsonObj']?.onChangeUpdateQuestion;

      const dataSource =
        question['dataSource'] ||
        question.getPropertyValue('dataSource') ||
        question['jsonObj']?.dataSource;

      // Only consider questions that might need data loading
      if (dataSource) {
        if (!onChangeUpdateQuestionName) {
          independentQuestions.push(question);
        } else {
          dependentQuestions.push(question);
        }
      }
    }

    // Build dependency graph to properly order dependent questions
    const dependencyGraph = new Map<string, Set<string>>();
    const questionsByName = new Map<string, SurveyQuestion>();

    // Initialize the graph with all questions
    for (const question of [...independentQuestions, ...dependentQuestions]) {
      dependencyGraph.set(question.name, new Set<string>());
      questionsByName.set(question.name, question);
    }

    // Build the dependency relationships
    for (const question of questions) {
      const updateTarget =
        question['onChangeUpdateQuestion'] ||
        question.getPropertyValue('onChangeUpdateQuestion') ||
        question['jsonObj']?.onChangeUpdateQuestion;

      if (updateTarget && questionsByName.has(updateTarget)) {
        // question affects updateTarget
        if (dependencyGraph.has(updateTarget)) {
          dependencyGraph.get(updateTarget)?.add(question.name);
        }
      }
    }

    // Function to get processing order using topological sort
    const getProcessingOrder = (): string[] => {
      const result: string[] = [];
      const visited = new Set<string>();
      const temp = new Set<string>();

      const visit = (nodeName: string) => {
        if (temp.has(nodeName)) {
          console.warn(`Cyclic dependency detected involving ${nodeName}`);
          return;
        }
        if (visited.has(nodeName)) return;

        temp.add(nodeName);

        // Visit all dependencies first
        const dependencies = dependencyGraph.get(nodeName) || new Set<string>();
        for (const dependency of dependencies) {
          visit(dependency);
        }

        temp.delete(nodeName);
        visited.add(nodeName);
        result.push(nodeName);
      };

      // Process all nodes
      for (const nodeName of dependencyGraph.keys()) {
        if (!visited.has(nodeName)) {
          visit(nodeName);
        }
      }

      return result;
    };

    // Get the proper processing order
    const processingOrder = getProcessingOrder();

    // Step 3: Load data for all independent questions first
    for (const question of independentQuestions) {
      await this.loadQuestionData(question);
    }

    // Step 4: Apply the saved form data if available
    if (formDataToApply) {

      // Add specific debug logging for start_date and end_date
      if (formDataToApply.start_date) {
      }
      if (formDataToApply.end_date) {
      }

      // Clear adjustment tracking before processing form data
      this.adjustedDatetimes.clear();

      // Pre-process all datetime values in the data to ensure proper conversion
      const processedData = this.fixDateTimeValues(formDataToApply);

      // Log the processed values
      if (processedData.start_date) {
      }
      if (processedData.end_date) {
      }

      this.surveyModel.data = processedData;
      this.updateGuidedScheduleInferredMode(this.surveyModel);
      this.applyParticipatePathModuleWarningHtml(processedData);
      this.maybeOpenPathModuleFutureSignupModal(processedData);
      this.maybeOpenCateringWishesFromLoadedForm(processedData);
    }

    this.applyDateTimeLocalDefaultsToAllQuestions();

    // Step 5: Apply visibility data sources for pages and questions (visibilityDataSource)
    this.visibilityFromDataSource.clear();
    this.noVisiblePagesHint = null;
    const visibilityParams: LoadDataParams = {
      dataSourceKey: '',
      queryParams: {
        ...(formDataToApply ?? {}),
        ...(this.surveyModel?.data ?? {}),
      },
    };
    this.mergeEventFormOrganizationIntoParams(visibilityParams);
    const jsonPages = (this.originalSurveyJSON?.pages || []) as Array<
      Record<string, unknown>
    >;
    // Run visibility data source for pages that have visibilityDataSource in the template OR on the survey model (in case merge dropped it from JSON).
    const templatePageNamesWithVisibilityDs: string[] = [];
    jsonPages.forEach((p: Record<string, unknown>) => {
      const ds = p['visibilityDataSource'];
      if (
        typeof ds === 'string' &&
        p['name'] &&
        typeof p['name'] === 'string'
      ) {
        templatePageNamesWithVisibilityDs.push(p['name'] as string);
      }
    });
    this.surveyModel.pages.forEach((p: any) => {
      const ds = p.getPropertyValue?.('visibilityDataSource');
      if (
        typeof ds === 'string' &&
        p.name &&
        !templatePageNamesWithVisibilityDs.includes(p.name)
      ) {
        templatePageNamesWithVisibilityDs.push(p.name);
      }
    });
    for (const templatePageName of templatePageNamesWithVisibilityDs) {
      const page = this.surveyModel.pages.find(
        (p: any) => p.name === templatePageName
      );
      if (!page) continue;
      const jsonPage = jsonPages.find(
        (p: Record<string, unknown>) => p['name'] === templatePageName
      );
      const visibilityDs = (jsonPage?.['visibilityDataSource'] ??
        page.getPropertyValue?.('visibilityDataSource')) as string | undefined;
      if (!visibilityDs || typeof visibilityDs !== 'string') continue;
      try {
        const obs = this.componentDataSourceService.executeDataSource(
          visibilityDs,
          visibilityParams
        );
        if (obs) {
          const result = await firstValueFrom(
            obs.pipe(catchError(() => of({ visible: true })))
          );
          const visible = this.normalizeVisibilityResult(result);
          const blockHint =
            DynamicFormComponent.visibilityResultMessage(result);
          if (!visible && blockHint) {
            this.noVisiblePagesHint = blockHint;
          }
          // Targeted diagnostics for page visibilityDataSource issues (e.g. event participate form)
          if (
            page.name === 'page5_disabled' ||
            visibilityDs === 'event_disallow_register_others'
          ) {
          }
          const visibleIfValue = this.pageVisibleIfAfterDataSource(
            page.name,
            visible
          );
          if (typeof (page as any).setPropertyValue === 'function') {
            (page as any).setPropertyValue('visibleIf', visibleIfValue);
            (page as any).setPropertyValue('visible', visible);
            (page as any).setPropertyValue('_visibilityDsResult', visible); // persist so FORCE-CHECK/updatePageVisibility can read even if map is stale
          } else {
            (page as any).visibleIf = visibleIfValue;
            page.visible = visible;
            (page as any)._visibilityDsResult = visible;
          }
          this.visibilityFromDataSource.set(`page:${page.name}`, visible);
          // Propagate result to auth-complement companion pages only for guest-only (auth 2) sources.
          // When a not-logged-in page's data source returns false, also hide logged-in-only (auth 1)
          // pages that have no own visibilityDataSource (wrong DS placement in stored JSON).
          // Do NOT propagate from auth 1 → hide auth 2: if e.g. page5 (logged-in, register others) is
          // false, that must not add visibilityFromDataSource=false for page1/page4 (guest registration).
          if (!visible) {
            const srcAuthVis = Number(
              page.getPropertyValue?.('authVisibility') ?? -1
            );
            const complementAuthVis = srcAuthVis === 2 ? 1 : -1;
            if (complementAuthVis !== -1) {
              this.surveyModel.pages.forEach((companion: any) => {
                if (companion.name === page.name) return;
                if (this.visibilityFromDataSource.has(`page:${companion.name}`))
                  return;
                const cAuthVis = Number(
                  companion.getPropertyValue?.('authVisibility') ?? -1
                );
                if (cAuthVis !== complementAuthVis) return;
                const visibleIfVal =
                  DynamicFormComponent.VISIBLEIF_ALWAYS_FALSE;
                if (typeof companion.setPropertyValue === 'function') {
                  companion.setPropertyValue('visibleIf', visibleIfVal);
                  companion.setPropertyValue('visible', false);
                } else {
                  companion.visibleIf = visibleIfVal;
                  companion.visible = false;
                }
                this.visibilityFromDataSource.set(
                  `page:${companion.name}`,
                  false
                );
              });
            }
          }
        } else {
          console.warn(
            '[visibilityDataSource] Page',
            page.name,
            '→ data source returned no observable'
          );
        }
      } catch (e) {
        console.warn(
          `[visibilityDataSource] Page "${page.name}" source "${visibilityDs}" failed:`,
          e
        );
      }
    }
    // Sync noVisiblePages and currentPage so we never show survey with no page or with an invisible current page
    const visiblePages = this.surveyModel.visiblePages;
    if (visiblePages.length === 0 && this.surveyModel.pages.length > 0) {
      this.noVisiblePages = true;
    } else {
      this.noVisiblePages = false;
      this.noVisiblePagesHint = null;
      if (
        visiblePages.length > 0 &&
        (!this.surveyModel.currentPage ||
          !visiblePages.includes(this.surveyModel.currentPage))
      ) {
        try {
          const firstVisiblePage = visiblePages[0];
          const surveyAny = this.surveyModel as unknown as Record<
            string,
            unknown
          >;
          if (typeof surveyAny['setPropertyValue'] === 'function') {
            (
              surveyAny['setPropertyValue'] as (
                name: string,
                val: unknown
              ) => void
            )('currentPage', firstVisiblePage);
          } else {
            this.surveyModel.currentPage = firstVisiblePage;
          }
          const idx = this.surveyModel.pages.indexOf(firstVisiblePage);
          if (idx >= 0) {
            this.surveyModel.currentPageNo = idx;
          }
        } catch (_e) {
          // ignore
        }
      }
    }
    this.cdr.detectChanges();
    // On refresh, visibility can be overwritten by FORCE-CHECK or SurveyJS; re-apply from stored after a tick and again later
    setTimeout(() => this.reapplyVisibilityFromStored(), 0);
    setTimeout(() => this.reapplyVisibilityFromStored(), 400);
    await this.applyQuestionVisibilityDataSources({
      ...(formDataToApply ?? {}),
      ...(this.surveyModel?.data ?? {}),
    });
    this.runSurveyVisibilityExpressions();

    // Final check to ensure all questions have data
    for (const question of questions) {
      const dataSource =
        question['dataSource'] ||
        question.getPropertyValue('dataSource') ||
        question['jsonObj']?.dataSource;

      if (
        dataSource &&
        (!question['choices'] || question['choices'].length === 0)
      ) {
        await this.loadQuestionData(question);
      }
    }

    // Dropdowns with mergeWithStaticChoices often have only static choices until the API runs; the check above
    // skips them. SurveyJS may also not fire onAfterRenderQuestion before this completes. Load from original JSON.
    await this.loadDataSourcesFromOriginalSurveyJson();
    await this.refreshCustomerStamdataDisplay(this.surveyModel);
    this.applyCoursePricingLogic(this.surveyModel, {
      name: 'course_price_template_ids',
    });
  }

  /**
   * Walk original survey JSON (before Survey mutates it) to find a question definition by name — including
   * paneldynamic templateElements — so dataSource is always discoverable for loadQuestionData.
   */
  private findElementDefinitionInOriginalJson(
    questionName: string
  ): Record<string, unknown> | undefined {
    const walk = (elements: unknown[]): Record<string, unknown> | undefined => {
      if (!Array.isArray(elements)) return undefined;
      for (const raw of elements) {
        const el = raw as Record<string, unknown>;
        if (el['name'] === questionName) return el;
        if (
          el['type'] === 'paneldynamic' &&
          Array.isArray(el['templateElements'])
        ) {
          const found = walk(el['templateElements'] as unknown[]);
          if (found) return found;
        }
        if (Array.isArray(el['elements'])) {
          const found = walk(el['elements'] as unknown[]);
          if (found) return found;
        }
      }
      return undefined;
    };
    const pages = this.originalSurveyJSON?.pages || [];
    for (const page of pages) {
      const els = (page as Record<string, unknown>)?.['elements'] as
        | unknown[]
        | undefined;
      const found = walk(els || []);
      if (found) return found;
    }
    return undefined;
  }

  /** Panels and other named elements are not always returned from getAllQuestions(). */
  private findSurveyModelElementByName(name: string): unknown | null {
    if (!this.surveyModel || !name) {
      return null;
    }
    try {
      const byName = this.surveyModel.getQuestionByName(name);
      if (byName) {
        return byName;
      }
    } catch {
      /* ignore */
    }
    const walk = (list: unknown[]): unknown | null => {
      if (!Array.isArray(list)) {
        return null;
      }
      for (const raw of list) {
        const item = raw as {
          name?: string;
          elements?: unknown[];
          questions?: unknown[];
        };
        if (item?.name === name) {
          return item;
        }
        const nested = item?.elements ?? item?.questions;
        if (nested) {
          const found = walk(nested as unknown[]);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    for (const page of this.surveyModel.pages) {
      const found = walk((page as { elements?: unknown[] }).elements ?? []);
      if (found) {
        return found;
      }
    }
    return null;
  }

  private applyVisibilityToSurveyElement(
    element: unknown,
    visibleIfValue: string,
    visible: boolean
  ): void {
    const el = element as {
      setPropertyValue?: (n: string, v: unknown) => void;
      visibleIf?: string;
      visible?: boolean;
    };
    if (typeof el.setPropertyValue === 'function') {
      el.setPropertyValue('visibleIf', visibleIfValue);
      if (!visible) {
        el.setPropertyValue('visible', false);
      }
      return;
    }
    el.visibleIf = visibleIfValue;
    if (!visible) {
      el.visible = false;
    }
  }

  private collectVisibilityDataSourceElementsFromOriginalJson(): Array<{
    name: string;
    visibilityDataSource: string;
  }> {
    const found: Array<{ name: string; visibilityDataSource: string }> = [];
    const walk = (elements: unknown[]): void => {
      if (!Array.isArray(elements)) {
        return;
      }
      for (const raw of elements) {
        const el = raw as Record<string, unknown>;
        if (
          typeof el['name'] === 'string' &&
          typeof el['visibilityDataSource'] === 'string'
        ) {
          found.push({
            name: el['name'],
            visibilityDataSource: el['visibilityDataSource'],
          });
        }
        if (
          el['type'] === 'paneldynamic' &&
          Array.isArray(el['templateElements'])
        ) {
          walk(el['templateElements'] as unknown[]);
        }
        if (Array.isArray(el['elements'])) {
          walk(el['elements'] as unknown[]);
        }
      }
    };
    const pages = this.originalSurveyJSON?.pages || [];
    for (const page of pages) {
      const els = (page as Record<string, unknown>)?.['elements'] as
        | unknown[]
        | undefined;
      walk(els || []);
    }
    return found;
  }

  private mergeEventFormOrganizationIntoParams(params: LoadDataParams): void {
    const auth = this.injector.get(AuthService);
    const surveyData = (this.surveyModel?.data ?? {}) as Record<
      string,
      unknown
    >;
    const orgId = resolveEventFormOrganizationId(
      {
        ...params,
        queryParams: {
          ...(params.queryParams ?? {}),
          ...surveyData,
        },
      },
      auth
    );
    if (orgId == null) {
      return;
    }
    params.organizationId = orgId;
    params.queryParams = {
      ...(params.queryParams ?? {}),
      organizationId: orgId,
      organization_id: orgId,
      organizations_id: orgId,
    };
  }

  private async applyQuestionVisibilityDataSources(
    formData?: Record<string, unknown> | null
  ): Promise<void> {
    if (!this.surveyModel) {
      return;
    }
    const auth = this.injector.get(AuthService);
    const visibilityParams: LoadDataParams = {
      dataSourceKey: '',
      queryParams: {
        ...(formData ?? {}),
        ...(this.surveyModel.data ?? {}),
      },
    };
    this.mergeEventFormOrganizationIntoParams(visibilityParams);

    const elements = this.collectVisibilityDataSourceElementsFromOriginalJson();
    const questionsByName = new Map(
      this.surveyModel
        .getAllQuestions()
        .map((question) => [question.name, question])
    );

    for (const element of elements) {
      const surveyElement =
        this.findSurveyModelElementByName(element.name) ??
        questionsByName.get(element.name);
      if (!surveyElement) {
        continue;
      }
      try {
        const obs = this.componentDataSourceService.executeDataSource(
          element.visibilityDataSource,
          visibilityParams
        );
        if (!obs) {
          continue;
        }
        const result = await firstValueFrom(
          obs.pipe(catchError(() => of({ visible: true })))
        );
        const visible = this.normalizeVisibilityResult(result);
        const visibleIfValue = this.questionVisibleIfAfterDataSource(
          element.name,
          visible
        );
        this.applyVisibilityToSurveyElement(
          surveyElement,
          visibleIfValue,
          visible
        );
        this.visibilityFromDataSource.set(`question:${element.name}`, visible);
      } catch (error) {
        console.warn(
          `[visibilityDataSource] Question "${element.name}" source "${element.visibilityDataSource}" failed:`,
          error
        );
      }
    }
  }

  private reapplyQuestionVisibilityFromStored(): void {
    if (!this.surveyModel) {
      return;
    }
    const fromJson = this.collectVisibilityDataSourceElementsFromOriginalJson();
    const names = new Set(fromJson.map((e) => e.name));
    for (const question of this.surveyModel.getAllQuestions()) {
      names.add(question.name);
    }
    for (const name of names) {
      const stored = this.visibilityFromDataSource.get(`question:${name}`);
      if (stored === undefined) {
        continue;
      }
      const surveyElement = this.findSurveyModelElementByName(name);
      if (!surveyElement) {
        continue;
      }
      const visibleIfValue = this.questionVisibleIfAfterDataSource(
        name,
        stored
      );
      this.applyVisibilityToSurveyElement(
        surveyElement,
        visibleIfValue,
        stored
      );
    }
    this.runSurveyVisibilityExpressions();
  }

  private async refreshBookingFieldVisibility(): Promise<void> {
    if (!this.surveyModel) {
      return;
    }
    await this.applyQuestionVisibilityDataSources(
      this.surveyModel.data as Record<string, unknown>
    );
    this.reapplyQuestionVisibilityFromStored();

    const orgResourcesQuestion = this.surveyModel.getQuestionByName(
      'organization_resource_ids'
    );
    if (orgResourcesQuestion && orgResourcesQuestion.visible) {
      await this.loadQuestionData(orgResourcesQuestion as SurveyQuestion);
    }

    await this.loadAllEventExtraRoomQuestions();
    this.cdr.detectChanges();
  }

  /** Re-fetch page/question visibility tied to event_type (modules page, catering fields). */
  private async refreshEventTypeDependentVisibility(): Promise<void> {
    if (!this.surveyModel) {
      return;
    }
    const formData = this.surveyModel.data as Record<string, unknown>;
    const visibilityParams: LoadDataParams = {
      dataSourceKey: '',
      queryParams: { ...formData },
    };
    this.mergeEventFormOrganizationIntoParams(visibilityParams);
    const jsonPages = (this.originalSurveyJSON?.pages || []) as Array<
      Record<string, unknown>
    >;
    for (const jsonPage of jsonPages) {
      const visibilityDs = jsonPage['visibilityDataSource'];
      const pageName = jsonPage['name'];
      if (typeof visibilityDs !== 'string' || typeof pageName !== 'string') {
        continue;
      }
      const page = this.surveyModel.pages.find(
        (p: { name?: string }) => p.name === pageName
      );
      if (!page) {
        continue;
      }
      try {
        const obs = this.componentDataSourceService.executeDataSource(
          visibilityDs,
          visibilityParams
        );
        if (!obs) {
          continue;
        }
        const result = await firstValueFrom(
          obs.pipe(catchError(() => of({ visible: true })))
        );
        const visible = this.normalizeVisibilityResult(result);
        const visibleIfValue = this.pageVisibleIfAfterDataSource(
          page.name,
          visible
        );
        const pageAny = page as {
          setPropertyValue?: (n: string, v: unknown) => void;
          visibleIf?: string;
          visible?: boolean;
          _visibilityDsResult?: boolean;
        };
        if (typeof pageAny.setPropertyValue === 'function') {
          pageAny.setPropertyValue('visibleIf', visibleIfValue);
          pageAny.setPropertyValue('visible', visible);
          pageAny.setPropertyValue('_visibilityDsResult', visible);
        } else {
          pageAny.visibleIf = visibleIfValue;
          page.visible = visible;
          pageAny._visibilityDsResult = visible;
        }
        this.visibilityFromDataSource.set(`page:${page.name}`, visible);
      } catch (error) {
        console.warn(
          `[visibilityDataSource] Page "${pageName}" refresh on event_type failed:`,
          error
        );
      }
    }
    await this.applyQuestionVisibilityDataSources(formData);
    this.updatePageVisibility(this.surveyModel);
    this.runSurveyVisibilityExpressions();
    this.ensureCurrentPageIsVisible(this.surveyModel);
    setTimeout(() => this.reapplyVisibilityFromStored(), 0);
    this.cdr.detectChanges();
  }

  /** Move off a page that became hidden (e.g. modules page after event_type change). */
  private ensureCurrentPageIsVisible(survey: Model | null): void {
    if (!survey?.pages?.length) {
      return;
    }
    const visiblePages = survey.visiblePages;
    if (
      visiblePages.length === 0 ||
      (survey.currentPage && visiblePages.includes(survey.currentPage))
    ) {
      return;
    }
    try {
      const firstVisiblePage = visiblePages[0];
      const surveyAny = survey as unknown as Record<string, unknown>;
      if (typeof surveyAny['setPropertyValue'] === 'function') {
        (
          surveyAny['setPropertyValue'] as (name: string, val: unknown) => void
        )('currentPage', firstVisiblePage);
      } else {
        survey.currentPage = firstVisiblePage;
      }
      const idx = survey.pages.indexOf(firstVisiblePage);
      if (idx >= 0) {
        survey.currentPageNo = idx;
      }
    } catch {
      // ignore
    }
  }

  /**
   * Ensures every dropdown/tagbox with a dataSource in the template JSON runs loadQuestionData at least once,
   * so sources like `locations` always execute even when onAfterRenderQuestion has not fired yet.
   */
  private async loadDataSourcesFromOriginalSurveyJson(): Promise<void> {
    if (!this.surveyModel) return;
    const all = this.surveyModel.getAllQuestions();
    const loadDropdown = async (
      question: SurveyQuestion,
      locationHint?: unknown
    ) => {
      const qType = question.getType();
      if (qType !== 'dropdown' && qType !== 'tagbox') return;

      const el = this.findElementDefinitionInOriginalJson(question.name);
      const qAny = question as unknown as Record<string, unknown>;
      const jsonObj = qAny['jsonObj'] as Record<string, unknown> | undefined;
      const dataSource =
        (el?.['dataSource'] as string | undefined) ||
        (typeof question.getPropertyValue === 'function'
          ? (question.getPropertyValue('dataSource') as string | undefined)
          : undefined) ||
        (qAny['dataSource'] as string | undefined) ||
        (jsonObj?.['dataSource'] as string | undefined);

      if (!dataSource || typeof dataSource !== 'string') return;

      try {
        await this.loadQuestionData(question, locationHint);
      } catch (e) {
        console.error(
          '[DynamicForm] [dataSource] loadQuestionData failed for',
          question.name,
          e
        );
      }
    };

    await Promise.all(all.map((question) => loadDropdown(question)));

    // Paneldynamic instances are not always included in the initial pass — load per panel
    const panelLoadTasks: Promise<void>[] = [];
    for (const question of all) {
      if (question.getType() !== 'paneldynamic') continue;
      const panels =
        (question as any).panels || (question as any).visiblePanels || [];
      for (const panel of panels) {
        const panelQuestions: SurveyQuestion[] = panel.questions || [];
        const isDependentInPanel = (q: SurveyQuestion): boolean => {
          const parentName = this.findParentQuestionName(q);
          if (
            parentName &&
            panelQuestions.some((pq) => pq.name === parentName)
          ) {
            return true;
          }
          const updateTarget = this.getOnChangeUpdateTargetName(q);
          if (!updateTarget || !this.surveyModel) {
            return false;
          }
          if (panelQuestions.some((pq) => pq.name === updateTarget)) {
            return true;
          }
          return !!this.surveyModel.getQuestionByName(updateTarget);
        };

        const resolveDependentLocationHint = (
          panelQuestion: SurveyQuestion
        ): unknown => {
          const parentName = this.findParentQuestionName(panelQuestion);
          if (parentName) {
            const parentQuestion = panelQuestions.find(
              (pq) => pq.name === parentName
            );
            if (parentQuestion?.value) {
              return parentQuestion.value;
            }
          }
          return this.getLocationValueForRoomQuestion(panelQuestion);
        };

        const independent = panelQuestions.filter(
          (pq) => !isDependentInPanel(pq)
        );
        const dependents = panelQuestions
          .filter((pq) => isDependentInPanel(pq))
          .map((pq) => ({
            question: pq,
            parentValue: resolveDependentLocationHint(pq),
          }))
          .filter((entry) => entry.parentValue);

        panelLoadTasks.push(
          Promise.all(independent.map((pq) => loadDropdown(pq))).then(
            async () => {
              await Promise.all(
                dependents.map(({ question: pq, parentValue }) =>
                  loadDropdown(pq, parentValue)
                )
              );
            }
          )
        );
      }
    }
    await Promise.all(panelLoadTasks);
  }

  /** Optional user-facing text from a visibilityDataSource result (e.g. registration closed). */
  private static visibilityResultMessage(result: unknown): string | null {
    if (result != null && typeof result === 'object' && 'message' in result) {
      const m = (result as { message?: unknown }).message;
      return typeof m === 'string' && m.trim() !== '' ? m.trim() : null;
    }
    return null;
  }

  /**
   * Normalize visibility data source result to a boolean.
   * Accepts a boolean or an object with a optional `visible` property.
   */
  private normalizeVisibilityResult(result: unknown): boolean {
    if (typeof result === 'boolean') return result;
    if (result != null && typeof result === 'object' && 'visible' in result)
      return (result as { visible?: boolean }).visible !== false;
    return true;
  }

  // Helper method to find the parent question that updates this question
  private findParentQuestionName(question: SurveyQuestion): string | null {
    const allQuestions = this.surveyModel.getAllQuestions();

    for (const potentialParent of allQuestions) {
      const updateTarget =
        potentialParent['onChangeUpdateQuestion'] ||
        potentialParent.getPropertyValue('onChangeUpdateQuestion') ||
        potentialParent['jsonObj']?.onChangeUpdateQuestion;

      if (updateTarget === question.name) {
        return potentialParent.name;
      }
    }

    return null;
  }

  // Find all questions that depend on this question
  private findDependentQuestions(question: SurveyQuestion): SurveyQuestion[] {
    const dependents: SurveyQuestion[] = [];
    const allQuestions = this.surveyModel.getAllQuestions();

    for (const q of allQuestions) {
      const updateSource = this.findParentQuestionName(q);
      if (updateSource === question.name) {
        dependents.push(q);
      }
    }

    return dependents;
  }

  /**
   * Sets up datetime constraint functionality for a question
   *
   * This feature allows a datetime-local input to be constrained so that:
   * 1. The selected date/time cannot be earlier than another datetime question
   * 2. A copy button can be shown to copy the value from the source question
   *
   * To use this feature in SurveyJS builder:
   * 1. Create a datetime-local text input question (source question)
   * 2. Create another datetime-local text input question (constrained question)
   * 3. In the constrained question's RNG Settings:
   *    - Enable "Enable Max Date/Time Constraint" (despite the name, this enforces minimum constraint)
   *    - Select the source question from "Max Date/Time Source Question"
   *    - Optionally enable/disable "Show Copy Button"
   *
   * Example JSON configuration:
   * {
   *   "name": "end_date",
   *   "type": "text",
   *   "inputType": "datetime-local",
   *   "title": "End Date",
   *   "datetimeMaxConstraintEnabled": true,
   *   "datetimeMaxConstraintQuestion": "start_date",
   *   "datetimeShowCopyButton": true
   * }
   */
  private setupDatetimeConstraint(
    question: SurveyQuestion,
    sourceQuestionName: string,
    showCopyButton: boolean
  ): void {
    try {
      const sourceQuestion =
        this.surveyModel.getQuestionByName(sourceQuestionName);
      if (!sourceQuestion) {
        console.warn(
          `Source question '${sourceQuestionName}' not found for datetime constraint on '${question.name}'`
        );
        return;
      }

      // Add validation first (doesn't require DOM)
      this.addDatetimeValidation(question, sourceQuestion);

      // Wait for DOM to be ready for UI elements
      this.waitForQuestionDOM(question, () => {
        const questionContainer = document.querySelector(`#${question.id}`);
        if (!questionContainer) {
          console.warn(
            `Question container not found for '${question.name}' after waiting`
          );
          return;
        }

        // Add data attribute for styling
        questionContainer.setAttribute('data-has-datetime-constraint', 'true');

        // Add copy button if enabled
        if (showCopyButton) {
          this.addDatetimeCopyButton(
            question,
            sourceQuestion,
            questionContainer as HTMLElement
          );
        }

      });
    } catch (error) {
      console.error(
        `Error setting up datetime constraint for '${question.name}':`,
        error
      );
    }
  }

  /**
   * Waits for a question's DOM to be fully rendered before executing callback
   */
  private waitForQuestionDOM(
    question: SurveyQuestion,
    callback: () => void,
    maxAttempts: number = 10
  ): void {
    let attempts = 0;

    const checkDOM = () => {
      attempts++;
      const container = document.querySelector(`#${question.id}`);
      const input = container?.querySelector('input[type="datetime-local"]');

      if (container && input) {
        // DOM is ready
        callback();
      } else if (attempts < maxAttempts) {
        // Try again after a short delay
        setTimeout(checkDOM, 200);
      } else {
        console.warn(
          `Failed to find DOM elements for question '${question.name}' after ${maxAttempts} attempts`
        );
      }
    };

    checkDOM();
  }

  /**
   * Adds a copy button above the datetime input
   */
  private addDatetimeCopyButton(
    question: SurveyQuestion,
    sourceQuestion: SurveyQuestion,
    container: HTMLElement
  ): void {
    try {
      // Check if copy button already exists
      const existingButton = container.querySelector('.datetime-copy-button');
      if (existingButton) {
        return;
      }

      // Debug: Log basic info

      // Create copy button container
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'datetime-copy-button-container';
      buttonContainer.style.cssText = `
        margin-bottom: 8px;
        text-align: right;
      `;

      // Create copy button
      const copyButton = document.createElement('button');
      copyButton.type = 'button';
      copyButton.className = 'datetime-copy-button';
      copyButton.textContent = `Kopier fra ${
        sourceQuestion.title || sourceQuestion.name
      }`;
      copyButton.style.cssText = `
        background-color: #007bff;
        color: white;
        border: none;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: background-color 0.2s ease;
      `;

      // Add hover effect
      copyButton.addEventListener('mouseenter', () => {
        copyButton.style.backgroundColor = '#0056b3';
      });
      copyButton.addEventListener('mouseleave', () => {
        copyButton.style.backgroundColor = '#007bff';
      });

      // Add click handler
      copyButton.addEventListener('click', () => {
        const sourceValue = sourceQuestion.value;
        if (sourceValue) {
          // Copy the value and trigger change events
          question.value = sourceValue;

          // Trigger validation to clear any existing errors
          setTimeout(() => {
            question.validate(true);
          }, 50);

          // Show brief feedback
          const originalText = copyButton.textContent;
          copyButton.textContent = 'Kopieret!';
          copyButton.style.backgroundColor = '#28a745';
          setTimeout(() => {
            copyButton.textContent = originalText;
            copyButton.style.backgroundColor = '#007bff';
          }, 1000);
        } else {
          console.warn(`No value to copy from '${sourceQuestion.name}'`);

          // Show brief feedback
          const originalText = copyButton.textContent;
          copyButton.textContent = 'Ingen værdi at kopiere';
          copyButton.style.backgroundColor = '#dc3545';
          setTimeout(() => {
            copyButton.textContent = originalText;
            copyButton.style.backgroundColor = '#007bff';
          }, 1000);
        }
      });

      buttonContainer.appendChild(copyButton);

      // Simple approach: just prepend to the main container
      // This ensures the button appears at the top of the question
      try {
        container.insertBefore(buttonContainer, container.firstChild);
      } catch (error) {
        console.warn(
          'Failed to insert at beginning, appending instead:',
          error
        );
        container.appendChild(buttonContainer);
      }
    } catch (error) {
      console.error(`Error adding copy button for '${question.name}':`, error);
    }
  }

  /**
   * Adds validation to prevent selecting dates earlier than the source question
   */
  private addDatetimeValidation(
    question: SurveyQuestion,
    sourceQuestion: SurveyQuestion
  ): void {
    try {
      // Create a custom validator function
      const validateDateTime = (value: any): string | null => {
        if (!value || !sourceQuestion.value) {
          return null; // No validation if either value is empty
        }

        const questionDate = new Date(value);
        const sourceDate = new Date(sourceQuestion.value);

        if (questionDate < sourceDate) {
          return `Dato/tid kan ikke være tidligere end ${
            sourceQuestion.title || sourceQuestion.name
          } (${this.formatDateTimeForDisplay(sourceQuestion.value)})`;
        }

        return null; // Valid
      };

      // Add custom validator using SurveyJS validator interface
      // Remove any existing datetime validators to avoid duplicates
      question.validators = question.validators.filter(
        (v: any) => !v.isDatetimeConstraintValidator
      );

      const customValidator = {
        validate: (value: any, name?: string) => {
          const errorText = validateDateTime(value);
          if (errorText) {
            return { isValid: false, errorText: errorText };
          }
          return null;
        },
        isDatetimeConstraintValidator: true,
      };

      question.validators.push(customValidator as any);

      // Add real-time validation on value change
      question.registerFunctionOnPropertyValueChanged('value', () => {
        // Trigger validation by calling the question's validation method
        setTimeout(() => {
          question.validate(true);
        }, 50);
      });

      // Also validate when source question changes
      sourceQuestion.registerFunctionOnPropertyValueChanged('value', () => {
        // Trigger validation on the constrained question
        setTimeout(() => {
          question.validate(true);
        }, 50);
      });

      // Add direct input validation and min attribute to prevent invalid values
      const updateMinAttribute = () => {
        const inputElement = document.querySelector(
          `#${question.id} input[type="datetime-local"]`
        ) as HTMLInputElement;
        if (inputElement && sourceQuestion.value) {
          // Set the min attribute to the source question's value
          inputElement.setAttribute('min', sourceQuestion.value);
        }
      };

      setTimeout(() => {
        const inputElement = document.querySelector(
          `#${question.id} input[type="datetime-local"]`
        ) as HTMLInputElement;
        if (inputElement) {
          // Set initial min attribute
          updateMinAttribute();

          inputElement.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            const value = target.value;

            if (value && sourceQuestion.value) {
              const questionDate = new Date(value);
              const sourceDate = new Date(sourceQuestion.value);

              if (questionDate < sourceDate) {
                // Reset to source date if invalid
                target.value = sourceQuestion.value;
                question.value = sourceQuestion.value;

                // Show error message
                setTimeout(() => {
                  question.validate(true);
                }, 10);

              }
            }
          });

        }
      }, 100);

      // Update min attribute when source question changes
      sourceQuestion.registerFunctionOnPropertyValueChanged('value', () => {
        setTimeout(updateMinAttribute, 50);
      });

    } catch (error) {
      console.error(
        `Error adding datetime validation for '${question.name}':`,
        error
      );
    }
  }

  /**
   * Formats a datetime value for display in error messages
   */
  private formatDateTimeForDisplay(value: string): string {
    try {
      const date = new Date(value);
      return date.toLocaleString('da-DK', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return value; // Return original value if formatting fails
    }
  }

  // Add a simple mutation observer for clean paneldynamic styling
  private addCleanPaneldynamicStylingObserver(): void {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;

              // Look for paneldynamic panels
              if (
                element.classList.contains('sd-panel') &&
                element.closest('.sd-paneldynamic')
              ) {
                this.applyCleanPaneldynamicStyling(element);
              }

              // Also look for dropdown questions being added to paneldynamic
              if (element.closest('.sv-paneldynamic, .sd-paneldynamic')) {
                const dropdowns = element.querySelectorAll(
                  '.sv-question--dropdown, .sd-question--dropdown'
                );
                if (
                  dropdowns.length > 0 ||
                  element.classList.contains('sv-question--dropdown') ||
                  element.classList.contains('sd-question--dropdown')
                ) {
                  this.applyPaneldynamicDropdownWidth(element);
                }
              }
            }
          });
        }
      });
    });

    this.paneldynamicObserver?.disconnect();
    this.paneldynamicObserver = observer;
    const host = this.el?.nativeElement as HTMLElement | undefined;
    if (host) {
      observer.observe(host, { childList: true, subtree: true });
    }
  }

  // Apply width to paneldynamic dropdowns
  private applyPaneldynamicDropdownWidth(element: HTMLElement): void {
    try {
      // Find all dropdown questions in the element or the element itself
      let dropdowns: HTMLElement[] = [];

      if (
        element.classList.contains('sv-question--dropdown') ||
        element.classList.contains('sd-question--dropdown')
      ) {
        dropdowns = [element];
      } else {
        dropdowns = Array.from(
          element.querySelectorAll(
            '.sv-question--dropdown, .sd-question--dropdown'
          )
        ) as HTMLElement[];
      }

      dropdowns.forEach((dropdown) => {
        dropdown.style.width = '100%';
        dropdown.style.minWidth = '400px';
        dropdown.style.maxWidth = '100%';

        const innerDropdown = dropdown.querySelector(
          '.sv-dropdown, .sd-dropdown'
        ) as HTMLElement;
        if (innerDropdown) {
          innerDropdown.style.width = '100%';
          innerDropdown.style.minWidth = '400px';
        }

      });
    } catch (error) {
      console.error('[PaneldynamicDropdownWidth] Error:', error);
    }
  }

  // Apply width to ALL paneldynamic dropdowns in the document
  private applyWidthToAllPaneldynamicDropdowns(): void {
    try {
      const extraRoomNames = [
        'primary_extra_room',
        'sub_extra_room',
        'exception_extra_room',
      ];
      const extraRoomPaneldynamicNames = [
        'additional_rooms',
        'sub_event_additional_rooms',
        'exception_additional_rooms',
      ];

      for (const name of extraRoomNames) {
        document.querySelectorAll(`[data-name="${name}"]`).forEach((node) => {
          this.applyDropdownWidthToElement(node as HTMLElement);
        });
      }

      for (const name of extraRoomPaneldynamicNames) {
        const container = document.querySelector(`[data-name="${name}"]`);
        if (!container) {
          continue;
        }
        (container as HTMLElement).style.width = '100%';
        container
          .querySelectorAll(
            '.sv-question--dropdown, .sd-question--dropdown, .sv-row, .sd-row, .sv-paneldynamic__panel, .sd-paneldynamic__panel, .sv-paneldynamic__panel-wrapper, .sd-paneldynamic__panel-wrapper'
          )
          .forEach((node) => {
            const el = node as HTMLElement;
            el.style.width = '100%';
            if (
              el.classList.contains('sv-question--dropdown') ||
              el.classList.contains('sd-question--dropdown')
            ) {
              this.applyDropdownWidthToElement(el);
            }
          });
      }

      // Find all paneldynamic containers
      const paneldynamics = document.querySelectorAll(
        '.sv-paneldynamic, .sd-paneldynamic, [data-name="sub_events"]'
      );

      paneldynamics.forEach((paneldynamic) => {
        const dropdowns = paneldynamic.querySelectorAll(
          '.sv-question--dropdown, .sd-question--dropdown, [data-name="sub_event_location"], [data-name="sub_event_room"], [data-name="exception_location"], [data-name="exception_room"], [data-name="primary_extra_room"], [data-name="sub_extra_room"], [data-name="exception_extra_room"]'
        );

        dropdowns.forEach((dropdown) => {
          this.applyDropdownWidthToElement(dropdown as HTMLElement);
        });
      });

      const locationDropdowns = document.querySelectorAll(
        '[data-name="sub_event_location"], [data-name="sub_event_room"], [data-name="exception_location"], [data-name="exception_room"], [data-name="primary_extra_room"], [data-name="sub_extra_room"], [data-name="exception_extra_room"]'
      );
      locationDropdowns.forEach((dropdown) => {
        this.applyDropdownWidthToElement(dropdown as HTMLElement);
      });

      this.applyExtraRoomPanelLayoutFix();
    } catch (error) {
      console.error('[applyWidthToAllPaneldynamicDropdowns] Error:', error);
    }
  }

  // Apply clean styling to paneldynamic elements
  private applyCleanPaneldynamicStyling(panelElement: HTMLElement): void {
    try {
      if (
        panelElement.closest(
          '[data-name="additional_dates"], [data-name="series_exceptions"], [data-name="additional_rooms"], [data-name="sub_event_additional_rooms"], [data-name="exception_additional_rooms"]'
        )
      ) {
        return;
      }

      // Find all possible content areas
      const contentArea = panelElement.querySelector(
        '.sd-element__content, .sd-panel__content, .sd-row'
      ) as HTMLElement;
      if (!contentArea) {
        return;
      }

      // Find all question elements - try multiple selectors
      let questionElements: Element[] = Array.from(
        contentArea.querySelectorAll(
          '.sd-element, .sd-question, .sd-row__element'
        )
      );

      // If no elements found, try finding by input types
      if (questionElements.length === 0) {
        const datetimeInputs = contentArea.querySelectorAll(
          'input[type="datetime-local"]'
        );
        const timeInputs = contentArea.querySelectorAll('input[type="time"]');

        if (datetimeInputs.length > 0 || timeInputs.length > 0) {
          // Find the parent containers of these inputs
          const allInputs = Array.from(datetimeInputs).concat(
            Array.from(timeInputs)
          );
          questionElements = allInputs
            .map((input) =>
              input.closest('.sd-element, .sd-question, .sd-row__element')
            )
            .filter(Boolean) as Element[];
        }
      }

      if (questionElements.length >= 2) {

        // Reset any existing margins/padding that might interfere
        contentArea.style.margin = '0';
        contentArea.style.padding = '0';

        // Apply flex layout to content area
        contentArea.style.display = 'flex';
        contentArea.style.flexDirection = 'row';
        contentArea.style.gap = '8px';
        contentArea.style.flexWrap = 'nowrap';
        contentArea.style.alignItems = 'flex-start';
        contentArea.style.width = '100%';
        contentArea.style.boxSizing = 'border-box';

        // Style each question element to be exactly 50% width
        questionElements.forEach((element, index) => {
          const el = element as HTMLElement;
          if (!el) return;

          // Reset margins and padding
          el.style.margin = '0';
          el.style.padding = '0';
          el.style.boxSizing = 'border-box';

          // Set exact 50% width accounting for gap
          el.style.flex = '0 0 calc(50% - 4px)';
          el.style.width = 'calc(50% - 4px)';
          el.style.maxWidth = 'calc(50% - 4px)';
          el.style.minWidth = 'calc(50% - 4px)';

          // Ensure proper display
          el.style.display = 'block';
          el.style.float = 'left';

        });

      } else {
      }
    } catch (error) {
      console.error('[CleanPaneldynamic] Error:', error);
    }
  }

  /**
   * Adds a copy button to each paneldynamic panel to allow duplicating entries
   * The button is placed next to the remove panel button
   */
  private addCopyButtonToPaneldynamicPanel(
    panel: any,
    paneldynamicQuestion: any,
    panelElement: HTMLElement,
    copyPanelText: string
  ): void {
    try {
      // Check if copy button already exists for this panel
      const existingCopyBtn = panelElement.querySelector(
        '.paneldynamic-copy-btn'
      );
      if (existingCopyBtn) {
        return; // Button already exists
      }

      // Find the remove button by looking for button/link containing "Slet" text or with remove-related classes
      let removeButton: HTMLElement | null = null;

      // Try multiple selectors to find the remove button
      const selectors = [
        '.sd-paneldynamic__remove-btn',
        '.sv-paneldynamic__remove-btn',
        '[data-key="remove-panel"]',
        '.sd-action[title*="Slet"]',
        '.sd-paneldynamic__btn--remove',
        '.sv-paneldynamic__btn--remove',
      ];

      for (const selector of selectors) {
        removeButton = panelElement.querySelector(selector);
        if (removeButton) break;
      }

      // If not found by selector, try finding by text content
      if (!removeButton) {
        const allButtons = panelElement.querySelectorAll(
          'button, .sd-action, .sv-action, a.sd-paneldynamic__btn'
        );
        for (const btn of Array.from(allButtons)) {
          if (btn.textContent?.includes('Slet')) {
            removeButton = btn as HTMLElement;
            break;
          }
        }
      }

      if (removeButton && removeButton.parentElement) {
        this.insertCopyButton(
          panel,
          paneldynamicQuestion,
          removeButton.parentElement,
          removeButton,
          copyPanelText
        );
      } else {
        // Fallback: Create our own actions container at the bottom of the panel
        const panelContent = panelElement.querySelector(
          '.sd-panel__content, .sv-panel__content, .sd-element__content, .sd-paneldynamic__panel-wrapper'
        );
        if (panelContent) {
          const actionsDiv = document.createElement('div');
          actionsDiv.className = 'paneldynamic-custom-actions';
          actionsDiv.style.cssText =
            'display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; padding-top: 8px;';
          panelContent.appendChild(actionsDiv);
          this.insertCopyButton(
            panel,
            paneldynamicQuestion,
            actionsDiv,
            null,
            copyPanelText
          );
        }
      }
    } catch (error) {
      console.error('[addCopyButtonToPaneldynamicPanel] Error:', error);
    }
  }

  /**
   * Creates and inserts the copy button next to the remove/delete button
   */
  private insertCopyButton(
    panel: any,
    paneldynamicQuestion: any,
    container: HTMLElement,
    removeButton: HTMLElement | null,
    copyPanelText: string
  ): void {
    // Create copy button - styled to match SurveyJS action buttons
    const copyButton = document.createElement('button');
    copyButton.type = 'button';
    copyButton.className =
      'paneldynamic-copy-btn sd-action sd-paneldynamic__btn';
    copyButton.title = copyPanelText;
    copyButton.innerHTML = `<span class="sd-action__title">${copyPanelText}</span>`;

    // Click handler - copy panel values
    copyButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        // Get current panel values
        const panelData = panel.getValue();

        // Add a new panel
        const newPanelIndex = paneldynamicQuestion.addPanel();

        // Copy values to the new panel (after a short delay to ensure panel is rendered)
        setTimeout(() => {
          const panels = paneldynamicQuestion.panels;
          const newPanel = panels[panels.length - 1];

          if (newPanel && panelData) {
            let locationQuestion: any = null;
            let locationValue: any = null;
            let roomQuestionName: string | null = null;
            let roomValue: any = null;

            // First pass: identify location and room questions, and collect room value
            Object.keys(panelData).forEach((key) => {
              if (key === 'sub_event_location' || key === 'location') {
                const question = newPanel.getQuestionByName(key);
                if (question) {
                  locationQuestion = question;
                  locationValue = panelData[key];

                  // Find the room question name that depends on this location
                  roomQuestionName =
                    locationQuestion['onChangeUpdateQuestion'] ||
                    locationQuestion.getPropertyValue?.(
                      'onChangeUpdateQuestion'
                    ) ||
                    locationQuestion['jsonObj']?.onChangeUpdateQuestion;

                  // If not found on the question itself, check the template
                  if (!roomQuestionName && paneldynamicQuestion.template) {
                    const template = paneldynamicQuestion.template;
                    if (template.questions) {
                      const locationTemplate = template.questions.find(
                        (q: any) => q.name === locationQuestion.name
                      );
                      if (locationTemplate) {
                        roomQuestionName =
                          locationTemplate.onChangeUpdateQuestion ||
                          locationTemplate.getPropertyValue?.(
                            'onChangeUpdateQuestion'
                          ) ||
                          locationTemplate.jsonObj?.onChangeUpdateQuestion;
                      }

                      // Also try finding any question with onChangeUpdateQuestion pointing to location
                      if (!roomQuestionName) {
                        const roomQuestionTemplate = template.questions.find(
                          (q: any) => {
                            const onChangeUpdateQuestion =
                              q.onChangeUpdateQuestion ||
                              q.getPropertyValue?.('onChangeUpdateQuestion') ||
                              q.jsonObj?.onChangeUpdateQuestion;
                            return (
                              onChangeUpdateQuestion &&
                              (onChangeUpdateQuestion ===
                                locationQuestion.name ||
                                onChangeUpdateQuestion.includes('location'))
                            );
                          }
                        );
                        if (roomQuestionTemplate) {
                          roomQuestionName = roomQuestionTemplate.name;
                        }
                      }
                    }
                  }

                  // If we found the room question name, get its value from panelData
                  if (
                    roomQuestionName &&
                    panelData[roomQuestionName] !== undefined &&
                    panelData[roomQuestionName] !== null
                  ) {
                    roomValue = panelData[roomQuestionName];
                  }
                }
              }
            });

            // Copy each value except the guid (which should be unique) and room (if location is being copied)
            Object.keys(panelData).forEach((key) => {
              if (
                key !== 'sub_event_guid' &&
                panelData[key] !== undefined &&
                panelData[key] !== null
              ) {
                // Skip room value if we're copying location - we'll set it after rooms are loaded
                if (
                  locationQuestion &&
                  roomQuestionName &&
                  key === roomQuestionName
                ) {
                  return;
                }

                const question = newPanel.getQuestionByName(key);
                if (question) {
                  question.value = panelData[key];
                }
              }
            });

            // If a location was copied, manually trigger room loading and then set room value
            if (locationQuestion && locationValue) {

              if (roomQuestionName) {
                const roomQuestion =
                  newPanel.getQuestionByName(roomQuestionName);

                if (roomQuestion) {
                  // Manually trigger loadQuestionData to load rooms
                  setTimeout(() => {
                    this.loadQuestionData(roomQuestion, locationValue)
                      .then(() => {

                        // After rooms are loaded, set the room value if we have one
                        if (roomValue !== null && roomValue !== undefined) {
                          // Wait a bit more to ensure choices are fully populated
                          setTimeout(() => {
                            roomQuestion.value = roomValue;
                          }, 100);
                        }
                      })
                      .catch((error) => {
                        console.error(
                          `[CopyPanel] Error loading rooms:`,
                          error
                        );
                      });
                  }, 150);
                } else {
                  console.warn(
                    `[CopyPanel] Room question ${roomQuestionName} not found in new panel`
                  );
                }
              } else {
                console.warn(
                  `[CopyPanel] Could not determine room question name for location ${locationQuestion.name}`
                );
              }
            }
          }

          // Apply width fixes after copying
          setTimeout(() => this.applyWidthToAllPaneldynamicDropdowns(), 100);
        }, 100);
      } catch (error) {
        console.error('[CopyPanel] Error copying panel:', error);
      }
    });

    // Insert button next to the remove button in an inline wrapper
    if (removeButton) {
      // Check if there's already a wrapper
      let wrapper = removeButton.parentElement?.querySelector(
        '.paneldynamic-btn-wrapper'
      ) as HTMLElement;

      if (!wrapper) {
        // Create an inline wrapper for both buttons
        wrapper = document.createElement('span');
        wrapper.className = 'paneldynamic-btn-wrapper';
        wrapper.style.cssText =
          'display: inline-flex; align-items: center; gap: 8px;';

        // Insert wrapper before remove button
        removeButton.parentElement?.insertBefore(wrapper, removeButton);

        // Move remove button into wrapper
        wrapper.appendChild(removeButton);
      }

      // Add copy button to wrapper (before the remove button)
      wrapper.insertBefore(copyButton, removeButton);
    } else {
      container.appendChild(copyButton);
    }
  }

  // Add a mutation observer to catch datetime inputs being added to the DOM
  private addDatetimeMutationObserver(): void {
    // Create a MutationObserver to watch for new datetime inputs
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;

              // Check if this is a datetime input or contains one
              const datetimeInputs = element.querySelectorAll(
                'input[type="datetime-local"]'
              );
              if (datetimeInputs.length > 0) {

                datetimeInputs.forEach((input) => {
                  const inputElement = input as HTMLInputElement;

                  // Add a direct event listener to fix on input
                  inputElement.addEventListener('input', (e) => {
                    const target = e.target as HTMLInputElement;
                    const originalValue = target.value;

                    if (
                      originalValue &&
                      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(originalValue)
                    ) {

                      // Skip if already adjusted
                      const uniqueKey = `adjust:${originalValue}`;
                      if (this.adjustedDatetimes.has(uniqueKey)) {
                        return;
                      }

                      // For direct user input, don't adjust the time as browser handles timezone
                      // Only adjust if this is API data (marked by isFromApiLoad)
                      if (
                        this.isFromApiLoad &&
                        !this.isDatetimeAlreadyAdjusted(originalValue)
                      ) {
                        try {
                          const fixedValue =
                            this.adjustDatetimeHours(originalValue);

                          // Prevent infinite loops
                          if (fixedValue !== originalValue) {

                            // Mark as adjusted
                            this.adjustedDatetimes.add(`adjust:${fixedValue}`);

                            // Use a timeout to avoid interfering with the current input event
                            setTimeout(() => {
                              target.value = fixedValue;

                              // Dispatch a change event to update the model
                              const event = new Event('change', {
                                bubbles: true,
                              });
                              target.dispatchEvent(event);
                            }, 0);
                          }
                        } catch (error) {
                          console.error(
                            '[DateTime MutationObserver] Error fixing input:',
                            error
                          );
                        }
                      }
                    }
                  });
                });
              }
            }
          });
        }
      });
    });

    this.datetimeObserver?.disconnect();
    this.datetimeObserver = observer;
    const host = this.el?.nativeElement as HTMLElement | undefined;
    if (host) {
      observer.observe(host, { childList: true, subtree: true });
    }
  }

  // Add this method after ngOnInit
  private addSurveyJSWidthFix() {
    let styleElement = document.getElementById(
      'surveyjs-width-fix'
    ) as HTMLStyleElement | null;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'surveyjs-width-fix';
      document.head.appendChild(styleElement);
    }
    styleElement.textContent = `
      /* Override SurveyJS width constraints globally */
      .sd-body.sd-body--static {
        max-width: none !important;
        width: 100% !important;
        padding-top: 0 !important;
        margin-top: 0 !important;
      }

      .sv-components-container-contentTop,
      [class*="components-container-contentTop"] {
        padding-top: 0 !important;
        margin-top: 0 !important;
      }

      .sv-components-container-contentTop .sd-body__navigation,
      .sv-components-container-contentTop .sv-body__navigation,
      [class*="components-container-contentTop"] .sd-body__navigation,
      [class*="components-container-contentTop"] .sv-body__navigation {
        padding-top: 0 !important;
        margin-top: 0 !important;
      }

      .sd-header:empty,
      .sv-header:empty,
      .sd-header--empty,
      .sv-header--empty {
        display: none !important;
        margin: 0 !important;
        padding: 0 !important;
        min-height: 0 !important;
      }
      
      .sd-question__content,
      .sd-question,
      .sd-row,
      .sd-page {
        width: 100% !important;
        max-width: none !important;
      }
      
      /* Ensure survey root containers are full width */
      .sv-root-modern,
      .sv-container-modern,
      .sv-body {
        width: 100% !important;
        max-width: none !important;
      }
      
      /* Specific fix for questions with custom components */
      .dynamictable .sd-element {
        width: 100% !important;
        max-width: none !important;
      }
      
      /* Don't hide all sv-string-viewer elements - too broad */
      /* Instead, let the JavaScript handle hiding specific messages */
      
      .sv-footer.sv-action-bar,
      .sd-footer.sd-action-bar {
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        justify-content: flex-start !important;
        align-items: center !important;
        gap: 0.75rem !important;
      }

      .form-submit-container {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        margin: 1rem 0 0 !important;
        padding: 0 !important;
        text-align: left !important;
      }

      .form-submit-container::before {
        display: none !important;
      }

      .sv_complete_btn,
      .form-submit-btn {
        background: #19b394 !important;
        color: #fff !important;
        border: 1.5px solid #19b394 !important;
        padding: 0 1.85rem !important;
        border-radius: 12px !important;
        cursor: pointer !important;
        font-size: 1rem !important;
        font-weight: 600 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 52px !important;
        min-width: 8.75rem !important;
        text-transform: none !important;
        letter-spacing: 0.01em !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08) !important;
      }

      .form-submit-btn:hover {
        background: #128a73 !important;
        border-color: #128a73 !important;
      }
      
      /* Modal-specific improvements */
      .modal-content .sv-root-modern,
      .modal-content .sv-container-modern {
        overflow: visible !important;
        max-height: none !important;
      }
      
      .modal-content .sv-body {
        overflow: visible !important;
        max-height: none !important;
      }
      
      /* Ensure completion messages are visible and well-styled in modals */
      .modal-content .sv-completedpage,
      .modal-content .sv-body__page--completed {
        background-color: #f8f9fa !important;
        border: 1px solid #dee2e6 !important;
        border-radius: 8px !important;
        padding: 2rem !important;
        margin: 1rem 0 !important;
        text-align: center !important;
        position: relative !important;
        z-index: 1 !important;
      }
      
      /* Add success icon to completion messages */
      .modal-content .sv-completedpage::before,
      .modal-content .sv-body__page--completed::before {
        content: "✓" !important;
        display: block !important;
        font-size: 3rem !important;
        color: #28a745 !important;
        margin-bottom: 1rem !important;
      }
      
      /* Ensure proper scrolling within modals */
      .modal-content {
        scroll-behavior: smooth !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }
      
      /* Simple paneldynamic side-by-side layout (sub_events date fields only) */
      .sd-paneldynamic .sd-panel .sd-element__content {
        display: flex !important;
        gap: 12px !important;
        flex-wrap: wrap !important;
      }
      
      [data-name="sub_events"] .sd-panel > .sd-element__content > .sd-element:not(.sd-question--paneldynamic),
      [data-name="sub_events"] .sd-panel > .sd-element:not(.sd-question--paneldynamic) {
        flex: 0 0 calc(50% - 6px) !important;
        max-width: calc(50% - 6px) !important;
      }

      /* Extra-room paneldynamics: stack content and use full width (not 50% grid) */
      [data-name="additional_rooms"],
      [data-name="sub_event_additional_rooms"],
      [data-name="exception_additional_rooms"] {
        width: 100% !important;
        max-width: 100% !important;
      }

      [data-name="additional_rooms"] .sd-question__content,
      [data-name="sub_event_additional_rooms"] .sd-question__content,
      [data-name="exception_additional_rooms"] .sd-question__content,
      [data-name="additional_rooms"] .sd-paneldynamic,
      [data-name="sub_event_additional_rooms"] .sd-paneldynamic,
      [data-name="exception_additional_rooms"] .sd-paneldynamic,
      [data-name="additional_rooms"] .sd-paneldynamic__panels-container,
      [data-name="sub_event_additional_rooms"] .sd-paneldynamic__panels-container,
      [data-name="exception_additional_rooms"] .sd-paneldynamic__panels-container,
      [data-name="additional_rooms"] .sd-paneldynamic__panel-wrapper,
      [data-name="sub_event_additional_rooms"] .sd-paneldynamic__panel-wrapper,
      [data-name="exception_additional_rooms"] .sd-paneldynamic__panel-wrapper,
      [data-name="additional_rooms"] .sd-paneldynamic__panel,
      [data-name="sub_event_additional_rooms"] .sd-paneldynamic__panel,
      [data-name="exception_additional_rooms"] .sd-paneldynamic__panel {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }

      [data-name="additional_rooms"] .sd-panel .sd-element__content,
      [data-name="sub_event_additional_rooms"] .sd-panel .sd-element__content,
      [data-name="exception_additional_rooms"] .sd-panel .sd-element__content {
        display: block !important;
        width: 100% !important;
      }

      [data-name="additional_rooms"] .sd-panel .sd-element,
      [data-name="sub_event_additional_rooms"] .sd-panel .sd-element,
      [data-name="exception_additional_rooms"] .sd-panel .sd-element,
      [data-name="primary_extra_room"],
      [data-name="sub_extra_room"],
      [data-name="exception_extra_room"] {
        flex: 1 1 100% !important;
        width: 100% !important;
        max-width: 100% !important;
        min-width: 280px !important;
      }

      [data-name="additional_rooms"] .sd-dropdown,
      [data-name="sub_event_additional_rooms"] .sd-dropdown,
      [data-name="exception_additional_rooms"] .sd-dropdown,
      [data-name="primary_extra_room"] .sd-dropdown,
      [data-name="sub_extra_room"] .sd-dropdown,
      [data-name="exception_extra_room"] .sd-dropdown,
      [data-name="primary_extra_room"] .sd-dropdown__value,
      [data-name="sub_extra_room"] .sd-dropdown__value,
      [data-name="exception_extra_room"] .sd-dropdown__value {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 280px !important;
        box-sizing: border-box !important;
      }
    `;
  }

  // Dynamic table components are now created directly by the widget

  /**
   * Displays an error message to the user when form submission fails
   */
  private showFormError(errorMessage: string): void {
    try {
      // Clear any existing error messages
      const existingErrors = document.querySelectorAll('.form-error-message');
      existingErrors.forEach((el) => el.remove());

      // Create error message element
      const errorElement = document.createElement('div');
      errorElement.className = 'form-error-message';
      errorElement.style.cssText = `
        background-color: #fee;
        border: 1px solid #fcc;
        color: #c33;
        padding: 12px 16px;
        margin: 16px 0;
        border-radius: 4px;
        font-weight: 500;
        display: block;
      `;
      errorElement.textContent = errorMessage;

      // Find the survey container and insert error at the top
      const surveyContainer =
        this.el.nativeElement.querySelector('.sv-root') ||
        this.el.nativeElement.querySelector('form') ||
        this.el.nativeElement;

      if (surveyContainer) {
        // Insert at the top of the container
        const firstChild = surveyContainer.firstChild;
        if (firstChild) {
          surveyContainer.insertBefore(errorElement, firstChild);
        } else {
          surveyContainer.appendChild(errorElement);
        }

        // Scroll to the error message
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Remove error after 10 seconds (optional)
        setTimeout(() => {
          if (errorElement.parentNode) {
            errorElement.remove();
          }
        }, 10000);
      } else {
        // Fallback: use alert if we can't find the container
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error displaying form error:', error);
      // Fallback to alert
      alert(errorMessage);
    }
  }

  /**
   * SurveyJS dropdown/tagbox inside modals: classic dropdown at the field (no mobile popup
   * dialog), block scroll jumps, and stack popups above dashboard chrome (z-index 110000).
   */
  private setupModalDropdownBehavior(): void {
    const modalRoot = this.el.nativeElement.closest('app-dynamic-modal');
    if (!modalRoot || !this.surveyModel) {
      return;
    }

    let modalBackdrop: HTMLElement | null = null;
    let savedModalScrollTop = 0;
    let savedWindowScrollY = 0;

    const getModalScrollContainer = (): HTMLElement | null =>
      modalRoot.querySelector('.modal-content') as HTMLElement | null;

    const restoreScrollPositions = (): void => {
      const scrollContainer = getModalScrollContainer();
      if (
        scrollContainer &&
        scrollContainer.scrollTop !== savedModalScrollTop
      ) {
        scrollContainer.scrollTop = savedModalScrollTop;
      }
      if (window.scrollY !== savedWindowScrollY) {
        window.scrollTo(0, savedWindowScrollY);
      }
    };

    this.surveyModel.onOpenDropdownMenu.add((_, options) => {
      options.menuType = 'dropdown';
    });

    this.surveyModel.onScrollToTop.add((_, options) => {
      options.cancel = true;
    });

    this.surveyModel.onPopupVisibleChanged.add((_, options) => {
      const scrollContainer = getModalScrollContainer();

      if (options.visible) {
        savedModalScrollTop = scrollContainer?.scrollTop ?? 0;
        savedWindowScrollY = window.scrollY;

        modalBackdrop = document.querySelector(
          '.cdk-overlay-backdrop.cdk-overlay-backdrop-showing'
        ) as HTMLElement | null;
        if (modalBackdrop) {
          modalBackdrop.style.pointerEvents = 'none';
        }

        const stabilize = (): void => {
          restoreScrollPositions();
          this.liftSurveyPopupLayers();
        };

        stabilize();
        requestAnimationFrame(stabilize);
        setTimeout(stabilize, 0);
        setTimeout(stabilize, 50);
        setTimeout(stabilize, 150);
      } else if (modalBackdrop) {
        modalBackdrop.style.pointerEvents = '';
        modalBackdrop = null;
      }
    });
  }

  /**
   * Keep dropdown/tagbox lists above following form text (paneldynamic location
   * selects otherwise paint underneath later questions when the list is tall).
   */
  private setupDropdownPopupLayering(): void {
    if (!this.surveyModel) {
      return;
    }
    this.surveyModel.onPopupVisibleChanged.add((_, options) => {
      if (!options.visible) {
        this.resetDropdownQuestionStacking();
        return;
      }
      const raise = (): void => this.raiseOpenDropdownAboveSiblings();
      raise();
      requestAnimationFrame(raise);
      setTimeout(raise, 50);
    });
  }

  private resetDropdownQuestionStacking(): void {
    document
      .querySelectorAll(
        '.sd-question--dropdown, .sv-question--dropdown, .sd-question--tagbox, .sv-question--tagbox'
      )
      .forEach((node) => {
        const el = node as HTMLElement;
        if (el.dataset['rngDropdownStack'] === '1') {
          el.style.zIndex = '';
          el.style.position = '';
          delete el.dataset['rngDropdownStack'];
        }
      });
  }

  private raiseOpenDropdownAboveSiblings(): void {
    document
      .querySelectorAll(
        '.sd-question--dropdown, .sv-question--dropdown, .sd-question--tagbox, .sv-question--tagbox'
      )
      .forEach((node) => {
        const el = node as HTMLElement;
        const isOpen = !!el.querySelector(
          '.sv-popup--show, .sd-popup--show, [aria-expanded="true"]'
        );
        if (isOpen) {
          el.style.zIndex = '40';
          el.style.position = 'relative';
          el.dataset['rngDropdownStack'] = '1';
        } else if (el.dataset['rngDropdownStack'] === '1') {
          el.style.zIndex = '';
          el.style.position = '';
          delete el.dataset['rngDropdownStack'];
        }
      });

    document
      .querySelectorAll(
        '.sv-popup--show, .sd-popup--show, .sv-popup--dropdown, .sd-popup--dropdown'
      )
      .forEach((popup) => {
        const node = popup as HTMLElement;
        node.style.setProperty('z-index', '110000', 'important');
        node
          .querySelectorAll(
            '.sv-popup__container, .sd-popup__container, .sv-popup__content, .sd-popup__content, .sv-list, .sd-list, .sv-list__container'
          )
          .forEach((inner) => {
            const box = inner as HTMLElement;
            box.style.setProperty('background', '#fff', 'important');
            box.style.setProperty('z-index', '110000', 'important');
          });
      });
  }

  /** Body-mounted SurveyJS popups must sit above dashboard filter panel (100001). */
  private liftSurveyPopupLayers(): void {
    const layerZ = '110000';
    document.querySelectorAll('body > div').forEach((el) => {
      if (
        !el.querySelector(
          '.sv-popup, .sd-popup, .sv-popup--show, .sd-popup--show'
        )
      ) {
        return;
      }
      const node = el as HTMLElement;
      node.style.setProperty('z-index', layerZ, 'important');
      node.style.setProperty('pointer-events', 'none', 'important');
      node
        .querySelectorAll(
          '.sv-popup, .sd-popup, .sv-popup--show, .sd-popup--show, .sv-popup__container, .sd-popup__container'
        )
        .forEach((inner) => {
          (inner as HTMLElement).style.setProperty(
            'pointer-events',
            'auto',
            'important'
          );
          (inner as HTMLElement).style.setProperty(
            'z-index',
            layerZ,
            'important'
          );
        });
    });
  }

  private scrollElementToTop(element: HTMLElement): void {
    if (typeof element.scrollTo === 'function') {
      element.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      element.scrollTop = 0;
    }
  }

  /** Collect scrollable ancestors (modal body, overlay pane, etc.) for scroll-to-top. */
  private collectModalScrollTargets(): HTMLElement[] {
    const targets: HTMLElement[] = [];
    const seen = new Set<HTMLElement>();

    const add = (el: HTMLElement | null | undefined) => {
      if (!el || seen.has(el)) {
        return;
      }
      seen.add(el);
      targets.push(el);
    };

    const modalHost = this.el.nativeElement.closest(
      'app-dynamic-modal'
    ) as HTMLElement | null;

    if (modalHost) {
      modalHost
        .querySelectorAll('.modal-body, .modal-content')
        .forEach((el) => add(el as HTMLElement));

      let node: HTMLElement | null = modalHost.parentElement;
      while (node && node !== document.body) {
        const { overflowY } = getComputedStyle(node);
        if (
          (overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowY === 'overlay') &&
          node.scrollHeight > node.clientHeight
        ) {
          add(node);
        }
        if (node.classList.contains('cdk-overlay-pane')) {
          break;
        }
        node = node.parentElement;
      }
    }

    let ancestor: HTMLElement | null = this.el.nativeElement;
    while (ancestor && ancestor !== document.body) {
      if (ancestor.scrollHeight > ancestor.clientHeight) {
        add(ancestor);
      }
      ancestor = ancestor.parentElement;
    }

    return targets;
  }

  /**
   * Scrolls to the top of the modal (and page) after form submission
   * This helps users see the completion message instead of being stuck at the bottom
   */
  private scrollToTopOfModal(): void {
    try {
      const scrollTargets = this.collectModalScrollTargets();
      const runScroll = () => {
        for (const target of scrollTargets) {
          this.scrollElementToTop(target);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const completion = this.el.nativeElement.querySelector(
          '.sv-completedpage, .sv-body__page--completed'
        ) as HTMLElement | null;
        completion?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      };

      runScroll();
      // SurveyJS completion page may render after the first scroll pass
      setTimeout(runScroll, 150);
      setTimeout(() => {
        runScroll();
        this.ensureCompletionMessageVisible();
      }, 300);
    } catch (error) {
      console.error('[DynamicForm] Error scrolling to top:', error);
    }
  }

  /**
   * Ensures the form completion message is visible after scrolling
   */
  private ensureCompletionMessageVisible(): void {
    try {
      // Look for SurveyJS completion elements
      const completionElements = document.querySelectorAll(
        '.sv-completedpage, .sv-body__page--completed, .sv-string-viewer'
      );

      completionElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          // Ensure the element is visible
          element.style.display = 'block';
          element.style.visibility = 'visible';
          element.style.opacity = '1';

          // Add some styling to make it stand out
          element.style.backgroundColor = '#f8f9fa';
          element.style.padding = '1rem';
          element.style.borderRadius = '4px';
          element.style.border = '1px solid #dee2e6';
          element.style.margin = '1rem 0';

        }
      });
    } catch (error) {
      console.error(
        '[DynamicForm] Error ensuring completion message visibility:',
        error
      );
    }
  }

  /**
   * Applies default values from JSON to survey questions
   */
  private applyDefaultValues(jsonObj: any): void {
    try {
      if (!jsonObj.pages || !Array.isArray(jsonObj.pages)) {
        return;
      }

      // SurveyJS: initial values from Creator/API are stored under root `data` (name → value).
      // mergeData applies them; per-element defaultValue is optional and was the only path before.
      if (
        jsonObj.data &&
        typeof jsonObj.data === 'object' &&
        !Array.isArray(jsonObj.data)
      ) {
        this.surveyModel.mergeData(jsonObj.data);
      }

      // Walk through all pages and elements to find default values (incl. nested panels)
      jsonObj.pages.forEach((page: any) => {
        if (page.elements && Array.isArray(page.elements)) {
          this.applyDefaultValuesFromElements(page.elements);
        }
      });

      this.applyDateTimeLocalDefaultsToAllQuestions();

      // Additional attempt after a longer delay to catch any missed defaults
      setTimeout(() => {
        this.retryDefaultValues(jsonObj);
      }, 500);
    } catch (error) {
      console.error('[DynamicForm] Error applying default values:', error);
    }
  }

  private applyDefaultValuesFromElements(elements: any[]): void {
    if (!Array.isArray(elements)) {
      return;
    }
    for (const element of elements) {
      this.applyElementDefaultFromJson(element);
      if (
        element?.type === 'paneldynamic' &&
        Array.isArray(element.templateElements)
      ) {
        this.applyDefaultValuesFromElements(element.templateElements);
      }
      if (element?.type === 'panel' && Array.isArray(element.elements)) {
        this.applyDefaultValuesFromElements(element.elements);
      }
    }
  }

  /**
   * Apply defaultValue / defaultDateTime from a single survey JSON element definition.
   */
  private applyElementDefaultFromJson(element: any): void {
    if (!element?.name) {
      return;
    }
    const question = this.surveyModel.getQuestionByName(element.name);
    if (!question) {
      if (
        element.defaultValue !== undefined ||
        element.defaultDateTime !== undefined
      ) {
        console.warn(
          `[DynamicForm] Question not found for default value: ${element.name}`
        );
      }
      return;
    }

    const isDateTimeLocal =
      element.type === 'text' && element.inputType === 'datetime-local';
    const dateTimeDefault =
      element.defaultDateTime ??
      (isDateTimeLocal && element.defaultValue !== undefined
        ? element.defaultValue
        : undefined);

    if (dateTimeDefault !== undefined && !question.value) {
      question.value = this.resolveDateTimeLocalDefault(
        String(dateTimeDefault)
      );
      return;
    }

    if (element.defaultValue === undefined) {
      return;
    }

    if (element.name === 'event_category' && element.defaultValue !== undefined) {
      if (
        question.value === undefined ||
        question.value === null ||
        question.value === ''
      ) {
        question.value = element.defaultValue;
      }
      return;
    }

    if (element.type === 'radiogroup') {
      // Never overwrite values already set (e.g. event_form_data on edit).
      // A delayed DOM sync must also re-read question.value so it cannot race
      // loadFormData and force the JSON default (e.g. series_share_participants "false").
      if (
        question.value === undefined ||
        question.value === null ||
        question.value === ''
      ) {
        question.value = element.defaultValue;
      }
      setTimeout(() => {
        const current = question.value;
        if (current !== undefined && current !== null && current !== '') {
          this.updateRadiogroupDOM(question, String(current));
        }
      }, 50);
      return;
    }

    if (!question.value) {
      question.value = element.defaultValue;
    }
  }

  /**
   * Retry applying default values for any questions that might have been missed
   */
  private retryDefaultValues(jsonObj: any): void {
    try {
      if (
        jsonObj.data &&
        typeof jsonObj.data === 'object' &&
        !Array.isArray(jsonObj.data)
      ) {
        this.surveyModel.mergeData(jsonObj.data);
      }
      jsonObj.pages.forEach((page: any) => {
        if (page.elements && Array.isArray(page.elements)) {
          this.applyDefaultValuesFromElements(page.elements);
        }
      });
      this.applyDateTimeLocalDefaultsToAllQuestions();
    } catch (error) {
      console.error('[DynamicForm] Error retrying default values:', error);
    }
  }

  /**
   * Updates radiogroup DOM elements to reflect the selected value
   */
  private updateRadiogroupDOM(question: any, value: string): void {
    try {
      const questionElement = document.querySelector(`#${question.id}`);
      if (questionElement) {
        // Find the radio input with the matching value
        const radioInput = questionElement.querySelector(
          `input[type="radio"][value="${value}"]`
        ) as HTMLInputElement;
        if (radioInput) {
          radioInput.checked = true;

          // Trigger change event to ensure SurveyJS knows about the change
          const changeEvent = new Event('change', { bubbles: true });
          radioInput.dispatchEvent(changeEvent);
        } else {
          console.warn(
            `[DynamicForm] Radio input not found for value: ${value} in question: ${question.name}`
          );
        }
      }
    } catch (error) {
      console.error('[DynamicForm] Error updating radiogroup DOM:', error);
    }
  }

  /** Wait to mount the survey until loadFormData() when initial values come from API or modalData. */
  private shouldDeferRevealUntilFormDataLoaded(
    surveyJson?: Record<string, unknown>
  ): boolean {
    const json =
      surveyJson ??
      (this.originalSurveyJSON as Record<string, unknown> | undefined);
    if (json?.['dataSource']) {
      return true;
    }
    const rawModal = this.getInput('modalData');
    if (!rawModal || typeof rawModal !== 'object') {
      return false;
    }
    const payload =
      (rawModal as { data?: Record<string, unknown> }).data ?? rawModal;
    return (
      typeof payload === 'object' &&
      payload !== null &&
      Object.keys(payload).length > 0
    );
  }

  private revealSurvey(): void {
    if (this.surveyReady) {
      return;
    }
    this.surveyReady = true;
    this.startKeepNavVisibleInterval();
    this.addDatetimeMutationObserver();
    this.addCleanPaneldynamicStylingObserver();
  }

  /**
   * Starts a periodic run of forceUpdateNavigationButtons so the "Videre" button stays
   * visible after resize or when the window is refreshed at a small size. SurveyJS can
   * hide the footer with a class or style; we re-apply visibility every 400ms.
   */
  private startKeepNavVisibleInterval(): void {
    if (this.keepNavVisibleIntervalId || !this.surveyModel) return;
    this.keepNavVisibleIntervalId = setInterval(() => {
      if (!this.surveyModel) return;
      this.forceUpdateNavigationButtons();
    }, 400);
  }

  /**
   * Called when the form container is resized. Refreshes SurveyJS responsive layout
   * and restores navigation button visibility so the form doesn't stay squished or
   * broken after resizing back to a larger viewport.
   * Only runs the aggressive restore when size actually changed (skips initial layout).
   */
  private onResizeForLayout(entry?: ResizeObserverEntry): void {
    if (!this.surveyModel || !entry) return;
    const w = entry.contentRect.width;
    const h = entry.contentRect.height;
    const isFirstEver =
      this.lastResizeWidth === 0 && this.lastResizeHeight === 0;
    const hadStableSize = this.lastResizeWidth > 0 && this.lastResizeHeight > 0;
    const sizeUnchanged =
      w === this.lastResizeWidth && h === this.lastResizeHeight;
    this.lastResizeWidth = w;
    this.lastResizeHeight = h;
    if (isFirstEver) return; // first callback: never treat as resize; don't block MutationObserver
    if (!hadStableSize) return; // was 0x0 before: initial layout, not a user resize
    if (sizeUnchanged) return; // duplicate callback: avoid running and blocking observer

    this.resizeLayoutInProgress = true;
    if (this.resizeRestoreIntervalId) {
      clearInterval(this.resizeRestoreIntervalId);
      this.resizeRestoreIntervalId = null;
    }
    try {
      this.surveyModel.updateElementCss(true);
    } catch {}
    const runRestore = () => this.forceUpdateNavigationButtons();
    runRestore();
    // Re-apply every 200ms for 5s so whatever hides the button after resize is overwritten
    let count = 0;
    this.resizeRestoreIntervalId = setInterval(() => {
      runRestore();
      if (++count >= 25) {
        if (this.resizeRestoreIntervalId)
          clearInterval(this.resizeRestoreIntervalId);
        this.resizeRestoreIntervalId = null;
        this.resizeLayoutInProgress = false;
      }
    }, 200);
    setTimeout(() => {
      if (this.resizeRestoreIntervalId) {
        clearInterval(this.resizeRestoreIntervalId);
        this.resizeRestoreIntervalId = null;
      }
      this.resizeLayoutInProgress = false;
    }, 5200);
  }

  /**
   * Forces navigation buttons to update in the DOM based on navigationButtonsLocation setting
   * This directly manipulates the DOM to ensure buttons are visible in the correct locations
   */
  private forceUpdateNavigationButtons(): void {
    try {
      if (!this.surveyModel) {
        return;
      }

      this.syncSurveyNavigationModel();
      this.tagSurveyNavigationButtonRoles();

      if (!this.shouldShowSurveyNavigation()) {
        this.ensureNativeCompleteButtonVisible();
        return;
      }

      // Get the navigationButtonsLocation setting (settings → model → JSON)
      let navLocation = this.getEffectiveNavigationButtonsLocation();

      // When no explicit custom location is configured we still need to run the
      // "restore visibility" pass below.  Something (a SurveyJS page-change CSS
      // transition, an animation-fill-mode, or a parent overflow:hidden) can leave
      // the footer hidden.  Restoring it here is the only reliable repair point.
      // We just skip the *hide* logic further down so nothing gets incorrectly hidden.
      const hasCustomNavLocation = !!navLocation;
      if (!navLocation) {
        navLocation = 'bottom'; // treat as default so the "show" branches run
      }

      // DEBUG: Log the navigation location setting

      // Find all navigation button containers - use multiple selectors to catch all variations
      // SurveyJS v2.0.1 uses 'sd-' prefix, older versions use 'sv-' prefix.
      // Scope to our host so we don't affect another survey on the page.
      const root = this.el?.nativeElement ?? document;
      const surveyContainer =
        root.querySelector('.sd-root') ||
        root.querySelector('.sv-root') ||
        root.querySelector('.sd-body') ||
        root.querySelector('.sv-body') ||
        root.querySelector('[data-survey]') ||
        root.querySelector('survey');
      if (!surveyContainer) {
        return;
      }

      // DEBUG: Log what we found

      // Find all possible navigation containers and buttons - support both sd- and sv- prefixes
      const allNavContainers = surveyContainer.querySelectorAll(
        '.sd-footer, .sv-footer, ' +
          '.sd-action-bar, .sv-action-bar, ' +
          '.sd-body__navigation, .sv-body__navigation, ' +
          '.sd-navigation, .sv-navigation, ' +
          '.sd_nav, .sv_nav, ' +
          '.sd-footer__nav, .sv-footer__nav'
      );
      const allNavButtons = surveyContainer.querySelectorAll(
        '.sd-btn, .sv-btn, ' +
          '.sd-action-bar__item, .sv-action-bar__item, ' +
          '.sd-navigation__prev-btn, .sd-navigation__next-btn, ' +
          '.sv-navigation__prev-btn, .sv-navigation__next-btn, ' +
          'button[data-action="prev"], button[data-action="next"], ' +
          'button[data-action], ' +
          'button.sd-btn, button.sv-btn, ' +
          'button[class*="prev"], button[class*="next"], button[class*="complete"], ' +
          'button[class*="nav"], ' +
          '.sd-prev, .sv-prev, ' +
          '.sd-next, .sv-next, ' +
          '.sd_complete_btn, .sv_complete_btn'
      );

      // Identify top and bottom containers
      const topContainers: HTMLElement[] = [];
      const bottomContainers: HTMLElement[] = [];

      allNavContainers.forEach((container: Element) => {
        const el = container as HTMLElement;
        const classes = el.className || '';
        // Classify using CSS class names only — the getBoundingClientRect heuristic
        // that was here previously caused bottom containers to be misclassified as
        // "top" whenever the survey was near the top of the viewport or the window
        // was resized, which set display:none on the navigation and made it vanish.
        // Never treat the main page footer as top: it has sd-body__navigation / sv-body__navigation
        // or our nav-bottom-detected. Otherwise on small viewport it gets classified as top and we
        // only restore the button, not the footer, so the button stays clipped.
        const isMainFooter =
          classes.includes('sd-body__navigation') ||
          classes.includes('sv-body__navigation') ||
          classes.includes('nav-bottom-detected');
        const isTop =
          !isMainFooter &&
          (classes.includes('nav-top') ||
            classes.includes('action-bar--top') ||
            classes.includes('footer__nav-top') ||
            classes.includes('sd-footer__nav-top') ||
            classes.includes('sd-action-bar--top') ||
            classes.includes('--top'));

        if (isTop) {
          topContainers.push(el);
        } else {
          bottomContainers.push(el);
        }
      });

      // Also check buttons directly to find their containers
      allNavButtons.forEach((btn: Element) => {
        const button = btn as HTMLElement;
        const container = button.closest(
          '.sd-action-bar, .sv-action-bar, ' +
            '.sd-footer, .sv-footer, ' +
            '.sd_nav, .sv_nav, ' +
            '.sd-footer__nav, .sv-footer__nav, ' +
            '[class*="nav"], [class*="footer"], [class*="action-bar"]'
        ) as HTMLElement;
        if (container) {
          const classes = container.className || '';
          const isMainFooter =
            classes.includes('sd-body__navigation') ||
            classes.includes('sv-body__navigation') ||
            classes.includes('nav-bottom-detected');
          const isTop =
            !isMainFooter &&
            (classes.includes('nav-top') ||
              classes.includes('action-bar--top') ||
              classes.includes('footer__nav-top') ||
              classes.includes('sd-footer__nav-top') ||
              classes.includes('sd-action-bar--top') ||
              classes.includes('--top'));

          if (isTop && !topContainers.includes(container)) {
            topContainers.push(container);
          } else if (!isTop && !bottomContainers.includes(container)) {
            bottomContainers.push(container);
          }
        }
      });

      // ── Show / restore passes (always run) ──────────────────────────────────
      // These clear any display:none or visibility:hidden that may have been left
      // by a SurveyJS page-change transition, a CSS animation fill, or a previous
      // erroneous call to this function.
      if (navLocation === 'top' || navLocation === 'topBottom') {
        topContainers.forEach((container) => {
          container.style.removeProperty('display');
          container.style.removeProperty('visibility');
        });
        allNavButtons.forEach((btn: Element) => {
          const button = btn as HTMLElement;
          const container = button.closest(
            '.sd-action-bar, .sv-action-bar, ' +
              '.sd-footer, .sv-footer, ' +
              '.sd_nav, .sv_nav, ' +
              '.sd-footer__nav, .sv-footer__nav, ' +
              '[class*="nav"], [class*="footer"], [class*="action-bar"]'
          ) as HTMLElement;
          if (container && topContainers.includes(container)) {
            button.style.removeProperty('display');
            button.style.removeProperty('visibility');
          }
        });
      }

      if (navLocation === 'bottom' || navLocation === 'topBottom') {
        bottomContainers.forEach((container) => {
          container.classList.remove('sv-action--hidden', 'sd-action--hidden'); // SurveyJS hides nav on small viewport with this class
          container.style.removeProperty('display');
          container.style.removeProperty('visibility');
          if (this.resizeLayoutInProgress) {
            container.style.setProperty('display', 'flex', 'important');
            container.style.setProperty('visibility', 'visible', 'important');
          }
        });
        allNavButtons.forEach((btn: Element) => {
          const button = btn as HTMLElement;
          const container = button.closest(
            '.sd-action-bar, .sv-action-bar, ' +
              '.sd-footer, .sv-footer, ' +
              '.sd_nav, .sv_nav, ' +
              '.sd-footer__nav, .sv-footer__nav, ' +
              '[class*="nav"], [class*="footer"], [class*="action-bar"]'
          ) as HTMLElement;
          if (container && bottomContainers.includes(container)) {
            button.style.removeProperty('display');
            button.style.removeProperty('visibility');
            if (this.resizeLayoutInProgress) {
              button.style.setProperty('display', 'inline-block', 'important');
              button.style.setProperty('visibility', 'visible', 'important');
            }
          }
        });
      }

      // ── Hide passes (only when a real custom location was configured) ─────
      // Never hide a bar that holds the only working prev/next buttons — cloned
      // footers are not wired to SurveyJS and must not replace a hidden original.
      if (hasCustomNavLocation) {
        const topHasButtons = topContainers.some((c) =>
          this.navContainerHasButtons(c)
        );
        const bottomHasButtons = bottomContainers.some((c) =>
          this.navContainerHasButtons(c)
        );

        if (navLocation !== 'top' && navLocation !== 'topBottom') {
          if (!(topHasButtons && !bottomHasButtons)) {
            topContainers.forEach((container) => {
              container.style.display = 'none';
            });
          }
        }
        if (navLocation !== 'bottom' && navLocation !== 'topBottom') {
          if (!(bottomHasButtons && !topHasButtons)) {
            bottomContainers.forEach((container) => {
              container.style.display = 'none';
            });
          }
        }
        if (navLocation === 'none') {
          topContainers.forEach((container) => {
            container.style.display = 'none';
          });
          bottomContainers.forEach((container) => {
            container.style.display = 'none';
          });
          allNavButtons.forEach((btn: Element) => {
            (btn as HTMLElement).style.display = 'none';
          });
        }
      }

      // Do not clone top navigation — cloned buttons are not connected to SurveyJS.

      // Force remove padding-top from top navigation elements
      this.removeTopNavigationPadding();
      this.compactTopSurveyChrome();
      this.ensureSurveyNavigationVisible();
      this.ensureNativeCompleteButtonVisible();

      if ((this as any).hideSubmitButtonForThisInstance) {
        this.hideSurveySubmitButtonsOnly();
      }
    } catch (error) {
      // Silently fail - navigation buttons might not be rendered yet
    }
  }

  /**
   * Removes empty survey header chrome and top padding so the top "Videre" bar sits flush.
   */
  private compactTopSurveyChrome(root?: HTMLElement | null): void {
    try {
      const scope = root ?? (this.el?.nativeElement as HTMLElement | undefined);
      if (!scope) {
        return;
      }

      scope.querySelectorAll('.sd-body, .sv-body').forEach((body) => {
        const el = body as HTMLElement;
        el.style.setProperty('padding-top', '0', 'important');
        el.style.setProperty('margin-top', '0', 'important');
      });

      scope
        .querySelectorAll(
          '.sv-components-container-contentTop, [class*="components-container-contentTop"]'
        )
        .forEach((container) => {
          const el = container as HTMLElement;
          el.style.setProperty('padding-top', '0', 'important');
          el.style.setProperty('margin-top', '0', 'important');
          el.style.setProperty('min-height', '0', 'important');
        });

      let hasVisibleSurveyTitle = false;
      scope.querySelectorAll('.sd-header, .sv-header').forEach((header) => {
        const el = header as HTMLElement;
        const title =
          el.querySelector('.sd-title, .sv-title')?.textContent?.trim() ?? '';
        const description =
          el
            .querySelector('.sd-description, .sv-description')
            ?.textContent?.trim() ?? '';
        const logo = el.querySelector('.sd-logo, .sv-logo');
        const isEmpty = !title && !description && !logo;
        if (isEmpty) {
          el.classList.add('sd-header--empty', 'sv-header--empty');
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('margin', '0', 'important');
          el.style.setProperty('padding', '0', 'important');
          el.style.setProperty('min-height', '0', 'important');
        } else if (title) {
          hasVisibleSurveyTitle = true;
        }
      });

      // SurveyJS title wrapper (.sd-title.sd-container-modern__title) may be the visible header.
      scope
        .querySelectorAll(
          '.sd-title.sd-container-modern__title, .sv-title.sd-container-modern__title'
        )
        .forEach((titleWrap) => {
          const el = titleWrap as HTMLElement;
          const titleText =
            el
              .querySelector(
                '.sd-header__text .sd-title, .sd-header__text h3, .sv-header__text .sv-title, .sv-header__text h3'
              )
              ?.textContent?.trim() ??
            el.textContent?.trim() ??
            '';
          if (titleText) {
            hasVisibleSurveyTitle = true;
            el.classList.add('sd-survey-title--visible');
            el.style.setProperty('margin-bottom', '0.75rem', 'important');
          } else {
            el.classList.remove('sd-survey-title--visible');
            el.style.removeProperty('margin-bottom');
          }
        });

      scope.querySelectorAll('.sd-page, .sv-page').forEach((page) => {
        const el = page as HTMLElement;
        if (hasVisibleSurveyTitle) {
          el.style.setProperty('padding-top', '0.75rem', 'important');
        } else {
          el.style.setProperty('padding-top', '0', 'important');
        }
      });

      const host = this.el?.nativeElement as HTMLElement | undefined;
      if (host) {
        host.classList.toggle('has-survey-title', hasVisibleSurveyTitle);
      }
    } catch {
      // Silently fail if survey chrome is not rendered yet
    }
  }

  /**
   * Removes padding-top from top navigation bar elements ONLY (not bottom navigation)
   * Never touches bottom navigation elements
   */
  private removeTopNavigationPadding(): void {
    try {
      // Scope to this component's host so we don't modify nav elements of other forms on the page.
      const root = this.el?.nativeElement ?? document;
      const surveyContainer =
        root.querySelector('.sd-root') ||
        root.querySelector('.sv-root') ||
        root.querySelector('.sd-body') ||
        root.querySelector('.sv-body');

      if (!surveyContainer) {
        return;
      }

      const surveyContainerRect = surveyContainer.getBoundingClientRect();

      // Find navigation elements only within this form (root)
      const allNavElements = root.querySelectorAll(
        '.sd-footer, .sv-footer, .sd-action-bar, .sv-action-bar'
      );

      allNavElements.forEach((element: Element) => {
        const el = element as HTMLElement;
        const classes = el.className || '';
        const elRect = el.getBoundingClientRect();

        // Never treat the main page footer as top (same rule as forceUpdateNavigationButtons).
        const isMainFooter =
          classes.includes('sd-body__navigation') ||
          classes.includes('sv-body__navigation') ||
          classes.includes('nav-bottom-detected');
        const hasTopClass =
          !isMainFooter &&
          (classes.includes('nav-top') ||
            classes.includes('action-bar--top') ||
            classes.includes('footer__nav-top') ||
            classes.includes('sd-footer__nav-top') ||
            classes.includes('sv-footer__nav-top') ||
            classes.includes('sd-action-bar--top') ||
            classes.includes('sv-action-bar--top'));

        const isNearTop = elRect.top < surveyContainerRect.top + 40;
        const isTop = hasTopClass || (isNearTop && !isMainFooter);

        // Remove any previous markers
        el.classList.remove('nav-top-detected', 'nav-bottom-detected');

        if (isTop) {
          // This is TOP navigation - add marker class and remove padding
          el.classList.add('nav-top-detected');
          el.style.setProperty('padding-top', '0', 'important');
          el.style.setProperty('margin-top', '0', 'important');
        } else {
          // This is BOTTOM navigation - add marker class and ensure it has padding
          el.classList.add('nav-bottom-detected');
          // Remove any inline styles that might have been set
          el.style.removeProperty('padding-top');
          el.style.removeProperty('margin-top');
        }
      });
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Sets up a MutationObserver to watch for navigation buttons being added to the DOM
   */
  private setupNavigationButtonsObserver(): void {
    try {
      // Disconnect existing observer if any
      if (this.navigationButtonsObserver) {
        this.navigationButtonsObserver.disconnect();
      }

      // Find survey container - support both sd- and sv- prefixes; scope to our host
      const root = this.el?.nativeElement ?? document;
      const surveyContainer =
        root.querySelector('.sd-root') ||
        root.querySelector('.sv-root') ||
        root.querySelector('.sd-body') ||
        root.querySelector('.sv-body') ||
        root.querySelector('[data-survey]') ||
        root.querySelector('survey');

      if (!surveyContainer) {
        // Retry after a short delay
        setTimeout(() => this.setupNavigationButtonsObserver(), 100);
        return;
      }

      // Create observer to watch for navigation buttons being added
      this.navigationButtonsObserver = new MutationObserver((mutations) => {
        let shouldUpdate = false;

        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              // Check if this is a navigation-related element - support both sd- and sv- prefixes
              if (
                element.classList.contains('sd-footer') ||
                element.classList.contains('sv-footer') ||
                element.classList.contains('sd-action-bar') ||
                element.classList.contains('sv-action-bar') ||
                element.classList.contains('sd_nav') ||
                element.classList.contains('sv_nav') ||
                element.classList.contains('sd-btn') ||
                element.classList.contains('sv-btn') ||
                element.querySelector(
                  '.sd-footer, .sv-footer, .sd-action-bar, .sv-action-bar, .sd_nav, .sv_nav, .sd-btn, .sv-btn'
                )
              ) {
                shouldUpdate = true;
              }
            }
          });
        });

        if (shouldUpdate) {
          // Debounce the update. Skip if we're in the middle of resize handling so we don't re-hide buttons.
          setTimeout(() => {
            if (this.resizeLayoutInProgress) return;
            this.forceUpdateNavigationButtons();
          }, 10);
        }
      });

      // Start observing
      this.navigationButtonsObserver.observe(surveyContainer, {
        childList: true,
        subtree: true,
      });
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Creates bottom navigation by cloning top navigation when navigationButtonsLocation is "topBottom"
   */
  private createBottomNavigationFromTop(
    surveyContainer: HTMLElement,
    topContainer: HTMLElement
  ): void {
    try {
      // Check if bottom navigation already exists - support both sd- and sv- prefixes
      const existingBottom = surveyContainer.querySelector(
        '.sd-footer:not(.sd-footer__nav-top):not(.sd-action-bar--top), ' +
          '.sv-footer:not(.sv-footer__nav-top):not(.sv-action-bar--top), ' +
          '.sd-action-bar:not(.sd-action-bar--top), ' +
          '.sv-action-bar:not(.sv-action-bar--top)'
      );
      if (existingBottom) {
        return; // Bottom navigation already exists
      }

      // Clone the top container
      const bottomContainer = topContainer.cloneNode(true) as HTMLElement;

      // Remove top-specific classes and add bottom classes - support both sd- and sv- prefixes
      bottomContainer.classList.remove(
        'sd-footer__nav-top',
        'sv-footer__nav-top',
        'sd-action-bar--top',
        'sv-action-bar--top',
        'sd_nav_top',
        'sv_nav_top'
      );
      bottomContainer.classList.add(
        'sd-footer__nav-bottom',
        'sv-footer__nav-bottom',
        'sd-action-bar--bottom',
        'sv-action-bar--bottom'
      );

      // Update button IDs and data attributes to avoid conflicts
      const buttons = bottomContainer.querySelectorAll('button, .sv-btn');
      buttons.forEach((btn: Element) => {
        const button = btn as HTMLElement;
        if (button.id) {
          button.id = button.id
            .replace(/-top/, '-bottom')
            .replace(/_top/, '_bottom');
        }
        // Update data attributes
        if (button.hasAttribute('data-action')) {
          const action = button.getAttribute('data-action');
          if (action) {
            button.setAttribute(
              'data-action',
              action.replace('-top', '-bottom')
            );
          }
        }
      });

      // Insert the bottom container after the survey content but before any existing footer
      // Support both sd- and sv- prefixes
      const surveyContent = surveyContainer.querySelector(
        '.sd-body, .sv-body, ' +
          '.sd-root__body, .sv-root__body, ' +
          '.sd_p_root, .sv_p_root'
      );
      if (surveyContent && surveyContent.nextSibling) {
        surveyContainer.insertBefore(
          bottomContainer,
          surveyContent.nextSibling
        );
      } else {
        surveyContainer.appendChild(bottomContainer);
      }

      // Ensure it's visible
      bottomContainer.style.display = '';
      bottomContainer.style.visibility = '';
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Ensures radiogroup questions have their default values applied when rendered
   */
  private ensureRadiogroupDefault(question: any): void {
    try {
      // Check if this question has a default value in the original JSON
      const surveyJSON = this.getInput('surveyJSON');
      const jsonObj = surveyJSON
        ? typeof surveyJSON === 'string'
          ? JSON.parse(surveyJSON)
          : surveyJSON
        : {};

      // Find the element in the JSON that matches this question
      let defaultValue = null;
      if (jsonObj.pages && Array.isArray(jsonObj.pages)) {
        for (const page of jsonObj.pages) {
          if (page.elements && Array.isArray(page.elements)) {
            for (const element of page.elements) {
              if (
                element.name === question.name &&
                element.defaultValue !== undefined
              ) {
                defaultValue = element.defaultValue;
                break;
              }
            }
          }
          if (defaultValue !== null) break;
        }
      }

      if (defaultValue !== null && !question.value) {
        question.value = defaultValue;
        this.updateRadiogroupDOM(question, defaultValue);
      }
    } catch (error) {
      console.error('[DynamicForm] Error ensuring radiogroup default:', error);
    }
  }
}
